import * as THREE from 'three'
import { FenceConfig, Position3D, ElementDimensions } from './types'
import { prepareElement, loadDAESceneByPath, loadDAEModel } from './modelLoader'
import { loadTexturesForFence } from './textureLoader'
import { getFenceConfigWithColor } from '../../config/fenceConfigs'

// 🚀 DEBUG FLAG - ustaw na true tylko do debugowania (wyłączone = szybsze ładowanie)
const DEBUG_LOGS = false

// 🚀 CACHE dla gate-door-config (ładowany tylko raz)
let gateDoorConfigCache: any = null
let gateDoorConfigLoading: Promise<any> | null = null

async function getGateDoorConfig(): Promise<any> {
  if (gateDoorConfigCache) return gateDoorConfigCache
  
  // Jeśli już ładujemy, poczekaj na zakończenie
  if (gateDoorConfigLoading) return gateDoorConfigLoading
  
  gateDoorConfigLoading = (async () => {
    try {
      const resp = await fetch('/fence-configs/gate-door-config.json')
      if (resp.ok) {
        gateDoorConfigCache = await resp.json()
        console.log('🚀 GATE-DOOR CONFIG LOADED & CACHED (elementManager)')
        return gateDoorConfigCache
      }
    } catch {}
    return null
  })()
  
  return gateDoorConfigLoading
}

/**
 * Mapa elementów 3D w scenie
 */
export class ElementManager {
  private elements = new Map<string, THREE.Group>()
  private pendingElements = new Set<string>()
  // 🚀 CACHE dla klonowanych elementów - przyspiesza pierwsze ładowanie
  private elementCache = new Map<string, THREE.Object3D>() // Klucz: "fenceId-elementType-elementName"

  /**
   * Klonuje element z cache - przyspiesza pierwsze ładowanie
   */
  private cloneElementWithCache(
    element: THREE.Object3D,
    fenceConfigId: string,
    elementType: string,
    elementName: string
  ): THREE.Object3D {
    const cacheKey = `${fenceConfigId}-${elementType}-${elementName}`
    
    // Sprawdź cache
    if (this.elementCache.has(cacheKey)) {
      const cached = this.elementCache.get(cacheKey)!
      return cached.clone() // Klonuj z cache (szybsze niż klonowanie oryginału)
    }
    
    // Jeśli nie ma w cache, sklonuj i zapisz
    const cloned = element.clone()
    this.elementCache.set(cacheKey, cloned)
    return cloned.clone() // Zwróć klon (pierwszy klon idzie do cache, drugi do użycia)
  }

  /**
   * Dodaje element ogrodzenia do sceny
   */
  async addFenceElement(
    elementId: string,
    elementType: 'post' | 'section' | 'gate' | 'door',
    position: Position3D,
    scene: THREE.Scene,
    fenceConfig: FenceConfig,
    preparedModel: THREE.Scene,
    selectedColor?: string,
    widthMultiplier: number = 1, // 🎯 Mnożnik szerokości dla przęsła (1x, 2x, 3x, etc.)
    postHeight?: number, // 🎯 Wysokość słupka w blokach (z suwaka w ElementToolbar)
    variant?: string // 🎯 Wariant elementu (np. 'p22', 'p38', 's22', 's38' dla Łupane Muro - Pełne)
  ): Promise<{ dimensions: ElementDimensions } | null> {
    // Zabezpieczenie przed duplikatami
    if (this.elements.has(elementId) || this.pendingElements.has(elementId)) {
      return null
    }

    this.pendingElements.add(elementId)

    try {
      // 🎯 Zapisz oryginalny ID przed podmianą na config wariantu (potrzebne do offsetów)
      const originalFenceConfigId = fenceConfig.id
      
      // 🎯 Dla ogrodzeń z wariantami (Muro Full, Royal Mix, itp.): załaduj config wariantu
      let activeFenceConfig = fenceConfig
      let activePreparedModel = preparedModel
      if (variant && fenceConfig.variantConfigs) {
        const variantConfigId = fenceConfig.variantConfigs[variant]
        if (variantConfigId) {
          DEBUG_LOGS && console.log(`🎯 Ładuję config wariantu ${variant} -> ${variantConfigId}`)
          const variantConfig = await getFenceConfigWithColor(variantConfigId, selectedColor)
          if (variantConfig) {
            activeFenceConfig = variantConfig
            DEBUG_LOGS && console.log(`✅ Załadowano config wariantu: ${variantConfig.name}`)
            
            // 🎯 Dla "Pełne": załaduj model dla wariantu zamiast używać preparedModel
            DEBUG_LOGS && console.log(`🎯 Ładuję model DAE dla wariantu ${variant}`)
            const variantModel = await loadDAEModel(variantConfig, selectedColor)
            if (variantModel) {
              activePreparedModel = variantModel
              DEBUG_LOGS && console.log(`✅ Załadowano model dla wariantu ${variant}`)
            } else {
              console.warn(`⚠️ Nie można załadować modelu dla wariantu ${variant}, używam preparedModel`)
            }
          } else {
            console.warn(`⚠️ Nie można załadować config wariantu ${variantConfigId}, używam głównego config`)
          }
        } else {
          console.warn(`⚠️ Brak mapowania dla wariantu ${variant}, używam głównego config`)
        }
      }
      
      // Użyj activeFenceConfig i activePreparedModel zamiast fenceConfig i preparedModel w dalszej części funkcji
      fenceConfig = activeFenceConfig
      preparedModel = activePreparedModel
      
      // 🔍 DEBUG: Sprawdź, czy model został zmieniony dla wariantu
      if (variant && originalFenceConfigId && (originalFenceConfigId === 'lupane-muro-mix' || originalFenceConfigId === 'royal-mix' || originalFenceConfigId === 'lupane-muro-full')) {
        console.log(`🔍 DEBUG MODEL SWITCH: originalFenceConfigId=${originalFenceConfigId}, variant=${variant}, fenceConfig.id=${fenceConfig.id}`)
        console.log(`🔍 DEBUG MODEL SWITCH: preparedModel (activePreparedModel) ma ${preparedModel.children.length} dzieci`)
        const sketchUpGroup = preparedModel.children.find((child: any) => child.name === 'SketchUp')
        if (sketchUpGroup) {
          console.log(`🔍 DEBUG MODEL SWITCH: SketchUp group ma ${sketchUpGroup.children.length} dzieci`)
          // Sprawdź, czy w modelu są grupy dla tego wariantu
          if (elementType === 'section') {
            const sectionGroups = fenceConfig.elements?.section?.groups || []
            console.log(`🔍 DEBUG MODEL SWITCH: Szukam grup dla section:`, sectionGroups)
            sectionGroups.forEach((groupName: string) => {
              const found = this.findGroupRecursively(sketchUpGroup, groupName)
              console.log(`🔍 DEBUG MODEL SWITCH: Grupa ${groupName} w modelu:`, found ? '✅ ZNALEZIONA' : '❌ NIE ZNALEZIONA')
            })
          }
        }
      }
      // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long, slim3d-kopia, slim-3d, slim
      // oraz dla royal_s50, royal_p50, muro p22 i muro s22: gate i door są identyczne jak section (ta sama geometria i logika)
      // Użyj konfiguracji section zamiast gate/door, ale zachowaj elementType jako gate/door
      const isVisionBetLongGateDoor = fenceConfig.id === 'vision-bet-long' && (elementType === 'gate' || elementType === 'door')
      const isVisionBetMaxGateDoor = fenceConfig.id === 'vision-bet-max' && (elementType === 'gate' || elementType === 'door')
      const isVisionBetMediumGateDoor = fenceConfig.id === 'vision-bet-medium' && (elementType === 'gate' || elementType === 'door')
      const isGladkieMediumGateDoor = fenceConfig.id === 'gladkie_medium' && (elementType === 'gate' || elementType === 'door')
      const isGladkieMaxGateDoor = fenceConfig.id === 'gladkie_max' && (elementType === 'gate' || elementType === 'door')
      const isGladkieLongGateDoor =
        (fenceConfig.id === 'gladkie_long' || fenceConfig.id === 'slim3d-kopia') &&
        (elementType === 'gate' || elementType === 'door')
      const isSlim3dGateDoor = fenceConfig.id === 'slim-3d' && (elementType === 'gate' || elementType === 'door')
      const isSlimGateDoor = fenceConfig.id === 'slim' && (elementType === 'gate' || elementType === 'door')
      const isRoyalS50GateDoor = fenceConfig.id === 'royal_s50' && (elementType === 'gate' || elementType === 'door')
      const isRoyalP50GateDoor = fenceConfig.id === 'royal_p50' && (elementType === 'gate' || elementType === 'door')
      const isMuroP22GateDoor = fenceConfig.id === 'lupane-muro-p22' && (elementType === 'gate' || elementType === 'door')
      const isMuroS22GateDoor = fenceConfig.id === 'lupane-muro-s22' && (elementType === 'gate' || elementType === 'door')
      const isMuroP38GateDoor = fenceConfig.id === 'lupane-muro-p38' && (elementType === 'gate' || elementType === 'door')
      const isMuroS38GateDoor = fenceConfig.id === 'lupane-muro-s38' && (elementType === 'gate' || elementType === 'door')
      const isVisionBetGateDoor =
        isVisionBetLongGateDoor ||
        isVisionBetMaxGateDoor ||
        isVisionBetMediumGateDoor ||
        isGladkieMediumGateDoor ||
        isGladkieMaxGateDoor ||
        isGladkieLongGateDoor ||
        isSlim3dGateDoor ||
        isSlimGateDoor ||
        isRoyalS50GateDoor ||
        isRoyalP50GateDoor ||
        isMuroP22GateDoor ||
        isMuroS22GateDoor ||
        isMuroP38GateDoor ||
        isMuroS38GateDoor
      
      // 🎯 DEBUG: Loguj dla gate/door w Royal Mix
      if ((elementType === 'gate' || elementType === 'door') && (fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50')) {
        console.log(`🔍 DEBUG Royal Mix ${elementType}: fenceConfig.id=${fenceConfig.id}, elementType=${elementType}, isRoyalS50GateDoor=${isRoyalS50GateDoor}, isRoyalP50GateDoor=${isRoyalP50GateDoor}, isVisionBetGateDoor=${isVisionBetGateDoor}`)
      }
      
      let elementConfig = fenceConfig.elements?.[elementType]
      
      if (isVisionBetGateDoor) {
        // 🚪 Użyj konfiguracji section dla gate/door w vision-bet-long, vision-bet-max, vision-bet-medium,
        // gladkie-max, gladkie-long, slim3d-kopia, slim-3d, slim, royal_s50, royal_p50, muro p22, muro s22, muro p38 oraz muro s38
        // 🎯 WAŻNE: Dla gate/door w Royal Mix (S50/P50) ZAWSZE używaj konfiguracji section, niezależnie od widthMultiplier
        const sectionConfig = fenceConfig.elements?.section
        if (!sectionConfig) {
          console.error(`❌ Brak konfiguracji section dla ${elementType} w ${fenceConfig.id}`)
          console.error(`   fenceConfig.elements:`, fenceConfig.elements)
          return null
        }
        elementConfig = sectionConfig
        // 🎯 Dla gate/door w S38: użyj grup z P38 (group_21, group_57, group_0) zamiast z section S38
        if (fenceConfig.id === 'lupane-muro-s38') {
          elementConfig = { ...sectionConfig, groups: ['group_21', 'group_57', 'group_0'] }
        }
        let fenceType = ''
        if (fenceConfig.id === 'vision-bet-long') fenceType = 'Vision-Bet-Long'
        else if (fenceConfig.id === 'vision-bet-max') fenceType = 'Vision-Bet-Max'
        else if (fenceConfig.id === 'vision-bet-medium') fenceType = 'Vision-Bet-Medium'
        else if (fenceConfig.id === 'gladkie_medium') fenceType = 'Gladkie-Medium'
        else if (fenceConfig.id === 'gladkie_max') fenceType = 'Gladkie-Max'
        else if (fenceConfig.id === 'gladkie_long' || fenceConfig.id === 'slim3d-kopia') fenceType = 'Gladkie-Long'
        else if (fenceConfig.id === 'slim-3d') fenceType = 'Slim-3D'
        else if (fenceConfig.id === 'slim') fenceType = 'Slim'
        else if (fenceConfig.id === 'royal_s50') fenceType = 'Royal-S50'
        else if (fenceConfig.id === 'royal_p50') fenceType = 'Royal-P50'
        else if (fenceConfig.id === 'lupane-muro-p22') fenceType = 'Muro-P22'
        else if (fenceConfig.id === 'lupane-muro-s38') fenceType = 'Muro-S38'
        console.log(`🚪 ${fenceType} ${elementType}: używam konfiguracji section zamiast ${elementType} (grupy: ${elementConfig.groups?.join(', ') || 'brak'}, widthMultiplier=${widthMultiplier})`)
      }
      
      if (!elementConfig) {
        console.error(`Brak konfiguracji dla typu: ${elementType}`)
        return null
      }
      
      // 🔍 DEBUG: Sprawdź groups dla sekcji (szczególnie dla Muro Mix i Full)
      if (elementType === 'section' && originalFenceConfigId && (originalFenceConfigId === 'lupane-muro-mix' || originalFenceConfigId === 'royal-mix' || originalFenceConfigId === 'lupane-muro-full')) {
        console.log(`🔍 DEBUG SECTION: originalFenceConfigId: ${originalFenceConfigId}, fenceConfig.id: ${fenceConfig.id}`)
        console.log(`🔍 DEBUG SECTION: elementConfig:`, elementConfig)
        console.log(`🔍 DEBUG SECTION: elementConfig.groups:`, elementConfig?.groups)
        console.log(`🔍 DEBUG SECTION: fenceConfig.elements?.section:`, fenceConfig.elements?.section)
        console.log(`🔍 DEBUG SECTION: fenceConfig.elements?.section?.groups:`, fenceConfig.elements?.section?.groups)
        console.log(`🔍 DEBUG SECTION: fenceConfig.elements:`, fenceConfig.elements)
      }
      
      // 🚪 DEBUG: Sprawdź czy gate/door ma modelPath (tylko jeśli nie jest vision-bet-long/max/medium/gladkie-max/gladkie-long/slim-3d)
      if (!isVisionBetGateDoor && (elementType === 'gate' || elementType === 'door') && elementConfig.modelPath) {
        DEBUG_LOGS && console.log(`🚪 ${elementType} element ma modelPath: ${elementConfig.modelPath}`)
      }

      // Dla gate/door: ZAWSZE ładuj model z configu (elementConfig.modelPath lub globalnego gate-door-config.json)
      // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d: używaj preparedModel (tego samego co section)
      let sourceModel: THREE.Scene = preparedModel
      // 🔍 DEBUG: Sprawdź, jaki model jest używany dla sekcji w Mix i Full
      if (elementType === 'section' && originalFenceConfigId && (originalFenceConfigId === 'lupane-muro-mix' || originalFenceConfigId === 'royal-mix' || originalFenceConfigId === 'lupane-muro-full')) {
        console.log(`🔍 DEBUG MODEL: elementType=section, originalFenceConfigId=${originalFenceConfigId}, fenceConfig.id=${fenceConfig.id}`)
        console.log(`🔍 DEBUG MODEL: preparedModel (czyli activePreparedModel) ma ${preparedModel.children.length} dzieci`)
        console.log(`🔍 DEBUG MODEL: sourceModel === preparedModel:`, sourceModel === preparedModel)
        const sketchUpGroup = preparedModel.children.find((child: any) => child.name === 'SketchUp')
        if (sketchUpGroup) {
          console.log(`🔍 DEBUG MODEL: SketchUp group ma ${sketchUpGroup.children.length} dzieci (grup)`)
          console.log(`🔍 DEBUG MODEL: Dostępne grupy w modelu:`, sketchUpGroup.children.map((c: any) => c.name).slice(0, 10))
          // Sprawdź, czy w modelu są grupy dla tego wariantu
          const sectionGroups = fenceConfig.elements?.section?.groups || []
          console.log(`🔍 DEBUG MODEL: Sprawdzam, czy grupy dla section (${sectionGroups.join(', ')}) są w modelu:`)
          sectionGroups.forEach((groupName: string) => {
            const found = this.findGroupRecursively(sketchUpGroup, groupName)
            console.log(`🔍 DEBUG MODEL:   ${groupName}:`, found ? '✅ ZNALEZIONA' : '❌ NIE ZNALEZIONA')
          })
        } else {
          console.error(`❌ DEBUG MODEL: Nie znaleziono SketchUp group w modelu!`)
        }
      }
      // 🎯 DEBUG: Loguj dla gate/door w Royal Mix
      if ((elementType === 'gate' || elementType === 'door') && (fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50')) {
        console.log(`🔍 DEBUG Royal Mix ${elementType} MODEL: isVisionBetGateDoor=${isVisionBetGateDoor}, elementConfig.modelPath=${elementConfig?.modelPath}, sourceModel będzie: ${isVisionBetGateDoor ? 'preparedModel (section)' : 'external model'}`)
      }
      
      if (!isVisionBetGateDoor && (elementType === 'gate' || elementType === 'door')) {
        let elementModelPath: string | undefined = elementConfig.modelPath
        // Fallback do globalnego gate/door jeśli brak w elemencie (z cache)
        if (!elementModelPath) {
          const gateDoorCfg = await getGateDoorConfig()
          elementModelPath = gateDoorCfg?.modelPath
          if (elementModelPath) {
            DEBUG_LOGS && console.log(`🚪 Fallback modelPath z globalnego gate-door-config.json (cache): ${elementModelPath}`)
          }
        }
        if (elementModelPath) {
          const externalModel = await loadDAESceneByPath(elementModelPath)
          if (externalModel) {
            sourceModel = externalModel
            DEBUG_LOGS && console.log(`🚪 Using external model for ${elementType}:`, elementModelPath)
          } else {
            console.warn(`⚠️ Nie udało się załadować zewnętrznego modelu dla ${elementType}, używam modelu bieżącego ogrodzenia`)
          }
        } else {
          console.warn(`⚠️ Brak modelPath w configu dla ${elementType} i w globalnym gate/door – używam modelu bieżącego ogrodzenia`)
        }
      } else if (isVisionBetGateDoor && (elementType === 'gate' || elementType === 'door')) {
        // 🎯 DEBUG: Loguj dla gate/door w Royal Mix - używamy preparedModel (section)
        console.log(`🚪 Royal Mix ${elementType}: używam preparedModel (section), NIE używam zewnętrznego modelu`)
      }

      // Znajdź grupy do dodania
      // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d gate/door: używaj typu 'section' żeby pobrać odpowiednie grupy
      const elementsToAddType = isVisionBetGateDoor ? 'section' : elementType
      const elementsToAdd = this.getElementsToAdd(
        sourceModel,
        elementsToAddType,
        elementConfig,
        fenceConfig,
        postHeight // 🎯 Przekaż wysokość słupka w blokach (z suwaka w ElementToolbar)
      )

      if (elementsToAdd.length === 0) {
        console.error(`Nie znaleziono elementów dla typu: ${elementType}`)
        return null
      }

      // Utwórz grupę dla elementu
      const elementGroup = new THREE.Group()
      // 🎯 Dla vision-bet / gladkie / slim3d-kopia / slim-3d / slim / royal_s50 / royal_p50 gate/door:
      //     traktuj geometrię JAKO section (pivot, łączenie, rotacja) – gate/door różni się tylko typem w React.
      const originalElementType = elementType
      const elementNameFor3D =
        isVisionBetGateDoor &&
        (fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50' || fenceConfig.id === 'lupane-muro-p22' || fenceConfig.id === 'lupane-muro-s22' || fenceConfig.id === 'lupane-muro-p38')
          ? 'section'
          : elementType

      elementGroup.name = `${elementNameFor3D}_group`
      elementGroup.userData.fenceElementId = elementId
      elementGroup.userData.elementType = elementNameFor3D
      elementGroup.userData.originalElementType = originalElementType
      elementGroup.userData.fenceConfig = fenceConfig // 🎯 Zapisz konfigurację dla rotationRightEdge

      // Dodaj elementy do grupy
      const modelScale = fenceConfig.modelScale || 0.01
      DEBUG_LOGS && console.log(`🔍 DEBUG: ElementManager - wywołuję loadTexturesForFence dla ${fenceConfig.id} kolor: ${selectedColor}`)
      const textures = await loadTexturesForFence(fenceConfig, selectedColor)
      DEBUG_LOGS && console.log(`🔍 DEBUG: ElementManager - loadTexturesForFence zwróciła:`, textures)

      // Oblicz wymiary PRZED przygotowaniem (potrzebne dla pivot point)
      // 🎯 NOWE: Przekaż właściwy model do obliczania rzeczywistych wymiarów (dla gate/door może być zewnętrzny)
      // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d gate/door: używaj typu 'section' żeby obliczyć wymiary jak section
      const dimensionsType = isVisionBetGateDoor ? 'section' : elementType
      let dimensions = this.calculateDimensions(dimensionsType, fenceConfig, sourceModel, postHeight) // 🎯 Przekaż postHeight do calculateDimensions
      
      // 🎯 Dla sections, gate, door: zapisz bazową szerokość PRZED mnożeniem przez widthMultiplier
      let baseWidth = dimensions.width
      if (elementType === 'section' || isVisionBetGateDoor) {
        // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long, slim3d-kopia,
        // slim-3d, slim oraz royal_s50 i royal_p50 gate/door: traktuj jak section
        baseWidth = dimensions.width
        let fenceTypeSuffix = ''
        if (isVisionBetGateDoor) {
          if (fenceConfig.id === 'vision-bet-long') fenceTypeSuffix = 'vision-bet-long'
          else if (fenceConfig.id === 'vision-bet-max') fenceTypeSuffix = 'vision-bet-max'
          else if (fenceConfig.id === 'vision-bet-medium') fenceTypeSuffix = 'vision-bet-medium'
          else if (fenceConfig.id === 'gladkie_medium') fenceTypeSuffix = 'gladkie_medium'
          else if (fenceConfig.id === 'gladkie_max') fenceTypeSuffix = 'gladkie_max'
          else if (fenceConfig.id === 'gladkie_long' || fenceConfig.id === 'slim3d-kopia') fenceTypeSuffix = 'gladkie_long'
          else if (fenceConfig.id === 'slim-3d') fenceTypeSuffix = 'slim-3d'
        }
        DEBUG_LOGS && console.log(`🎯 ${elementType} ${elementId}: baseWidth=${baseWidth.toFixed(3)}m (przed widthMultiplier, ${isVisionBetGateDoor ? `${fenceTypeSuffix} używa section` : ''})`)
      } else if (elementType === 'gate' || elementType === 'door') {
        // 🚪 Dla gate/door (nie vision-bet-long/max/medium/gladkie-max/gladkie-long/slim-3d): użyj defaultWidth lub spacing z configu jako baseWidth (nie pełnej szerokości)
        const gateDoorConfig = fenceConfig.elements?.[elementType]
        if (gateDoorConfig?.defaultWidth) {
          baseWidth = gateDoorConfig.defaultWidth
          DEBUG_LOGS && console.log(`🚪 ${elementType} ${elementId}: baseWidth=${baseWidth.toFixed(3)}m (z defaultWidth w config)`)
        } else if (gateDoorConfig?.spacing) {
          baseWidth = gateDoorConfig.spacing
          DEBUG_LOGS && console.log(`🚪 ${elementType} ${elementId}: baseWidth=${baseWidth.toFixed(3)}m (z spacing w config)`)
        } else {
          // Fallback: użyj spacing z calculateDimensions (spacing * 2 dla door, spacing * 4 dla gate) / odpowiednią liczbę
          const spacing = gateDoorConfig?.spacing || 0.5
          baseWidth = spacing // Dla gate/door baseWidth to spacing, nie pełna szerokość
          DEBUG_LOGS && console.log(`🚪 ${elementType} ${elementId}: baseWidth=${baseWidth.toFixed(3)}m (fallback z spacing)`)
        }
      }
      
      // 🎯 Dla przęsła: przemnóż szerokość przez widthMultiplier
      // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long, slim3d-kopia,
      // slim-3d, slim oraz royal_s50 i royal_p50 gate/door: traktuj jak section
      if ((elementType === 'section' || isVisionBetGateDoor) && widthMultiplier > 1) {
        dimensions = {
          ...dimensions,
          width: dimensions.width * widthMultiplier
        }
        DEBUG_LOGS && console.log(`🎯 ${elementType} z widthMultiplier=${widthMultiplier}: szerokość zwiększona do ${dimensions.width}m`)
      }
      
      // 🚪 Dla bramy/drzwi (nie vision-bet-long): szerokość powinna być równa rzeczywistej szerokości z DAE * widthMultiplier
      // (nie używamy spacing - szerokość jest bezpośrednio z modelu)
      // dimensions.width jest już ustawione z calculateDimensions (z modelu lub fallback)
      // Nie mnożymy przez widthMultiplier tutaj - to będzie zrobione później w pozycjonowaniu elementów
      // dimensions.width pozostaje jako szerokość pojedynczego elementu dla gate/door
      
      elementsToAdd.forEach((item, index) => {
        const offsetY = this.calculateOffsetY(item, index, elementType, fenceConfig)

        // Oblicz szerokość z modelu dla metalu (bez tworzenia klona - użyj oryginalnego elementu)
        const bboxDebug = new THREE.Box3().setFromObject(item.element)
        const sizeDebug = new THREE.Vector3()
        bboxDebug.getSize(sizeDebug)

        // ✅ TYLKO dla elementu metalowego użyj szerokości z modelu DAE (w jednostkach modelu)
        // Dla pozostałych elementów użyj dimensions.width (jak było wcześniej)
        // ✅ Sprawdzamy metalGroup dla post, section, gate i door
        // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d gate/door: używaj metalGroup z section
        const metalGroup = (elementType === 'section' || isVisionBetGateDoor)
          ? fenceConfig.elements?.section?.metalGroup 
          : elementType === 'post'
          ? fenceConfig.elements?.post?.metalGroup
          : elementType === 'gate'
          ? fenceConfig.elements?.gate?.metalGroup
          : elementType === 'door'
          ? fenceConfig.elements?.door?.metalGroup
          : undefined
        const isMetalElement = metalGroup && item.name === metalGroup
        
        
        // 🎯 Bazowa szerokość (bez widthMultiplier) dla pojedynczego bloczka
        // Użyj zapisanej wartości baseWidth dla sections, gate, door, lub oblicz dla innych elementów
        // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d gate/door: traktuj jak section
        const elementBaseWidth = (elementType === 'section' || elementType === 'gate' || elementType === 'door' || isVisionBetGateDoor) ? baseWidth : (dimensions.width / widthMultiplier)
        let elementWidth = elementBaseWidth // Domyślnie: szerokość z dimensions (w metrach, dla bloczków)
        
        // Oblicz szerokość z DAE
        const widthInModelUnits = sizeDebug.x
        const widthFromDAE = widthInModelUnits * modelScale
        
        // ✅ Zapamię tај czy to metal i jaki współczynnik skalowania
        let metalScaleFactorX = 1.0
        
        if (isMetalElement) {
          // 🚪 Dla gate/door: elementy metalowe obok siebie BEZ odstępów (tylko jeśli NIE jest vision-bet)
          if ((elementType === 'gate' || elementType === 'door') && !isVisionBetGateDoor) {
            
            // 🚪 Dla gate/door: użyj rzeczywistej szerokości z DAE (nie spacing)
            // Elementy powinny być bezpośrednio obok siebie
            const gateDoorElementWidth = widthFromDAE // Rzeczywista szerokość z modelu DAE
            
            // 🚪 Oblicz całkowitą szerokość gate/door (szerokość pojedynczego elementu * widthMultiplier)
            const totalGateDoorWidth = gateDoorElementWidth * widthMultiplier
            
            for (let i = 0; i < widthMultiplier; i++) {
              // 🚀 Użyj cache dla szybszego klonowania
              const clonedElement = this.cloneElementWithCache(item.element, fenceConfig.id, elementType, item.name)
              
              prepareElement(clonedElement, {
                modelScale,
                positionY: offsetY,
                elementId,
                textures,
                elementWidth: gateDoorElementWidth, // Użyj rzeczywistej szerokości z DAE
                fenceConfig
              })
              
              // 🚪 Pozycjonuj elementy bezpośrednio obok siebie (bez odstępów)
              // prepareElement ustawia position.x na elementWidth/2 (środek elementu)
              // Musimy przesunąć elementy tak, żeby były obok siebie od środka grupy
              // Pierwszy element: -totalWidth/2 + elementWidth/2
              // Każdy kolejny: przesunięty o elementWidth w prawo
              const elementCenterOffset = -totalGateDoorWidth / 2 + gateDoorElementWidth / 2
              clonedElement.position.x = elementCenterOffset + (i * gateDoorElementWidth)
              
              clonedElement.userData.fenceElementId = elementId
              elementGroup.add(clonedElement)
            }
            
            // 🚪 Zaktualizuj dimensions.width na rzeczywistą szerokość wszystkich elementów
            // To jest używane do obliczania rightEdge (prawa krawędź gate/door)
            dimensions = {
              ...dimensions,
              width: totalGateDoorWidth
            }
          } else {
            // ✅ Dla metalu (tylko nie-gate/door lub widthMultiplier=1): skaluj według wartości z configu (metalWidthScale * widthMultiplier)
          const metalWidthScale = elementConfig.metalWidthScale || 1.1
          const referenceBlockWidth = dimensions.width // To jest szerokość z config (spacing * widthMultiplier)
          const targetWidth = referenceBlockWidth * metalWidthScale // 🎯 Skalowana według configu
          metalScaleFactorX = targetWidth / widthFromDAE
          DEBUG_LOGS && console.log(`🔧 Metal ${item.name}: skaluję z ${widthFromDAE}m do ${targetWidth}m (metalWidthScale: ${metalWidthScale}, widthMultiplier: ${widthMultiplier}, factor: ${metalScaleFactorX})`)
          
          // ✅ Użyj DOCELOWEJ szerokości dla offsetX w prepareElement
          elementWidth = targetWidth
          
          // 🎯 Dla metalu: dodaj JEDEN element (przeskalowany w X)
          // 🚀 Użyj cache dla szybszego klonowania
          const clonedElement = this.cloneElementWithCache(item.element, fenceConfig.id, elementType, item.name)
          prepareElement(clonedElement, {
            modelScale,
            positionY: offsetY,
            elementId,
            textures,
            elementWidth: elementWidth,
            fenceConfig
          })
          
          // ✅ Skaluj metal TYLKO w osi X
          const scaleY = clonedElement.scale.y
          const scaleZ = clonedElement.scale.z
          clonedElement.scale.set(modelScale * metalScaleFactorX, scaleY, scaleZ)
          DEBUG_LOGS && console.log(`🔧 Metal ${item.name}: scale.x ustawione na ${clonedElement.scale.x}`)
          
          // ✅ Skoryguj pozycję X (wyśrodkowanie) - tylko dla widthMultiplier > 1
          if (widthMultiplier > 1) {
            const widthDifference = elementWidth - dimensions.width
            const correctionX = -widthDifference / 2
            clonedElement.position.x += correctionX
            
            // Dodaj metalRightOffset żeby metal był wyrównany z bloczkami
            const metalRightOffset = this.getMetalRightOffsetForWidthMultiplier(elementConfig, widthMultiplier)
            clonedElement.position.x += metalRightOffset
            DEBUG_LOGS && console.log(`🔧 Metal ${item.name}: koryguje pozycję X o ${correctionX}m + metalRightOffset ${metalRightOffset}m (widthMultiplier = ${widthMultiplier})`)
          } else {
            // Dla widthMultiplier = 1: skoryguj pozycję X jak w backupie
            const widthDifference = elementWidth - dimensions.width
            const correctionX = -widthDifference / 2 // Przesunięcie w lewo
            clonedElement.position.x += correctionX
            
            // Dodaj metalRightOffset żeby metal był wyrównany z bloczkami
            const metalRightOffset = this.getMetalRightOffsetForWidthMultiplier(elementConfig, widthMultiplier)
            clonedElement.position.x += metalRightOffset
            DEBUG_LOGS && console.log(`🔧 Metal ${item.name}: koryguje pozycję X o ${correctionX}m + metalRightOffset ${metalRightOffset}m (widthMultiplier = ${widthMultiplier})`)
          }
          
          // 🎯 Dla Royal S50 section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'royal_s50' && isMetalElement) {
            const metalZOffset = 0.055 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Royal S50 ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Royal P50 section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'royal_p50' && isMetalElement) {
            const metalZOffset = 0.018 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Royal P50 ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Gladkie Max section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'gladkie_max' && isMetalElement) {
            const metalZOffset = 0.025 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Gladkie Max ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Gladkie Long section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'gladkie_long' && isMetalElement) {
            const metalZOffset = 0.019 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Gladkie Long ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Slim section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'slim' && isMetalElement) {
            const metalZOffset = 0.03 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Slim ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Slim-3D section: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          // 🎯 FIX: Dla gate/door w Slim 3D NIE przesuwaj w osi Z, żeby brama nie była przesunięta
          if (elementType === 'section' && fenceConfig.id === 'slim-3d' && isMetalElement) {
            const metalZOffset = 0.019 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Slim-3D ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Vision-Bet Long section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'vision-bet-long' && isMetalElement) {
            const metalZOffset = 0.019 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Vision-Bet Long ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Vision-Bet Max section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'vision-bet-max' && isMetalElement) {
            const metalZOffset = 0.025 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Vision-Bet Max ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Vision-Bet Medium/Gladkie Medium section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && (fenceConfig.id === 'vision-bet-medium' || fenceConfig.id === 'gladkie_medium') && isMetalElement) {
            const metalZOffset = 0.019 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Vision-Bet Medium/Gladkie Medium ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Muro P22 section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'lupane-muro-p22' && isMetalElement) {
            const metalZOffset = 0.022 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Muro P22 ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Muro S22 section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'lupane-muro-s22' && isMetalElement) {
            const metalZOffset = 0.022 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Muro S22 ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Muro S38 section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'lupane-muro-s38' && isMetalElement) {
            const metalZOffset = 0.055 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Muro S38 ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          // 🎯 Dla Muro P38 section/gate/door: przesuń metal w osi Z, żeby był na środku bloczka dekoracyjnego
          if ((elementType === 'section' || elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'lupane-muro-p38' && isMetalElement) {
            const metalZOffset = 0.055 // Przesunięcie w osi Z (dodatnie = do przodu, ujemne = do tyłu)
            clonedElement.position.z += metalZOffset
            DEBUG_LOGS && console.log(`🎯 Muro P38 ${elementType}: przesunięto metal w osi Z o ${metalZOffset}m, nowa pozycja Z=${clonedElement.position.z.toFixed(3)}m`)
          }
          
          clonedElement.userData.fenceElementId = elementId
          
          // 🎯 Odwróć post w pionie dla S38
          if (elementType === 'post' && fenceConfig.id === 'lupane-muro-s38') {
            clonedElement.scale.y *= -1
            DEBUG_LOGS && console.log(`🔄 Odwrócono post w pionie dla S38: scale.y = ${clonedElement.scale.y}`)
          }
          
          elementGroup.add(clonedElement)
          }
        } else {
          // 🎯 Dla bloczków (nie-metal): powiel widthMultiplier razy w poziomie
          DEBUG_LOGS && console.log(`🎯 Bloczek ${item.name}: tworzę ${widthMultiplier} kopii w poziomie`)
          
          // 🔍 P38 DEBUG: Sprawdź strukturę PRZED klonowaniem
          if (item.type === 'decoration' && fenceConfig.id === 'lupane-muro-p38') {
            DEBUG_LOGS && console.log(`🔍 P38 DEBUG (PRZED CLONE): ${item.name} - dzieci: ${item.element.children.length}, hasGeometry: ${!!(item.element as any).geometry}`)
          }
          
          for (let i = 0; i < widthMultiplier; i++) {
            // 🚀 Użyj cache dla szybszego klonowania
            const clonedElement = this.cloneElementWithCache(item.element, fenceConfig.id, elementType, item.name)
            
            // 🔍 DEBUG: Dla sekcji Muro Mix i Full - loguj szczegóły klonowania
            if (elementType === 'section' && originalFenceConfigId && (originalFenceConfigId === 'lupane-muro-mix' || originalFenceConfigId === 'royal-mix' || originalFenceConfigId === 'lupane-muro-full')) {
              console.log(`🔍 DEBUG CLONE SECTION: fenceConfig.id=${fenceConfig.id}, groupName=${item.name}, copy=${i+1}/${widthMultiplier}, elementType=${item.type}`)
              console.log(`   clonedElement.children.length=${clonedElement.children.length}`)
              const bbox = new THREE.Box3().setFromObject(clonedElement)
              const size = new THREE.Vector3()
              bbox.getSize(size)
              console.log(`   bbox size: x=${size.x.toFixed(3)}, y=${size.y.toFixed(3)}, z=${size.z.toFixed(3)}`)
            }
            
            // 🔍 P38 DEBUG: Sprawdź strukturę PO klonowaniu
            if (item.type === 'decoration' && fenceConfig.id === 'lupane-muro-p38') {
              DEBUG_LOGS && console.log(`🔍 P38 DEBUG (PO CLONE kopia ${i+1}): ${item.name} - dzieci: ${clonedElement.children.length}, hasGeometry: ${!!(clonedElement as any).geometry}`)
              DEBUG_LOGS && console.log(`🔍 P38 DEBUG (PO CLONE kopia ${i+1}): Szczegóły dzieci:`, clonedElement.children.map((c: any) => ({ 
                name: c.name, 
                type: c.type, 
                hasGeometry: !!(c as any).geometry,
                visible: c.visible 
              })))
            }
            
            // 🎯 Dla S22 post: usuń zagnieżdżone dekoracje z bloków bazowych PRZED dodaniem do sceny
            if (elementType === 'post' && fenceConfig.id === 'lupane-muro-s22' && item.type !== 'decoration') {
              const postConfig = fenceConfig.elements?.post
              const decorationGroupName = postConfig?.decorationGroup || 'group_41'
              
              // Zbuduj listę nazw dekoracji do usunięcia
              const decorationNamesToRemove: string[] = []
              if (decorationGroupName) {
                decorationNamesToRemove.push(decorationGroupName)
              }
              // Dodaj wszystkie grupy z textures, które mogą być dekoracjami
              const postTextures = postConfig?.textures
              if (postTextures) {
                Object.keys(postTextures).forEach(textureGroupName => {
                  // Pomiń grupy bazowe (group_44, group_55)
                  const baseGroups = postConfig?.groups || []
                  if (!baseGroups.includes(textureGroupName)) {
                    decorationNamesToRemove.push(textureGroupName)
                  }
                })
              }
              // Dodaj znane grupy dekoracyjne jako fallback
              decorationNamesToRemove.push('group_41', 'group_63')
              
              // Usuń zagnieżdżone dekoracje
              if (decorationNamesToRemove.length > 0) {
                this.removeNestedDecorations(clonedElement, decorationNamesToRemove)
                DEBUG_LOGS && console.log(`🧹 S22 (addFenceElement): Usunięto zagnieżdżone dekoracje z bloku bazowego ${item.name}`)
              }
            }
            
            // 🎯 Użyj prepareElement dla WSZYSTKICH bloków (włącznie z dekoracyjnymi Slim 3D)
            // Tak samo jak dla vision-bet-long - bloki dekoracyjne przechodzą przez prepareElement
            // 🔍 DEBUG: Dla sekcji Muro Mix i Full - loguj przed prepareElement
            if (elementType === 'section' && originalFenceConfigId && (originalFenceConfigId === 'lupane-muro-mix' || originalFenceConfigId === 'royal-mix' || originalFenceConfigId === 'lupane-muro-full')) {
              console.log(`🔍 DEBUG PREPARE SECTION: przed prepareElement, groupName=${item.name}, position przed: x=${clonedElement.position.x.toFixed(3)}, y=${clonedElement.position.y.toFixed(3)}, z=${clonedElement.position.z.toFixed(3)}`)
            }
            prepareElement(clonedElement, {
              modelScale,
              positionY: offsetY,
              elementId,
              textures,
              elementWidth: elementBaseWidth, // ✅ Szerokość pojedynczego bloczka (dla pivot point)
              fenceConfig
            })
            // 🔍 DEBUG: Dla sekcji Muro Mix i Full - loguj po prepareElement
            if (elementType === 'section' && originalFenceConfigId && (originalFenceConfigId === 'lupane-muro-mix' || originalFenceConfigId === 'royal-mix' || originalFenceConfigId === 'lupane-muro-full')) {
              console.log(`🔍 DEBUG PREPARE SECTION: po prepareElement, groupName=${item.name}, position po: x=${clonedElement.position.x.toFixed(3)}, y=${clonedElement.position.y.toFixed(3)}, z=${clonedElement.position.z.toFixed(3)}`)
            }
            
            // 🔍 P38 DEBUG: Sprawdź stan PO prepareElement
            if (item.type === 'decoration' && fenceConfig.id === 'lupane-muro-p38') {
              DEBUG_LOGS && console.log(`🔍 P38 DEBUG (PO prepareElement): ${item.name}`)
              DEBUG_LOGS && console.log(`  position: x=${clonedElement.position.x.toFixed(3)}, y=${clonedElement.position.y.toFixed(3)}, z=${clonedElement.position.z.toFixed(3)}`)
              DEBUG_LOGS && console.log(`  rotation: x=${clonedElement.rotation.x.toFixed(3)}, y=${clonedElement.rotation.y.toFixed(3)}, z=${clonedElement.rotation.z.toFixed(3)}`)
              DEBUG_LOGS && console.log(`  scale: x=${clonedElement.scale.x.toFixed(3)}, y=${clonedElement.scale.y.toFixed(3)}, z=${clonedElement.scale.z.toFixed(3)}`)
              DEBUG_LOGS && console.log(`  visible: ${clonedElement.visible}`)
            }
            
            // 🎯 Pozycjonuj X dla wszystkich bloków (tak samo jak dla vision-bet-long/max/medium/gladkie-max/gladkie-long/slim-3d)
            // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d gate/door: używaj elementConfig (który jest konfiguracją section)
            // Dla innych przypadków: używaj normalElementConfig
            const configForBlocks = isVisionBetGateDoor ? elementConfig : fenceConfig.elements?.[elementType as 'post' | 'section']
            // 🎯 prepareElement ustawia position.x na elementBaseWidth/2 (offset dla pivot point)
            const prepareElementPositionX = clonedElement.position.x
            
            // 🎯 Dla widthMultiplier > 1: rozmieść bloczki od LEWEJ KRAWĘDZI METALU!
            if (widthMultiplier > 1) {
              // Metal ma lewą krawędź w: (dimensions.width/2) - (metalWidth/2)
              const metalWidthScaleFromConfig = configForBlocks?.metalWidthScale || 1.1
              const metalWidth = dimensions.width * metalWidthScaleFromConfig
              const metalLeftEdge = (dimensions.width / 2) - (metalWidth / 2)
              
              // Bloczki zaczynają się od środka sekcji (x=0) i rozkładają się symetrycznie
              // Dla widthMultiplier=2: bloczek 0 w -elementBaseWidth/2, bloczek 1 w +elementBaseWidth/2
              // Dla widthMultiplier=3: bloczek 0 w -elementBaseWidth, bloczek 1 w 0, bloczek 2 w +elementBaseWidth
              const blockCenterOffset = (i - (widthMultiplier - 1) / 2) * elementBaseWidth
              
              // Przesuń bloczki lekko w prawo żeby nie nachodziły
              const rightOffset = this.getRightOffsetForWidthMultiplier(configForBlocks, widthMultiplier)
              clonedElement.position.x = blockCenterOffset + rightOffset
              
              // 🔍 DEBUG
              DEBUG_LOGS && console.log(`🔍 metalLeftEdge=${metalLeftEdge.toFixed(4)}m, elementBaseWidth=${elementBaseWidth.toFixed(4)}m`)
              DEBUG_LOGS && console.log(`🎯 Bloczek ${item.name} kopia ${i+1}/${widthMultiplier}: środek=${blockCenterOffset.toFixed(3)}m`)
            } else {
              // 🎯 Pojedynczy bloczek: dodaj rightOffset żeby był taki sam jak dla widthMultiplier > 1
              const rightOffset = this.getRightOffsetForWidthMultiplier(configForBlocks, widthMultiplier)
              clonedElement.position.x = prepareElementPositionX + rightOffset
              DEBUG_LOGS && console.log(`🎯 Bloczek ${item.name}: pojedynczy, pozycja X z rightOffset: ${(prepareElementPositionX + rightOffset).toFixed(3)}m`)
            }
            
            clonedElement.userData.fenceElementId = elementId
            
            // 🔍 DEBUG: Dla sekcji Muro Mix i Full - loguj finalną pozycję przed dodaniem do grupy
            if (elementType === 'section' && originalFenceConfigId && (originalFenceConfigId === 'lupane-muro-mix' || originalFenceConfigId === 'royal-mix' || originalFenceConfigId === 'lupane-muro-full')) {
              console.log(`🔍 DEBUG FINAL SECTION: przed add do elementGroup, groupName=${item.name}, copy=${i+1}/${widthMultiplier}`)
              console.log(`   finalna position: x=${clonedElement.position.x.toFixed(3)}, y=${clonedElement.position.y.toFixed(3)}, z=${clonedElement.position.z.toFixed(3)}`)
              console.log(`   finalna scale: x=${clonedElement.scale.x.toFixed(3)}, y=${clonedElement.scale.y.toFixed(3)}, z=${clonedElement.scale.z.toFixed(3)}`)
            }
            
            // 🎯 Odwróć bloczek dekoracyjny w pionie dla postu S38
            if (item.type === 'decoration' && elementType === 'post' && fenceConfig.id === 'lupane-muro-s38') {
              clonedElement.scale.y *= -1
              DEBUG_LOGS && console.log(`🔄 Odwrócono bloczek dekoracyjny ${item.name} w pionie dla S38: scale.y = ${clonedElement.scale.y}`)
            }
            
            // 🔍 P38 DEBUG: Sprawdź finalną pozycję PRZED dodaniem do grupy
            if (item.type === 'decoration' && fenceConfig.id === 'lupane-muro-p38') {
              DEBUG_LOGS && console.log(`🔍 P38 DEBUG (PRZED add do grupy): ${item.name}`)
              DEBUG_LOGS && console.log(`  finalna position.x: ${clonedElement.position.x.toFixed(3)}`)
              DEBUG_LOGS && console.log(`  finalna position.y: ${clonedElement.position.y.toFixed(3)}`)
              DEBUG_LOGS && console.log(`  finalna position.z: ${clonedElement.position.z.toFixed(3)}`)
            }
            
            elementGroup.add(clonedElement)
            
            // 🔍 P38 DEBUG: Sprawdź czy element został dodany
            if (item.type === 'decoration' && fenceConfig.id === 'lupane-muro-p38') {
              DEBUG_LOGS && console.log(`🔍 P38 DEBUG (PO add do grupy): ${item.name} dodany do elementGroup`)
              DEBUG_LOGS && console.log(`  elementGroup.children.length: ${elementGroup.children.length}`)
              DEBUG_LOGS && console.log(`  clonedElement.parent: ${clonedElement.parent?.type}`)
            }
            
            // 🎯 Dla S38 post: dodaj group_25 obok KAŻDEGO group_24 (dla każdego bloku w pionie)
            if (elementType === 'post' && fenceConfig.id === 'lupane-muro-s38' && item.type === 'base') {
              const postConfig = fenceConfig.elements?.post
              const group24Name = postConfig?.groups?.[0]
              const secondaryGroupName = postConfig?.groups?.find((name: string) => 
                name !== group24Name && name !== (postConfig?.decorationGroup || postConfig?.groups?.[1])
              )
              
              // item.name może być "group_24" lub "group_24_copy_0", więc sprawdź czy zaczyna się od group24Name
              if (secondaryGroupName && (item.name === group24Name || item.name.startsWith(`${group24Name}_copy_`))) {
                // Znajdź group_25 w modelu (sourceModel jest dostępny w zakresie addFenceElement)
                const sketchUpGroup = sourceModel.children.find((child: any) => child.name === 'SketchUp')
                const secondarySourceElement = this.findGroupRecursively(sketchUpGroup || sourceModel, secondaryGroupName)
                
                if (secondarySourceElement) {
                  // 🚀 Użyj cache dla szybszego klonowania
                  const clonedSecondaryElement = this.cloneElementWithCache(secondarySourceElement, fenceConfig.id, elementType, secondaryGroupName || 'secondary')
                  
                  prepareElement(clonedSecondaryElement, {
                    modelScale,
                    positionY: offsetY, // Ta sama pozycja Y co group_24
                    elementId,
                    textures,
                    elementWidth: elementBaseWidth,
                    fenceConfig
                  })
                  
                  // 🎯 Ustaw group_25 jako drugi bloczek po prawej stronie group_24 (bez skalowania geometrii)
                  clonedElement.updateMatrixWorld(true)
                  clonedSecondaryElement.updateMatrixWorld(true)
                  const group24Box = new THREE.Box3().setFromObject(clonedElement)
                  const group25Box = new THREE.Box3().setFromObject(clonedSecondaryElement)
                  const width25 = group25Box.max.x - group25Box.min.x
                  const postConfig = fenceConfig.elements?.post
                  const secondaryOffset = (postConfig as any)?.secondaryOffset || 0
                  const group25Center = new THREE.Vector3()
                  group25Box.getCenter(group25Center)
                  const targetCenterX = group24Box.min.x - secondaryOffset - (width25 / 2)
                  const deltaX = targetCenterX - group25Center.x
                  const group24Center = new THREE.Vector3()
                  group24Box.getCenter(group24Center)
                  const secondaryOffsetZ = (postConfig as any)?.secondaryOffsetZ || 0
                  const targetCenterZ = group24Center.z + secondaryOffsetZ
                  const deltaZ = targetCenterZ - group25Center.z
                  clonedSecondaryElement.position.x += deltaX
                  clonedSecondaryElement.position.z += deltaZ
                  clonedSecondaryElement.position.y = offsetY
                  DEBUG_LOGS && console.log(`🎯 S38 align: width25=${width25.toFixed(3)}, secondaryOffset=${secondaryOffset.toFixed(3)}, secondaryOffsetZ=${secondaryOffsetZ.toFixed(3)}, deltaX=${deltaX.toFixed(3)}, deltaZ=${deltaZ.toFixed(3)}, newCenterX=${(group25Center.x + deltaX).toFixed(3)}, newCenterZ=${(group25Center.z + deltaZ).toFixed(3)}`)
                  
                  // clonedSecondaryElement.scale.y *= -1
                  
                  clonedSecondaryElement.userData.fenceElementId = elementId
                  elementGroup.add(clonedSecondaryElement)
                }
              }
            }
          }
        }
      })

      // 🎯 Dla S38 post: zastosuj decorationOffset i decorationOffsetZ do bloczku dekoracyjnego
      if (elementType === 'post' && fenceConfig.id === 'lupane-muro-s38') {
        const postConfig = fenceConfig.elements?.post
        const decorationGroupName = postConfig?.decorationGroup || postConfig?.groups?.[1]
        const baseGroupName = postConfig?.groups?.[0] // group_19
        
        if (decorationGroupName && baseGroupName) {
          const decorationBlock = elementGroup.children.find((child: any) => child.name === decorationGroupName)
          
          if (decorationBlock) {
            // Znajdź najwyższy blok bazowy (group_19)
            const baseBlocks = elementGroup.children.filter((child: any) => 
              child.name === baseGroupName || child.name.startsWith(`${baseGroupName}_copy_`)
            )
            
            if (baseBlocks.length > 0) {
              // Znajdź najwyższy blok bazowy
              baseBlocks.sort((a: any, b: any) => a.position.y - b.position.y)
              const highestBaseBlock = baseBlocks[baseBlocks.length - 1]
              
              const decorationOffset = (postConfig as any)?.decorationOffset || 0
              const decorationOffsetZ = (postConfig as any)?.decorationOffsetZ || 0

              // Zastosuj offsety do bloczku dekoracyjnego
              decorationBlock.position.x = highestBaseBlock.position.x + decorationOffset
              decorationBlock.position.z = highestBaseBlock.position.z + decorationOffsetZ
              DEBUG_LOGS && console.log(`🎯 S38 post: Blok dekoracyjny ${decorationGroupName} ustawiony na X=${decorationBlock.position.x.toFixed(3)}, Z=${decorationBlock.position.z.toFixed(3)} (baseBlock: X=${highestBaseBlock.position.x.toFixed(3)}, Z=${highestBaseBlock.position.z.toFixed(3)}; decorationOffset=${decorationOffset.toFixed(3)}, decorationOffsetZ=${decorationOffsetZ.toFixed(3)})`)
            }
          }
        }
      }
      
      // 🎯 Dla S22 post: przesuń bloczek dekoracyjny do prawej o 1.25cm i do lewej o 5cm
      if (elementType === 'post' && fenceConfig.id === 'lupane-muro-s22') {
        const postConfig = fenceConfig.elements?.post
        const decorationGroupName = postConfig?.decorationGroup || 'group_41'
        
        // 🎯 FINALNE CZYSZCZENIE: Usuń WSZYSTKIE zagnieżdżone dekoracje z bloków bazowych
        const decorationNamesToRemove: string[] = []
        if (decorationGroupName) {
          decorationNamesToRemove.push(decorationGroupName)
        }
        const postTextures = postConfig?.textures
        if (postTextures) {
          Object.keys(postTextures).forEach(textureGroupName => {
            const baseGroups = postConfig?.groups || []
            if (!baseGroups.includes(textureGroupName)) {
              decorationNamesToRemove.push(textureGroupName)
            }
          })
        }
        decorationNamesToRemove.push('group_41', 'group_63')
        
        // Usuń wszystkie zagnieżdżone dekoracje
        const nestedDecorationsToRemove: THREE.Object3D[] = []
        elementGroup.traverse(obj => {
          if (obj === elementGroup) return
          const objName = obj.name || ''
          if (decorationNamesToRemove.some(decName => {
            if (!decName) return false
            return objName === decName || objName.startsWith(`${decName}_copy_`)
          })) {
            // Sprawdź czy dekoracja jest zagnieżdżona w bloku (nie jest bezpośrednim dzieckiem elementGroup)
            if (obj.parent && obj.parent !== elementGroup) {
              let current: THREE.Object3D | null = obj.parent
              let isNestedInBlock = false
              const baseGroups = postConfig?.groups || []
              while (current && current !== elementGroup) {
                const parentName = current.name || ''
                if (baseGroups.some(baseName => 
                  parentName === baseName || parentName.startsWith(`${baseName}_copy_`)
                )) {
                  isNestedInBlock = true
                  break
                }
                current = current.parent
              }
              if (isNestedInBlock) {
                nestedDecorationsToRemove.push(obj)
              }
            }
          }
        })
        
        if (nestedDecorationsToRemove.length > 0) {
          DEBUG_LOGS && console.log(`🧹 S22 (addFenceElement FINALNE): Znaleziono ${nestedDecorationsToRemove.length} zagnieżdżonych dekoracji do usunięcia`)
          nestedDecorationsToRemove.forEach(dec => {
            if (dec.parent) {
              dec.parent.remove(dec)
              DEBUG_LOGS && console.log(`🧹 S22 (addFenceElement FINALNE): Usunięto zagnieżdżoną dekorację ${dec.name || 'unnamed'} z ${dec.parent.name || 'unknown'}`)
            }
          })
        }
        
        // Teraz znajdź główną dekorację (bezpośrednie dziecko elementGroup)
        const decorationBlock = elementGroup.children.find((child: any) => {
          const childName = child.name || ''
          return decorationNamesToRemove.some(decName => childName === decName)
        })
          
          if (decorationBlock) {
            const decorationOffset = (postConfig as any)?.decorationOffset || 0
            const decorationOffsetZ = (postConfig as any)?.decorationOffsetZ || 0
            decorationBlock.position.x += decorationOffset
            decorationBlock.position.z += decorationOffsetZ
          DEBUG_LOGS && console.log(`🎯 S22 post: Blok dekoracyjny ${decorationBlock.name} przesunięty podczas tworzenia: X=${decorationBlock.position.x.toFixed(3)} (+${decorationOffset}), Z=${decorationBlock.position.z.toFixed(3)} (+${decorationOffsetZ})`)
        } else {
          console.warn(`⚠️ S22 post: Nie znaleziono głównej dekoracji ${decorationGroupName} po czyszczeniu`)
        }
      }
      
      // 🎯 Dla P22 post: zastosuj decorationOffset i decorationOffsetZ do bloczku dekoracyjnego
      if (elementType === 'post' && fenceConfig.id === 'lupane-muro-p22') {
        const postConfig = fenceConfig.elements?.post
        const decorationGroupName = postConfig?.decorationGroup
        
        if (decorationGroupName) {
          const decorationBlock = elementGroup.children.find((child: any) => child.name === decorationGroupName)
          
          if (decorationBlock) {
            const decorationOffset = (postConfig as any)?.decorationOffset || 0
            const decorationOffsetZ = (postConfig as any)?.decorationOffsetZ || 0
            decorationBlock.position.x += decorationOffset
            decorationBlock.position.z += decorationOffsetZ
            DEBUG_LOGS && console.log(`🎯 P22 post: Blok dekoracyjny ${decorationGroupName} przesunięty: X=${decorationBlock.position.x.toFixed(3)} (+${decorationOffset}), Z=${decorationBlock.position.z.toFixed(3)} (+${decorationOffsetZ})`)
          }
        }
      }

      // 🎯 Dla Slim 3D: ustaw pozycję Y bloków dekoracyjnych na górze bloków
      if (fenceConfig.id === 'slim-3d') {
        const elementConfigForSlim = fenceConfig.elements?.[elementType as 'post' | 'section']
        const decorationGroupName = elementConfigForSlim?.decorationGroup
        
        if (decorationGroupName) {
          const decorationBlocks = elementGroup.children.filter((child: any) => 
            child.name === decorationGroupName
          )
          
          if (decorationBlocks.length > 0) {
            // Znajdź najwyższy blok podstawowy (nie dekoracyjny, nie metalowy)
            const basicGroupName = elementConfigForSlim?.groups?.[0]
            const metalGroupName = elementConfigForSlim?.metalGroup
            const basicBlocks = elementGroup.children.filter((child: any) => 
              (basicGroupName && (child.name === basicGroupName || child.name.startsWith(`${basicGroupName}_copy_`))) &&
              child.name !== decorationGroupName &&
              child.name !== metalGroupName
            )
            
            if (basicBlocks.length > 0) {
              basicBlocks.sort((a, b) => a.position.y - b.position.y)
              const highestBlock = basicBlocks[basicBlocks.length - 1]
              
              const decorationSpacing = elementConfigForSlim?.decorationSpacing || 0.15
              const blockSpacing = elementConfigForSlim?.blockSpacing || 0.1
              
              // Ustaw pozycję Y na górze bloków
              let decorationY = highestBlock.position.y

              // Ustaw orientację dekoracji zgodnie z bloczkiem bazowym (dla słupków i przęseł)
              const axisX = new THREE.Vector3(1, 0, 0)
              const axisY = new THREE.Vector3(0, 1, 0)
              const axisZ = new THREE.Vector3(0, 0, 1)
              const decorationLengthScale = elementConfigForSlim?.decorationLengthScale || 1
              type Axis = 'x' | 'y' | 'z'
              const axisNames: Axis[] = ['x', 'y', 'z']

              decorationBlocks.forEach(decorationBlock => {
                const originalQuat = decorationBlock.quaternion.clone()
                const originalScale = decorationBlock.scale.clone()
                const baseQuat = highestBlock.quaternion.clone()
                const candidates: Array<{ axis?: THREE.Vector3; angle: number }> = [
                  { angle: 0 },
                  { axis: axisX, angle: Math.PI / 2 },
                  { axis: axisX, angle: -Math.PI / 2 },
                  { axis: axisY, angle: Math.PI / 2 },
                  { axis: axisY, angle: -Math.PI / 2 },
                  { axis: axisZ, angle: Math.PI / 2 },
                  { axis: axisZ, angle: -Math.PI / 2 }
                ]

                let bestQuat = originalQuat.clone()
                let bestScore = -Infinity

                candidates.forEach(candidate => {
                  decorationBlock.quaternion.copy(baseQuat)
                  decorationBlock.scale.copy(originalScale)
                  decorationBlock.position.copy(highestBlock.position)
                  if (candidate.axis) {
                    decorationBlock.rotateOnAxis(candidate.axis, candidate.angle)
                  }
                  decorationBlock.updateMatrixWorld(true)
                  const size = new THREE.Vector3()
                  new THREE.Box3().setFromObject(decorationBlock).getSize(size)
                  const width = size.x
                  const height = size.y
                  const depth = size.z
                  const score = (width - depth) - height
                  if (score > bestScore) {
                    bestScore = score
                    bestQuat = decorationBlock.quaternion.clone()
                  }
                })

                decorationBlock.quaternion.copy(bestQuat)
                decorationBlock.scale.copy(originalScale)
                decorationBlock.updateMatrixWorld(true)

                if (decorationLengthScale !== 1) {
                  const size = new THREE.Vector3()
                  new THREE.Box3().setFromObject(decorationBlock).getSize(size)
                  const axisVectors: Record<Axis, THREE.Vector3> = {
                    x: new THREE.Vector3(1, 0, 0),
                    y: new THREE.Vector3(0, 1, 0),
                    z: new THREE.Vector3(0, 0, 1)
                  }
                  const worldUp = new THREE.Vector3(0, 1, 0)
                  let verticalAxis: Axis = 'y'
                  let maxDot = -Infinity

                  axisNames.forEach((axisName: Axis) => {
                    const worldDir = axisVectors[axisName].clone().applyQuaternion(decorationBlock.quaternion)
                    const dot = Math.abs(worldDir.dot(worldUp))
                    if (dot > maxDot) {
                      maxDot = dot
                      verticalAxis = axisName
                    }
                  })

                  const horizontalAxes = axisNames.filter(axis => axis !== verticalAxis)
                  const axisSize = (axis: Axis) => axis === 'x' ? size.x : axis === 'y' ? size.y : size.z
                  let targetAxis: Axis = horizontalAxes[0]
                  if (horizontalAxes.length > 1) {
                    targetAxis = axisSize(horizontalAxes[0]) <= axisSize(horizontalAxes[1]) ? horizontalAxes[0] : horizontalAxes[1]
                  }

                  decorationBlock.scale[targetAxis] *= decorationLengthScale
                  decorationBlock.updateMatrixWorld(true)
                }
              })

              if (elementType === 'post') {
                // Dla słupka: dodaj blockSpacing (wysokość bloku)
                decorationY += blockSpacing
              }

              // Dla sekcji: nie dodajemy blockSpacing, bo dekoracja jest NAD blokiem
              decorationY += decorationSpacing
              
              const decorationOffsetX = elementConfigForSlim?.decorationOffset || 0
              const decorationOffsetZ = elementConfigForSlim?.decorationOffsetZ || 0

              decorationBlocks.forEach(decorationBlock => {
                decorationBlock.position.set(
                  highestBlock.position.x + decorationOffsetX,
                  decorationY,
                  highestBlock.position.z + decorationOffsetZ
                )
                console.log(
                  `🎯 Slim 3D: Blok dekoracyjny ${decorationGroupName} ustawiony na (${decorationBlock.position.x.toFixed(
                    3
                  )}, ${decorationBlock.position.y.toFixed(3)}, ${decorationBlock.position.z.toFixed(3)})`
                )
              })

              // 🎯 ZAPAMIĘTAJ BAZOWĄ POZYCJĘ DEKORACJI (X/Z), ŻEBY PRZY ZMIANIE WYSOKOŚCI NIE PRZESUWAŁA SIĘ W PRAWO
              if (decorationBlocks.length > 0) {
                // Zapisz w userData grupy sekcji (section_group)
                const sectionGroup = elementGroup
                if (sectionGroup) {
                  sectionGroup.userData.decorationBaseX = decorationBlocks[0].position.x
                  sectionGroup.userData.decorationBaseZ = decorationBlocks[0].position.z
                  console.log(
                    `🎯 Slim 3D: Zapamiętano bazową pozycję dekoracji X=${sectionGroup.userData.decorationBaseX.toFixed(
                      3
                    )}, Z=${sectionGroup.userData.decorationBaseZ.toFixed(3)}`
                  )
                }
              }
            }
          }
        }
      }

      // Ustaw pozycję grupy (ŚRODEK elementu - jak przekazano)
      let finalPositionY = position.y
      
      // 🚪 Dla gate i door w gladkie_max, gladkie_long, slim3d-kopia, slim-3d, slim, vision-bet-long, vision-bet-max, vision-bet-medium, royal_s50, royal_p50, muro p22, muro s22, muro p38 i muro s38: obniż do poziomu ziemi (Y=0)
      // 🎯 Dla Muro Mix: sprawdź również originalFenceConfigId i variant
      const isMuroMixS38 = originalFenceConfigId === 'lupane-muro-mix' && variant === 's38'
      if (
        (elementType === 'gate' || elementType === 'door') &&
        (fenceConfig.id === 'gladkie_max' ||
          fenceConfig.id === 'gladkie_long' ||
          fenceConfig.id === 'slim3d-kopia' ||
          fenceConfig.id === 'slim-3d' ||
          fenceConfig.id === 'slim' ||
          fenceConfig.id === 'vision-bet-long' ||
          fenceConfig.id === 'vision-bet-max' ||
          fenceConfig.id === 'vision-bet-medium' ||
          fenceConfig.id === 'gladkie_medium' ||
          fenceConfig.id === 'royal_s50' ||
          fenceConfig.id === 'royal_p50' ||
          fenceConfig.id === 'lupane-muro-p22' ||
          fenceConfig.id === 'lupane-muro-s22' ||
          fenceConfig.id === 'lupane-muro-p38' ||
          fenceConfig.id === 'lupane-muro-s38' ||
          isMuroMixS38)
      ) {
        finalPositionY = 0
        let fenceType = ''
        if (fenceConfig.id === 'gladkie_max') fenceType = 'Gladkie-Max'
        else if (fenceConfig.id === 'gladkie_long' || fenceConfig.id === 'slim3d-kopia') fenceType = 'Gladkie-Long'
        else if (fenceConfig.id === 'slim-3d') fenceType = 'Slim-3D'
        else if (fenceConfig.id === 'slim') fenceType = 'Slim'
        else if (fenceConfig.id === 'vision-bet-long') fenceType = 'Vision-Bet-Long'
        else if (fenceConfig.id === 'vision-bet-max') fenceType = 'Vision-Bet-Max'
        else if (fenceConfig.id === 'vision-bet-medium') fenceType = 'Vision-Bet-Medium'
        else if (fenceConfig.id === 'gladkie_medium') fenceType = 'Gladkie-Medium'
        else if (fenceConfig.id === 'royal_s50') fenceType = 'Royal-S50'
        else if (fenceConfig.id === 'royal_p50') fenceType = 'Royal-P50'
        else if (fenceConfig.id === 'lupane-muro-p22') fenceType = 'Muro-P22'
        else if (fenceConfig.id === 'lupane-muro-s22') fenceType = 'Muro-S22'
        else if (fenceConfig.id === 'lupane-muro-p38') fenceType = 'Muro-P38'
        else if (fenceConfig.id === 'lupane-muro-s38' || isMuroMixS38) fenceType = 'Muro-S38'
        DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType}: obniżono do poziomu ziemi (Y=0, było ${position.y.toFixed(3)})`)
      }
      
      elementGroup.position.set(position.x, finalPositionY, position.z)

      // 🎯 FIX: Dla gate i door w Slim 3D - przesuń o 0.019 w osi Z
      if ((elementType === 'gate' || elementType === 'door') && fenceConfig.id === 'slim-3d') {
        const gateDoorZOffset = 0.019 // Przesunięcie w osi Z dla gate/door w Slim 3D
        elementGroup.position.z += gateDoorZOffset
        DEBUG_LOGS && console.log(`🎯 Slim-3D ${elementType}: przesunięto o ${gateDoorZOffset}m w osi Z, nowa pozycja Z=${elementGroup.position.z.toFixed(3)}m`)
      }
      
      // 🎯 Dla P22 post - przesuń cały słupek w osi Z PO rotacji (TYLKO wizualne)
      // ⚠️ WAŻNE: Przesunięcie TYLKO wizualne - rightEdge pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
      // ⚠️ WAŻNE: Przesunięcie PO rotacji, żeby nie wpływało na geometryOffsetZ i pivotPoint
      if (elementType === 'post' && fenceConfig.id === 'lupane-muro-p22') {
        const postConfig = fenceConfig.elements?.post
        const postZOffset = (postConfig as any)?.postOffsetZ || 0.172
        // Utwórz wektor przesunięcia w lokalnym układzie (do przodu = +Z)
        const localOffset = new THREE.Vector3(0, 0, postZOffset)
        // Zastosuj rotację do wektora przesunięcia (w globalnym układzie)
        localOffset.applyEuler(new THREE.Euler(0, elementGroup.rotation.y, 0))
        // Dodaj przesunięcie do pozycji (TYLKO wizualne)
        elementGroup.position.add(localOffset)
        DEBUG_LOGS && console.log(`🎯 P22 post: przesunięto o ${postZOffset}m w kierunku rotacji (${(elementGroup.rotation.y * 180 / Math.PI).toFixed(1)}°) - TYLKO wizualne, nowa pozycja X=${elementGroup.position.x.toFixed(3)}, Z=${elementGroup.position.z.toFixed(3)}m`)
        // ⚠️ NIE aktualizuj rightEdge - pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
      }
      
      // 🎯 Dla S22 post - przesuń cały słupek w osi Z PO rotacji (TYLKO wizualne)
      // ⚠️ WAŻNE: Przesunięcie TYLKO wizualne - rightEdge pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
      // ⚠️ WAŻNE: Przesunięcie PO rotacji, żeby nie wpływało na geometryOffsetZ i pivotPoint
      if (elementType === 'post' && fenceConfig.id === 'lupane-muro-s22') {
        const postConfig = fenceConfig.elements?.post
        const postZOffset = (postConfig as any)?.postOffsetZ || 0.172
        // Utwórz wektor przesunięcia w lokalnym układzie (do przodu = +Z)
        const localOffset = new THREE.Vector3(0, 0, postZOffset)
        // Zastosuj rotację do wektora przesunięcia (w globalnym układzie)
        localOffset.applyEuler(new THREE.Euler(0, elementGroup.rotation.y, 0))
        // Dodaj przesunięcie do pozycji (TYLKO wizualne)
        elementGroup.position.add(localOffset)
        DEBUG_LOGS && console.log(`🎯 S22 post: przesunięto o ${postZOffset}m w kierunku rotacji (${(elementGroup.rotation.y * 180 / Math.PI).toFixed(1)}°) - TYLKO wizualne, nowa pozycja X=${elementGroup.position.x.toFixed(3)}, Z=${elementGroup.position.z.toFixed(3)}m`)
        // ⚠️ NIE aktualizuj rightEdge - pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
      }
      
      // 🎯 Dla P22 section - przesuń całe przęsło w osi Z PO rotacji (TYLKO wizualne)
      // ⚠️ WAŻNE: Przesunięcie TYLKO wizualne - rightEdge pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
      // ⚠️ WAŻNE: Przesunięcie PO rotacji, żeby nie wpływało na geometryOffsetZ i pivotPoint
      if (elementType === 'section' && fenceConfig.id === 'lupane-muro-p22') {
        const sectionConfig = fenceConfig.elements?.section
        const sectionZOffset = (sectionConfig as any)?.sectionOffsetZ || 0
        if (sectionZOffset !== 0) {
          // Utwórz wektor przesunięcia w lokalnym układzie (do przodu = +Z)
          const localOffset = new THREE.Vector3(0, 0, sectionZOffset)
          // Zastosuj rotację do wektora przesunięcia (w globalnym układzie)
          localOffset.applyEuler(new THREE.Euler(0, elementGroup.rotation.y, 0))
          // Dodaj przesunięcie do pozycji (TYLKO wizualne)
          elementGroup.position.add(localOffset)
          DEBUG_LOGS && console.log(`🎯 P22 section: przesunięto o ${sectionZOffset}m w kierunku rotacji (${(elementGroup.rotation.y * 180 / Math.PI).toFixed(1)}°) - TYLKO wizualne, nowa pozycja X=${elementGroup.position.x.toFixed(3)}, Z=${elementGroup.position.z.toFixed(3)}m`)
          // ⚠️ NIE aktualizuj rightEdge - pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
        }
      }
      
      // 🎯 Dla S22 section - przesuń całe przęsło w osi Z PO rotacji (TYLKO wizualne)
      // ⚠️ WAŻNE: Przesunięcie TYLKO wizualne - rightEdge pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
      // ⚠️ WAŻNE: Przesunięcie PO rotacji, żeby nie wpływało na geometryOffsetZ i pivotPoint
      if (elementType === 'section' && fenceConfig.id === 'lupane-muro-s22') {
        const sectionConfig = fenceConfig.elements?.section
        const sectionZOffset = (sectionConfig as any)?.sectionOffsetZ || 0
        if (sectionZOffset !== 0) {
          // Utwórz wektor przesunięcia w lokalnym układzie (do przodu = +Z)
          const localOffset = new THREE.Vector3(0, 0, sectionZOffset)
          // Zastosuj rotację do wektora przesunięcia (w globalnym układzie)
          localOffset.applyEuler(new THREE.Euler(0, elementGroup.rotation.y, 0))
          // Dodaj przesunięcie do pozycji (TYLKO wizualne)
          elementGroup.position.add(localOffset)
          DEBUG_LOGS && console.log(`🎯 S22 section: przesunięto o ${sectionZOffset}m w kierunku rotacji (${(elementGroup.rotation.y * 180 / Math.PI).toFixed(1)}°) - TYLKO wizualne, nowa pozycja X=${elementGroup.position.x.toFixed(3)}, Z=${elementGroup.position.z.toFixed(3)}m`)
          // ⚠️ NIE aktualizuj rightEdge - pozostaje bez przesunięcia, żeby kolejne elementy były w odpowiednim miejscu
        }
      }
      
      // 🎯 Dla S38 post - przesuń cały słupek w osi Z
      if (elementType === 'post' && fenceConfig.id === 'lupane-muro-s38') {
        const postConfig = fenceConfig.elements?.post
        const postZOffset = (postConfig as any)?.postOffsetZ || 0
        if (postZOffset !== 0) {
          elementGroup.position.z += postZOffset
          DEBUG_LOGS && console.log(`🎯 S38 post: przesunięto cały słupek o ${postZOffset}m w osi Z, nowa pozycja Z=${elementGroup.position.z.toFixed(3)}m`)
        }
      }
      
      // 🎯 Dla S38 section - przesuń całe przęsło w osi Z
      if (elementType === 'section' && fenceConfig.id === 'lupane-muro-s38') {
        const sectionConfig = fenceConfig.elements?.section
        const sectionZOffset = (sectionConfig as any)?.sectionOffsetZ || 0
        if (sectionZOffset !== 0) {
          elementGroup.position.z += sectionZOffset
          DEBUG_LOGS && console.log(`🎯 S38 section: przesunięto całe przęsło o ${sectionZOffset}m w osi Z, nowa pozycja Z=${elementGroup.position.z.toFixed(3)}m`)
        }
      }

      // Zapisz wymiary
      elementGroup.userData.width = dimensions.width
      elementGroup.userData.height = dimensions.height
      elementGroup.userData.widthMultiplier = widthMultiplier // 🎯 Zapisz mnożnik szerokości
      elementGroup.userData.rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, widthMultiplier)
      elementGroup.userData.originalFenceConfigId = originalFenceConfigId // 🎯 Zapisz oryginalny ID (dla Royal Mix)
      elementGroup.userData.variant = variant // 🎯 Zapisz variant (np. s50, p50) dla Royal Mix / Muro Mix
      
      // 🎯 Dla sections: zapisz wysokość w blokach (uwzględniając dekorację)
      // Początkowy element ma już 1 zwykły + decorationBlocksCount dekoracyjnych
      if (elementType === 'section' && elementConfig) {
        const decorationBlocksCount = (elementConfig as any).decorationBlocksCount ?? 1
        const initialHeightInBlocks = 1 + decorationBlocksCount // 1 zwykły + dekoracyjne
        elementGroup.userData.heightInBlocks = initialHeightInBlocks
        DEBUG_LOGS && console.log(`🎯 Section ${elementId}: Początkowa wysokość w blokach: ${initialHeightInBlocks} (1 zwykły + ${decorationBlocksCount} dekoracyjny)`)
      }
      
      // 🎯 Dla sections, gate, door: zapisz bazową szerokość (1x) dla pivot point
            if (elementType === 'section' || elementType === 'gate' || elementType === 'door' || isVisionBetGateDoor) {
              elementGroup.userData.baseWidth = baseWidth
              DEBUG_LOGS && console.log(`🎯 ${elementType} ${elementId}: baseWidth=${baseWidth.toFixed(3)}m, width=${dimensions.width.toFixed(3)}m, widthMultiplier=${widthMultiplier}`)
            }
      
      // Zapisz oryginalną pozycję (środek) dla obliczania pivot point
      elementGroup.userData.originalPosition = new THREE.Vector3(position.x, position.y, position.z)
      // 🎯 Dla section z defaultHeight > 1: dodaj dodatkowe bloki w pionie (jak dla post)
      // UWAGA: To musi być PRZED powieleniem w poziomie, żeby każda kopia miała odpowiednią wysokość
      if (elementType === 'section' && elementConfig.defaultHeight && elementConfig.defaultHeight > 1) {
        const baseGroupName = elementConfig.groups[0] // Pierwszy group (group_13)
        // Policz wszystkie bloki baseGroupName (łącznie z tymi z widthMultiplier)
        const currentBlocks = elementGroup.children.filter((child: any) => {
          const childName = child.name
          // Sprawdź czy to jest baseGroupName lub jego kopia z widthMultiplier (ale bez _copy_)
          return childName === baseGroupName || 
                 (childName.startsWith(baseGroupName) && childName.includes('_copy_'))
        }).length
        
        // Dla section z widthMultiplier: każdy zestawienie powinno mieć defaultHeight bloków
        // Obecnie mamy widthMultiplier bloków (po 1 na zestawienie)
        // Potrzebujemy widthMultiplier * defaultHeight bloków łącznie
        const totalBlocksNeeded = widthMultiplier * elementConfig.defaultHeight
        const blocksToAdd = totalBlocksNeeded - currentBlocks
        
        DEBUG_LOGS && console.log(`🎯 Section ${elementId}: defaultHeight=${elementConfig.defaultHeight}, widthMultiplier=${widthMultiplier}, currentBlocks=${currentBlocks}, totalBlocksNeeded=${totalBlocksNeeded}, blocksToAdd=${blocksToAdd}`)
        
        if (blocksToAdd > 0) {
          // Dodaj bloki dla każdego zestawienia (widthMultiplier)
          for (let wmIndex = 0; wmIndex < widthMultiplier; wmIndex++) {
            // Znajdź wszystkie bloki dla tego zestawienia (na podstawie pozycji X)
            const baseBlockForSet = elementGroup.children.find((child: any) => {
              if (child.name === baseGroupName || child.name.startsWith(`${baseGroupName}_copy_`)) {
                // Sprawdź czy to blok dla tego zestawienia na podstawie pozycji X
                // Bloki są rozmieszczone w poziomie z rightOffset i blockCenterOffset
                const rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, widthMultiplier)
                const blockCenterOffset = (wmIndex - (widthMultiplier - 1) / 2) * baseWidth
                const expectedX = blockCenterOffset + rightOffset
                return Math.abs(child.position.x - expectedX) < 0.01 // Tolerancja
              }
              return false
            })
            
            if (baseBlockForSet) {
              // Dla każdego zestawienia: dodaj (defaultHeight - 1) dodatkowych bloków w pionie
              const blocksForThisSet = elementConfig.defaultHeight - 1
              DEBUG_LOGS && console.log(`🎯 Dodaję ${blocksForThisSet} bloków dla zestawienia ${wmIndex + 1}/${widthMultiplier} na pozycji X=${baseBlockForSet.position.x.toFixed(3)}`)
              
              for (let h = 0; h < blocksForThisSet; h++) {
                const sourceElement = sourceModel.children.find((child: any) => {
                  if (child.name === 'SketchUp') {
                    return child.children.find((c: any) => c.name === baseGroupName)
                  }
                  return false
                })
                
                if (sourceElement) {
                  const sketchUpGroup = sourceModel.children.find((child: any) => child.name === 'SketchUp')
                  const baseBlock = sketchUpGroup?.children.find((child: any) => child.name === baseGroupName)
                  
                  if (baseBlock) {
                    const clonedElement = baseBlock.clone()
                    const existingBlocksForSet = elementGroup.children.filter((child: any) => {
                      if (child.name === baseGroupName || child.name.startsWith(`${baseGroupName}_copy_`)) {
                        return Math.abs(child.position.x - baseBlockForSet.position.x) < 0.01
                      }
                      return false
                    })
                    
                    const blockSpacing = elementConfig.blockSpacing || 0.119
                    let offsetY = baseBlockForSet.position.y
                    if (existingBlocksForSet.length > 0) {
                      existingBlocksForSet.sort((a: any, b: any) => a.position.y - b.position.y)
                      const highestBlock = existingBlocksForSet[existingBlocksForSet.length - 1]
                      offsetY = highestBlock.position.y + blockSpacing
                    }
                    
                    prepareElement(clonedElement, {
                      modelScale: fenceConfig.modelScale || 0.01,
                      positionY: offsetY,
                      elementId,
                      textures,
                      elementWidth: baseWidth,
                      fenceConfig
                    })
                    
                    // Ustaw pozycję X taką samą jak baseBlockForSet
                    clonedElement.position.x = baseBlockForSet.position.x
                    
                    clonedElement.userData.fenceElementId = elementId
                    elementGroup.add(clonedElement)
                    DEBUG_LOGS && console.log(`✅ Dodano blok ${h + 1}/${blocksForThisSet} dla zestawienia ${wmIndex + 1} na Y=${offsetY.toFixed(3)}`)
                  }
                }
              }
              
              // 🎯 Po dodaniu bloków w pionie: zaktualizuj pozycję bloku dekoracyjnego dla tego zestawienia
              const decorationGroupName = elementConfig.decorationGroup || elementConfig.groups[1]
              if (decorationGroupName) {
                // Znajdź bloczek dekoracyjny dla tego zestawienia (ten sam X)
                // Szukaj z większą tolerancją, bo może być mała różnica w pozycji X
                const allDecorationBlocks = elementGroup.children.filter((child: any) => child.name === decorationGroupName)
                const expectedX = baseBlockForSet.position.x
                
                DEBUG_LOGS && console.log(`🔍 Szukam bloku dekoracyjnego ${decorationGroupName} dla zestawienia ${wmIndex + 1}, expectedX=${expectedX.toFixed(3)}, znaleziono ${allDecorationBlocks.length} bloków dekoracyjnych`)
                
                const decorationBlockForSet = allDecorationBlocks.find((child: any) => {
                  const diff = Math.abs(child.position.x - expectedX)
                  DEBUG_LOGS && console.log(`  - Bloczek dekoracyjny na X=${child.position.x.toFixed(3)}, diff=${diff.toFixed(3)}`)
                  return diff < 0.1 // Zwiększona tolerancja
                })
                
                if (decorationBlockForSet) {
                  // Znajdź najwyższy blok group_13 dla tego zestawienia
                  const allBlocksForSet = elementGroup.children.filter((child: any) => {
                    if (child.name === baseGroupName || child.name.startsWith(`${baseGroupName}_copy_`)) {
                      return Math.abs(child.position.x - baseBlockForSet.position.x) < 0.01
                    }
                    return false
                  })
                  
                  if (allBlocksForSet.length > 0) {
                    allBlocksForSet.sort((a: any, b: any) => a.position.y - b.position.y)
                    const highestBlock = allBlocksForSet[allBlocksForSet.length - 1]
                    const decorationSpacing = elementConfig.decorationSpacing || 0.119
                    
                    // Ustaw bloczek dekoracyjny nad najwyższym blokiem
                    decorationBlockForSet.position.y = highestBlock.position.y + decorationSpacing
                    DEBUG_LOGS && console.log(`🎯 Blok dekoracyjny ${decorationGroupName} dla zestawienia ${wmIndex + 1} przesunięty na Y=${decorationBlockForSet.position.y.toFixed(3)} (nad blokiem na Y=${highestBlock.position.y.toFixed(3)})`)
                  }
                } else {
                  console.warn(`⚠️ Nie znaleziono bloku dekoracyjnego ${decorationGroupName} dla zestawienia ${wmIndex + 1} na pozycji X=${baseBlockForSet.position.x.toFixed(3)}`)
                  console.warn(`   Dostępne bloki dekoracyjne:`, allDecorationBlocks.map((b: any) => `X=${b.position.x.toFixed(3)}`))
                }
              }
            }
          }
          
          DEBUG_LOGS && console.log(`✅ Dodano dodatkowe bloki w pionie dla section i zaktualizowano pozycje bloków dekoracyjnych`)
        }
      }
      
      // 🎯 Dziedzicz rotację z poprzedniego elementu (jeśli istnieje)
      const allElements = Array.from(this.elements.values())
      const sortedElements = allElements.sort((a: any, b: any) => {
        const aTimestamp = parseInt(a.userData?.fenceElementId.split('-')[1] || '0')
        const bTimestamp = parseInt(b.userData?.fenceElementId.split('-')[1] || '0')
        return aTimestamp - bTimestamp
      })
      const previousElement = sortedElements[sortedElements.length - 1]
      const inheritedRotation = previousElement?.userData?.absoluteRotation || 0
      
      DEBUG_LOGS && console.log(`🔄 Dziedziczenie rotacji: poprzedni element=${previousElement?.userData?.fenceElementId}, absoluteRotation=${inheritedRotation}°`)
      
      elementGroup.userData.inheritedRotation = inheritedRotation // Dziedzicz rotację z poprzedniego elementu
      elementGroup.userData.rotation = 0 // Suwak na 0°
      elementGroup.userData.absoluteRotation = inheritedRotation // Finalna = inheritedRotation + 0 = inheritedRotation
      elementGroup.userData.reactRotation = 0 // React suwak na 0°
      
      // 🎯 Ustaw wizualną rotację elementu
      if (inheritedRotation !== 0) {
        const rotationInRadians = (inheritedRotation * Math.PI) / 180
        elementGroup.rotation.y = rotationInRadians
        DEBUG_LOGS && console.log(`🔄 Ustawiono wizualną rotację: ${inheritedRotation}° (${rotationInRadians.toFixed(3)} rad)`)
      }
      
      // 🎯 Dla Royal Mix + P50: NIE przesuwaj pierwszego elementu - punkt startowy już ma z: 0
      // Usunięto przesunięcie wizualne, które powodowało przesunięcie pierwszego słupka w osi Z
      
      // 🎯 Dla gate/door w gladkie_long (i podobnych): ZAWSZE przelicz rightEdge używając tej samej logiki co updateSectionWidth
      // To zapewnia, że rightEdge będzie obliczony identycznie jak przy zmianie szerokości na suwaku
      // Nieważne czy element ma rotację czy nie - zawsze przeliczamy z uwzględnieniem actualRotation
      if ((elementType === 'gate' || elementType === 'door') && isVisionBetGateDoor) {
        DEBUG_LOGS && console.log(`🔍 DEBUG ${elementType}: isVisionBetGateDoor=${isVisionBetGateDoor}, elementConfig=${elementConfig ? 'OK' : 'NULL'}, spacing=${elementConfig?.spacing || 'NULL'}, fenceConfig.id=${fenceConfig.id}`)
        
        // ⚠️ Upewnij się, że elementConfig jest dostępne
        if (!elementConfig) {
          console.error(`❌ BŁĄD dla ${elementType}: elementConfig jest NULL! Nie można przeliczyć rightEdge.`)
        } else {
          const absoluteRotation = elementGroup.userData?.absoluteRotation || 0
          const absoluteRotationRad = (absoluteRotation * Math.PI) / 180
          const currentWidth = elementGroup.userData?.width || dimensions.width
          const currentWidthMultiplier = elementGroup.userData?.widthMultiplier || widthMultiplier
          
          // 🎯 ZAWSZE przelicz rightEdge dla gate/door w gladkie_long (niezależnie od fenceConfig.id)
          // SKOPIOWANE DOKŁADNIE Z SECTION ROYAL_S50/P50 - używam position.x tak samo jak dla section
          // Oblicz rightEdgeX tak samo jak dla section - używam position.x zamiast elementGroup.position.x
          let rightEdgeX = position.x + (dimensions.width / 2)
          
          // 🎯 Dla royal_s50 i royal_p50 gate/door: użyj DOKŁADNIE tej samej logiki co dla section
          // 🚪 WAŻNE: Dla gate i door w Royal S50/P50: odejmij spacing od rightEdge dla widthMultiplier >= 2
          // żeby zachować odstępy takie same jak dla bram (które mają domyślnie 2x i działają poprawnie)
          if (fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50') {
            // 🎯 Dla Royal S50/P50 gate/door: specjalna logika - dla widthMultiplier >= 2 odejmij spacing od rightEdge
            // Dla 2x: odejmij spacing (tak samo jak dla bram, które działają poprawnie)
            // Dla 3x+: odstęp rośnie liniowo
            const royalType = fenceConfig.id === 'royal_s50' ? 'S50' : 'P50'
            const spacing = elementConfig.spacing || 0.198
            if (currentWidthMultiplier >= 2) {
              // Dla 2x: odejmij spacing (zachowaj odstęp jak dla bram)
              // Dla 3x+: użyj wzoru (widthMultiplier - 2) * spacing * 0.5 + spacing
              let reduction = spacing // Dla 2x: po prostu spacing
              if (currentWidthMultiplier >= 3) {
                // Dla 3x+: (widthMultiplier - 2) * spacing * 0.5 + spacing
                // Dla 3x: (3-2) * spacing * 0.5 + spacing = 0.5 * spacing + spacing = 1.5 * spacing
                // Dla 4x: (4-2) * spacing * 0.5 + spacing = 1.0 * spacing + spacing = 2.0 * spacing
                // Dla 5x: (5-2) * spacing * 0.5 + spacing = 1.5 * spacing + spacing = 2.5 * spacing
                reduction = (currentWidthMultiplier - 2) * spacing * 0.5 + spacing
              }
              rightEdgeX -= reduction
              DEBUG_LOGS && console.log(`🔍 DEBUG (Royal ${royalType} ${elementType} ${currentWidthMultiplier}x): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Royal ${royalType} ${elementType} ${currentWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge (zachowaj odstęp jak dla bram)`)
            } else {
              // Dla 1x: użyj prostego rightEdge bez kompensacji
              DEBUG_LOGS && console.log(`🔍 DEBUG (Royal ${royalType} ${elementType}): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Royal ${royalType} ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
            }
            
            // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją) - DOKŁADNIE jak dla section
            const rightEdgeOffsetX = rightEdgeX - position.x
            const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
            rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            const actualRightEdge = elementGroup.position.clone().add(rightEdgeOffset)
            elementGroup.userData.rightEdge = actualRightEdge
            DEBUG_LOGS && console.log(`🔄 Przeliczono rightEdge dla ${elementType} po dodaniu: X=${actualRightEdge.x.toFixed(3)}, Z=${actualRightEdge.z.toFixed(3)} (width=${currentWidth.toFixed(3)}m, widthMultiplier=${currentWidthMultiplier}, rotacja=${absoluteRotation}°)`)
          } else if (fenceConfig.id === 'lupane-muro-p22') {
            // 🎯 Dla Muro P22 gate/door: użyj DOKŁADNIE tej samej logiki co dla section
            // Dla widthMultiplier >= 2 odejmij część spacing od rightEdge
            if (currentWidthMultiplier >= 2) {
              const spacing = elementConfig.spacing || 0.074
              // Różne kompensacje dla różnych widthMultiplier
              let reduction = 0
              if (currentWidthMultiplier === 2) {
                reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
              } else if (currentWidthMultiplier === 3) {
                reduction = spacing * 0.0 // 0.0 dla 3x
              } else if (currentWidthMultiplier === 4) {
                reduction = spacing * 1.0 // 0.074 dla 4x
              } else if (currentWidthMultiplier === 5) {
                reduction = spacing * 2.0 // 0.148 dla 5x
              }
              rightEdgeX -= reduction
              DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 ${elementType} ${currentWidthMultiplier}x): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 ${elementType} ${currentWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
            } else {
              // Dla 1x: użyj prostego rightEdge bez kompensacji
              DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 ${elementType}): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
            }
            
            // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją) - DOKŁADNIE jak dla section
            const rightEdgeOffsetX = rightEdgeX - position.x
            const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
            rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            const actualRightEdge = elementGroup.position.clone().add(rightEdgeOffset)
            elementGroup.userData.rightEdge = actualRightEdge
            DEBUG_LOGS && console.log(`🔄 Przeliczono rightEdge dla ${elementType} po dodaniu: X=${actualRightEdge.x.toFixed(3)}, Z=${actualRightEdge.z.toFixed(3)} (width=${currentWidth.toFixed(3)}m, widthMultiplier=${currentWidthMultiplier}, rotacja=${absoluteRotation}°)`)
          } else if (fenceConfig.id === 'lupane-muro-s22' && isMuroS22GateDoor) {
            // 🎯 Dla Muro S22 gate/door: użyj DOKŁADNIE tej samej logiki co dla section
            // Dla widthMultiplier >= 2 odejmij część spacing od rightEdge
            if (currentWidthMultiplier >= 2) {
              const spacing = elementConfig.spacing || 0.086
              // Różne kompensacje dla różnych widthMultiplier
              let reduction = 0
              if (currentWidthMultiplier === 2) {
                reduction = spacing * -0.9 // -0.077 dla 2x (dodaje zamiast odejmować)
              } else if (currentWidthMultiplier === 3) {
                reduction = spacing * 0.0 // 0.0 dla 3x
              } else if (currentWidthMultiplier === 4) {
                reduction = spacing * 0.9 // 0.077 dla 4x
              } else if (currentWidthMultiplier === 5) {
                reduction = spacing * 1.8 // 0.155 dla 5x
              }
              rightEdgeX -= reduction
              DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22 ${elementType} ${currentWidthMultiplier}x): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22 ${elementType} ${currentWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
            } else {
              // Dla 1x: użyj prostego rightEdge bez kompensacji
              DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22 ${elementType}): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
            }
            
            // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją) - DOKŁADNIE jak dla section
            const rightEdgeOffsetX = rightEdgeX - position.x
            const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
            rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            const actualRightEdge = elementGroup.position.clone().add(rightEdgeOffset)
            elementGroup.userData.rightEdge = actualRightEdge
            DEBUG_LOGS && console.log(`🔄 Przeliczono rightEdge dla ${elementType} po dodaniu: X=${actualRightEdge.x.toFixed(3)}, Z=${actualRightEdge.z.toFixed(3)} (width=${currentWidth.toFixed(3)}m, widthMultiplier=${currentWidthMultiplier}, rotacja=${absoluteRotation}°)`)
          } else if (fenceConfig.id === 'lupane-muro-p38' && isMuroP38GateDoor) {
            // 🎯 Dla Muro P38 gate/door: użyj DOKŁADNIE tej samej logiki co dla section
            // Dla widthMultiplier >= 2 odejmij część spacing od rightEdge
            if (currentWidthMultiplier >= 2) {
              const spacing = elementConfig.spacing || 0.074
              // Różne kompensacje dla różnych widthMultiplier
              let reduction = 0
              if (currentWidthMultiplier === 2) {
                reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
              } else if (currentWidthMultiplier === 3) {
                reduction = spacing * 0.0 // 0.0 dla 3x
              } else if (currentWidthMultiplier === 4) {
                reduction = spacing * 1.0 // 0.074 dla 4x
              } else if (currentWidthMultiplier === 5) {
                reduction = spacing * 2.0 // 0.148 dla 5x
              }
              rightEdgeX -= reduction
              DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38 ${elementType} ${currentWidthMultiplier}x): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38 ${elementType} ${currentWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
            } else {
              // Dla 1x: użyj prostego rightEdge bez kompensacji
              DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38 ${elementType}): widthMultiplier=${currentWidthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
              DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
            }
            
            // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją) - DOKŁADNIE jak dla section
            const rightEdgeOffsetX = rightEdgeX - position.x
            const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
            rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            const actualRightEdge = elementGroup.position.clone().add(rightEdgeOffset)
            elementGroup.userData.rightEdge = actualRightEdge
            DEBUG_LOGS && console.log(`🔄 Przeliczono rightEdge dla ${elementType} po dodaniu: X=${actualRightEdge.x.toFixed(3)}, Z=${actualRightEdge.z.toFixed(3)} (width=${currentWidth.toFixed(3)}m, widthMultiplier=${currentWidthMultiplier}, rotacja=${absoluteRotation}°)`)
          } else {
            // Standardowa logika dla innych ogrodzeń
            let rightEdgeOffsetX = (currentWidth / 2) - (elementConfig.spacing || 0)
            const rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, currentWidthMultiplier)
            const coefficient = this.getCoefficientForWidthMultiplier(elementConfig, currentWidthMultiplier)
            const rightOffsetReduction = rightOffset * coefficient
            rightEdgeOffsetX -= rightOffsetReduction
            
            // Obróć offset o rotację
            const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
            rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            
            // Oblicz finalny rightEdge
            const actualRightEdge = elementGroup.position.clone().add(rightEdgeOffset)
            elementGroup.userData.rightEdge = actualRightEdge
            DEBUG_LOGS && console.log(`🔄 Przeliczono rightEdge dla ${elementType} po dodaniu: X=${actualRightEdge.x.toFixed(3)}, Z=${actualRightEdge.z.toFixed(3)} (width=${currentWidth.toFixed(3)}m, widthMultiplier=${currentWidthMultiplier}, rotacja=${absoluteRotation}°)`)
          }
        }
      } else if (inheritedRotation !== 0) {
        // Dla section i innych elementów: aktualizuj rightEdge tylko jeśli mają rotację
        const rotationInRadians = (inheritedRotation * Math.PI) / 180
        const currentWidth = elementGroup.userData?.width || dimensions.width
        const currentWidthMultiplier = elementGroup.userData?.widthMultiplier || widthMultiplier
        
        if (elementType === 'section' || isVisionBetGateDoor) {
          if (!['lupane-muro-p22', 'lupane-muro-p38', 'lupane-muro-s22', 'lupane-muro-s38', 'royal_s50', 'royal_p50'].includes(fenceConfig.id)) {
            // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją)
            let rightEdgeOffsetX = (currentWidth / 2) - (elementConfig.spacing || 0)
            
            const rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, currentWidthMultiplier)
            const coefficient = this.getCoefficientForWidthMultiplier(elementConfig, currentWidthMultiplier)
            const rightOffsetReduction = rightOffset * coefficient
            rightEdgeOffsetX -= rightOffsetReduction
            
            // Obróć offset o rotację
            const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
            rightEdgeOffset.applyEuler(new THREE.Euler(0, rotationInRadians, 0))
            
            // Oblicz finalny rightEdge
            const actualRightEdge = elementGroup.position.clone().add(rightEdgeOffset)
            elementGroup.userData.rightEdge = actualRightEdge
            DEBUG_LOGS && console.log(`🔄 Zaktualizowano rightEdge po dziedziczeniu rotacji: X=${actualRightEdge.x.toFixed(3)}, Z=${actualRightEdge.z.toFixed(3)} (width=${currentWidth.toFixed(3)}m, widthMultiplier=${currentWidthMultiplier})`)
          }
        }
      }
      
      // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long, slim3d-kopia, slim-3d i slim gate/door: ukryj zwykłe bloki i bloki dekoracyjne
      // Pozostaw tylko metal
      // vision-bet-long: group_18 (zwykły), group_19 (dekoracyjny), group_4 (metal)
      // vision-bet-max: group_14 (zwykły), group_13 (dekoracyjny), group_10 (metal)
      // vision-bet-medium: group_14 (zwykły), group_15 (dekoracyjny), group_16 (metal)
      // gladkie-max: group_14 (zwykły), group_13 (dekoracyjny), group_10 (metal) - takie same jak vision-bet-max
      // gladkie-long: group_3 (zwykły), group_10 (dekoracyjny), group_4 (metal)
      // slim-3d: group_21 (zwykły), group_28 (dekoracyjny), group_24 (metal)
      // slim: group_2 (zwykły), group_0 (dekoracyjny), group_15 (metal)
      if (isVisionBetGateDoor) {
        const isVisionBetLong = fenceConfig.id === 'vision-bet-long'
        const isVisionBetMax = fenceConfig.id === 'vision-bet-max'
        const isVisionBetMedium = fenceConfig.id === 'vision-bet-medium'
        const isGladkieMedium = fenceConfig.id === 'gladkie_medium'
        const isGladkieMax = fenceConfig.id === 'gladkie_max'
        const isGladkieLong = fenceConfig.id === 'gladkie_long' || fenceConfig.id === 'slim3d-kopia'
        const isSlim3d = fenceConfig.id === 'slim-3d'
        const isSlim = fenceConfig.id === 'slim'
        const isRoyalS50 = fenceConfig.id === 'royal_s50'
        const isRoyalP50 = fenceConfig.id === 'royal_p50'
        
        // Określ grupy do ukrycia i pokazania na podstawie typu ogrodzenia
        let normalBlockGroups: string[] = []
        let decorationBlockGroups: string[] = []
        let metalGroup: string = ''
        
        if (isVisionBetLong) {
          normalBlockGroups = ['group_18']
          decorationBlockGroups = ['group_19']
          metalGroup = 'group_4'
        } else if (isVisionBetMax || isGladkieMax) {
          // vision-bet-max i gladkie-max używają tych samych grup
          normalBlockGroups = ['group_14']
          decorationBlockGroups = ['group_13']
          metalGroup = 'group_10'
        } else if (isVisionBetMedium || isGladkieMedium) {
          // vision-bet-medium i gladkie-medium używają grup z konfiguracji section
          const sectionConfig = fenceConfig.elements?.section
          const sectionGroups = sectionConfig?.groups || []
          const sectionMetalGroup = sectionConfig?.metalGroup || ''
          const sectionDecorationGroup = sectionConfig?.decorationGroup || ''
          
          // normalBlockGroups to wszystkie grupy oprócz metalGroup i decorationGroup
          normalBlockGroups = sectionGroups.filter(g => g !== sectionMetalGroup && g !== sectionDecorationGroup)
          decorationBlockGroups = sectionDecorationGroup ? [sectionDecorationGroup] : []
          metalGroup = sectionMetalGroup
        } else if (isGladkieLong) {
          normalBlockGroups = ['group_3']
          decorationBlockGroups = ['group_10']
          metalGroup = 'group_4'
        } else if (isSlim3d) {
          normalBlockGroups = ['group_21']
          decorationBlockGroups = ['group_28']
          metalGroup = 'group_24'
        } else if (isSlim) {
          normalBlockGroups = ['group_2']
          decorationBlockGroups = ['group_0']
          metalGroup = 'group_15'
        } else if (isRoyalS50) {
          // Royal S50: bloczek zwykły + dekoracyjny = group_15, group_21; metal = group_30
          normalBlockGroups = ['group_15']
          decorationBlockGroups = ['group_21']
          metalGroup = 'group_30'
        } else if (isRoyalP50) {
          // Royal P50: bloczek zwykły + dekoracyjny = group_9, group_7; metal = group_30
          normalBlockGroups = ['group_9']
          decorationBlockGroups = ['group_7']
          metalGroup = 'group_30'
        } else if (fenceConfig.id === 'lupane-muro-p22') {
          // Muro P22: bloczek zwykły = group_13; dekoracja = group_42; metal = group_0
          normalBlockGroups = ['group_13']
          decorationBlockGroups = ['group_42']
          metalGroup = 'group_0'
        } else if (fenceConfig.id === 'lupane-muro-s22') {
          // Muro S22: bloczek zwykły = group_44; dekoracja = group_77; metal = group_0
          normalBlockGroups = ['group_44']
          decorationBlockGroups = ['group_77']
          metalGroup = 'group_0'
        } else if (fenceConfig.id === 'lupane-muro-p38') {
          // Muro P38: bloczek zwykły = group_21; dekoracja = group_57; metal = group_0
          normalBlockGroups = ['group_21']
          decorationBlockGroups = ['group_57']
          metalGroup = 'group_0'
        } else if (fenceConfig.id === 'lupane-muro-s38') {
          // Muro S38: bloczek zwykły = group_21; dekoracja = group_57; metal = group_0 (takie same jak P38)
          normalBlockGroups = ['group_21']
          decorationBlockGroups = ['group_57']
          metalGroup = 'group_0'
        }

        // 🎯 SPECJALNY FIX DLA Slim 3D, Royal S50/P50 oraz Muro P22/S22/P38/S38 gate/door:
        // Gate/door korzystają z tej samej geometrii co section (bloczek + metal),
        // ale wizualnie pokazujemy tylko metal. W section metal siedzi idealnie,
        // bo jest "osadzony" w bloczku. Tutaj, po ukryciu bloczków, chcemy żeby
        // metal nadal był ustawiony względem LEWEJ krawędzi sekcji jak w przęśle.
        const isMuroP22 = fenceConfig.id === 'lupane-muro-p22'
        const isMuroS22 = fenceConfig.id === 'lupane-muro-s22'
        const isMuroP38 = fenceConfig.id === 'lupane-muro-p38'
        const isMuroS38 = fenceConfig.id === 'lupane-muro-s38'
        if ((isSlim3d || isRoyalS50 || isRoyalP50 || isMuroP22 || isMuroS22 || isMuroP38 || isMuroS38) && metalGroup) {
          try {
            // Oblicz bounding box CAŁEGO elementu (bloczek + metal)
            const fullBBox = new THREE.Box3().setFromObject(elementGroup)
            const fullCenter = new THREE.Vector3()
            fullBBox.getCenter(fullCenter)

            // Oblicz bounding box TYLKO dla metalu (group_24 + kopie)
            const metalChildren: THREE.Object3D[] = []
            elementGroup.children.forEach((child: THREE.Object3D) => {
              const childName = child.name || ''
              if (childName === metalGroup || childName.startsWith(`${metalGroup}_`)) {
                metalChildren.push(child)
              }
            })

            if (metalChildren.length > 0) {
              const metalBBox = new THREE.Box3()
              metalChildren.forEach(child => {
                metalBBox.expandByObject(child)
              })
              const metalCenter = new THREE.Vector3()
              metalBBox.getCenter(metalCenter)

              // 🎯 Dla Slim 3D: wyrównaj środek metalu do środka sekcji.
              // 🎯 Dla Royal S50/P50 oraz Muro P22/S22/P38: wyrównaj LEWĄ krawędź metalu do LEWEJ krawędzi sekcji,
              //                  żeby nie było luzu po lewej stronie.
              let deltaX = 0
              let deltaZ = 0
              if (isSlim3d) {
                // 🎯 FIX: Dla Slim 3D gate/door - wyrównaj tylko X, NIE przesuwaj w osi Z
                deltaX = fullCenter.x - metalCenter.x
                deltaZ = 0 // Nie przesuwaj w osi Z dla gate w Slim 3D
              } else if (isRoyalS50 || isRoyalP50 || isMuroP22 || isMuroS22 || isMuroP38) {
                const fullMin = fullBBox.min
                const metalMin = metalBBox.min
                deltaX = fullMin.x - metalMin.x
                // Z wyrównujemy nadal po środku, żeby metal siedział w tej samej linii Z
                deltaZ = fullCenter.z - metalCenter.z
              }

              metalChildren.forEach(child => {
                child.position.x += deltaX
                child.position.z += deltaZ
              })

              let fenceType = ''
              if (isSlim3d) fenceType = 'Slim-3D'
              else if (isRoyalS50) fenceType = 'Royal-S50'
              else if (isRoyalP50) fenceType = 'Royal-P50'
              else if (isMuroP22) fenceType = 'Muro-P22'
              else if (isMuroS22) fenceType = 'Muro-S22'
              else if (isMuroP38) fenceType = 'Muro-P38'
              console.log(
                `🎯 ${fenceType} gate/door: wyrównano metal ${metalGroup} do środka sekcji (deltaX=${deltaX.toFixed(
                  3
                )}, deltaZ=${deltaZ.toFixed(3)})`
              )
            }
          } catch (e) {
            let fenceType = ''
            if (isSlim3d) fenceType = 'Slim-3D'
            else if (isRoyalS50) fenceType = 'Royal-S50'
            else if (isRoyalP50) fenceType = 'Royal-P50'
            else if (isMuroP22) fenceType = 'Muro-P22'
            else if (isMuroS22) fenceType = 'Muro-S22'
            else if (isMuroP38) fenceType = 'Muro-P38'
            console.warn(`⚠️ ${fenceType} gate/door: nie udało się wyrównać metalu do środka sekcji:`, e)
          }
        }
        
        // Funkcja do ukrywania bloków - użyj traverse żeby przejść przez wszystkie elementy
        elementGroup.traverse((obj: THREE.Object3D) => {
          // Pomiń sam elementGroup
          if (obj === elementGroup) return
          
          const objName = obj.name || ''
          
          // Najpierw sprawdź, czy to metal - jeśli tak, zostaw widoczny i nie ukrywaj
          if (metalGroup && (objName === metalGroup || objName.startsWith(`${metalGroup}_`))) {
            obj.visible = true
            let fenceType = ''
            if (isVisionBetLong) fenceType = 'Vision-Bet-Long'
            else if (isVisionBetMax) fenceType = 'Vision-Bet-Max'
            else if (isVisionBetMedium || isGladkieMedium) fenceType = 'Vision-Bet-Medium/Gladkie-Medium'
            else if (isGladkieMax) fenceType = 'Gladkie-Max'
            else if (isGladkieLong) fenceType = 'Gladkie-Long'
            else if (isSlim3d) fenceType = 'Slim-3D'
            else if (isSlim) fenceType = 'Slim'
            else if (isRoyalS50) fenceType = 'Royal-S50'
            else if (isRoyalP50) fenceType = 'Royal-P50'
            else if (isMuroP22) fenceType = 'Muro-P22'
            else if (isMuroS22) fenceType = 'Muro-S22'
            else if (isMuroP38) fenceType = 'Muro-P38'
            DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType}: pozostawiono widoczny ${objName} (metal)`)
          } else {
            // Ukryj zwykłe bloki i bloki dekoracyjne (tylko jeśli to nie metal)
            const isNormalBlock = normalBlockGroups.some(group => 
              objName === group || objName.startsWith(`${group}_copy_`) || objName.startsWith(`${group}_`)
            )
            const isDecorationBlock = decorationBlockGroups.some(group => 
              objName === group || objName.startsWith(`${group}_copy_`) || objName.startsWith(`${group}_`)
            )
            
            if (isNormalBlock || isDecorationBlock) {
              obj.visible = false
              let fenceType = ''
              if (isVisionBetLong) fenceType = 'Vision-Bet-Long'
              else if (isVisionBetMax) fenceType = 'Vision-Bet-Max'
              else if (isVisionBetMedium) fenceType = 'Vision-Bet-Medium'
              else if (isGladkieMax) fenceType = 'Gladkie-Max'
              else if (isGladkieLong) fenceType = 'Gladkie-Long'
              else if (isSlim3d) fenceType = 'Slim-3D'
              else if (isSlim) fenceType = 'Slim'
              else if (isRoyalS50) fenceType = 'Royal-S50'
              else if (isRoyalP50) fenceType = 'Royal-P50'
              else if (isMuroP22) fenceType = 'Muro-P22'
              else if (isMuroS22) fenceType = 'Muro-S22'
              DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType}: ukryto ${objName}`)
            }
          }
        })
        
        const normalGroups = normalBlockGroups.join(', ')
        const decorationGroups = decorationBlockGroups.join(', ')
        let fenceType = ''
        if (isVisionBetLong) fenceType = 'Vision-Bet-Long'
        else if (isVisionBetMax) fenceType = 'Vision-Bet-Max'
        else if (isVisionBetMedium || isGladkieMedium) fenceType = 'Vision-Bet-Medium/Gladkie-Medium'
        else if (isGladkieMax) fenceType = 'Gladkie-Max'
        else if (isGladkieLong) fenceType = 'Gladkie-Long'
        else if (isSlim3d) fenceType = 'Slim-3D'
        else if (isSlim) fenceType = 'Slim'
        else if (isRoyalS50) fenceType = 'Royal-S50'
        else if (isRoyalP50) fenceType = 'Royal-P50'
        else if (isMuroP22) fenceType = 'Muro-P22'
        else if (isMuroS22) fenceType = 'Muro-S22'
        DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType}: ukryto zwykłe bloki (${normalGroups}) i bloki dekoracyjne (${decorationGroups}), pozostawiono tylko metal (${metalGroup})`)
      }
      
      // 🎯 Zapisz prawą krawędź (dla calculateNextElementPosition) - OBLICZENIE WSTĘPNE
      // Zostanie zaktualizowane po dziedziczeniu rotacji jeśli potrzeba
      // Dla sekcji: dimensions.width = spacing * 2, więc rightEdge = position.x + spacing
      // Ale rightEdge powinno być bez dodatkowego spacing!
      // PROBLEM: rightEdge = position.x + spacing, ale potem w calculateNextElementPosition
      // nowy element jest umieszczany w rightEdge + (elementWidth / 2) = rightEdge + spacing
      // = position.x + spacing + spacing = position.x + 2*spacing!
      // ROZWIĄZANIE: rightEdge powinno być position.x + (dimensions.width / 2) - spacing
      // ALE TYLKO DLA SEKCJI! Dla słupków zostaw normalne rightEdge
      // 🚪 Dla vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d gate/door: traktuj jak section
      // ⚠️ Dla gate/door w gladkie_long: NIE przeliczaj rightEdge tutaj, bo już zostało przeliczone wcześniej (linie 994-1018)
      let rightEdgeX = position.x + (dimensions.width / 2)
      const isGateDoorAlreadyCalculated = (elementType === 'gate' || elementType === 'door') && isVisionBetGateDoor
      if ((elementType === 'section' || isVisionBetGateDoor) && !isGateDoorAlreadyCalculated) {
        // 🎯 Dla Muro P22 gate/door TYLKO: specjalna logika - użyj prostego rightEdge bez kompensacji
        // ⚠️ NIE modyfikować logiki dla section - tylko dla gate/door
        // (ma defaultWidth=0.148, spacing=0.394, co powoduje problemy z kompensacją)
        if (fenceConfig.id === 'lupane-muro-p22' && isMuroP22GateDoor) {
          // 🎯 Dla Muro P22 gate/door: specjalna logika - dla widthMultiplier >= 2 odejmij część spacing od rightEdge
          // Odstęp rośnie liniowo: 3x ma odstęp, 4x ma 2x większy, 5x ma 3x większy
          if (widthMultiplier >= 2) {
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier
            let reduction = 0
            if (widthMultiplier === 2) {
              reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
            } else if (widthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (widthMultiplier === 4) {
              reduction = spacing * 1.0 // 0.074 dla 4x
            } else if (widthMultiplier === 5) {
              reduction = spacing * 2.0 // 0.148 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 ${elementType} ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 ${elementType} ${widthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 ${elementType}): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'lupane-muro-s22' && isMuroS22GateDoor) {
          // 🎯 Dla Muro S22 gate/door: specjalna logika - dla widthMultiplier >= 2 odejmij część spacing od rightEdge
          // (takie same kompensacje jak dla section w Muro S22)
          if (widthMultiplier >= 2) {
            const spacing = elementConfig.spacing || 0.086
            // Różne kompensacje dla różnych widthMultiplier (takie same jak dla section w Muro S22)
            let reduction = 0
            if (widthMultiplier === 2) {
              reduction = spacing * -0.9 // -0.077 dla 2x (dodaje zamiast odejmować)
            } else if (widthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (widthMultiplier === 4) {
              reduction = spacing * 0.9 // 0.077 dla 4x
            } else if (widthMultiplier === 5) {
              reduction = spacing * 1.8 // 0.155 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22 ${elementType} ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22 ${elementType} ${widthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22 ${elementType}): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'lupane-muro-p38' && isMuroP38GateDoor) {
          // 🎯 Dla Muro P38 gate/door: specjalna logika - dla widthMultiplier >= 2 odejmij część spacing od rightEdge
          // (takie same kompensacje jak dla section w Muro P38)
          if (widthMultiplier >= 2) {
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier (takie same jak dla section w Muro P38)
            let reduction = 0
            if (widthMultiplier === 2) {
              reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
            } else if (widthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (widthMultiplier === 4) {
              reduction = spacing * 1.0 // 0.074 dla 4x
            } else if (widthMultiplier === 5) {
              reduction = spacing * 2.0 // 0.148 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38 ${elementType} ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38 ${elementType} ${widthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38 ${elementType}): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'lupane-muro-p22' && elementType === 'section') {
          // 🎯 Dla Muro P22 section: specjalna logika - dla widthMultiplier >= 2 odejmij część spacing od rightEdge
          // Odstęp rośnie liniowo: 3x ma odstęp, 4x ma 2x większy, 5x ma 3x większy
          if (widthMultiplier >= 2) {
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier
            let reduction = 0
            if (widthMultiplier === 2) {
              reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
            } else if (widthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (widthMultiplier === 4) {
              reduction = spacing * 1.0 // 0.074 dla 4x
            } else if (widthMultiplier === 5) {
              reduction = spacing * 2.0 // 0.148 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 section ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 section ${widthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 section): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 section): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'lupane-muro-p38') {
          // 🎯 Dla Muro P38 section: specjalna logika - dla widthMultiplier >= 2 odejmij część spacing od rightEdge
          // Odstęp rośnie liniowo: 3x ma odstęp, 4x ma 2x większy, 5x ma 3x większy
          if (widthMultiplier >= 2) {
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier
            let reduction = 0
            if (widthMultiplier === 2) {
              reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
            } else if (widthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (widthMultiplier === 4) {
              reduction = spacing * 1.0 // 0.074 dla 4x
            } else if (widthMultiplier === 5) {
              reduction = spacing * 2.0 // 0.148 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38 ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38 ${widthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'lupane-muro-s22') {
          // 🎯 Dla Muro S22: specjalna logika - dla widthMultiplier >= 2 odejmij część spacing od rightEdge
          // Odstęp rośnie liniowo: 3x ma odstęp, 4x ma 2x większy, 5x ma 3x większy
          if (widthMultiplier >= 2) {
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier
            let reduction = 0
            if (widthMultiplier === 2) {
              reduction = spacing * -0.9 // -0.067 dla 2x (dodaje zamiast odejmować)
            } else if (widthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (widthMultiplier === 4) {
              reduction = spacing * 0.9 // 0.067 dla 4x
            } else if (widthMultiplier === 5) {
              reduction = spacing * 1.8 // 0.133 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22 ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22 ${widthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'lupane-muro-s38') {
          // 🎯 Dla Muro S38: specjalna logika - dla widthMultiplier >= 2 odejmij część spacing od rightEdge
          // Odstęp rośnie liniowo: 3x ma odstęp, 4x ma 2x większy, 5x ma 3x większy
          if (widthMultiplier >= 2) {
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier
            let reduction = 0
            if (widthMultiplier === 2) {
              reduction = spacing * -0.9 // -0.067 dla 2x (dodaje zamiast odejmować)
            } else if (widthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (widthMultiplier === 4) {
              reduction = spacing * 0.9 // 0.067 dla 4x
            } else if (widthMultiplier === 5) {
              reduction = spacing * 1.8 // 0.133 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S38 ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S38 ${widthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S38): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S38): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'royal_s50') {
          // 🎯 Dla Royal S50: specjalna logika - dla widthMultiplier >= 3 odejmij część spacing od rightEdge
          // Odstęp rośnie liniowo: 3x ma odstęp, 4x ma 2x większy, 5x ma 3x większy
          if (widthMultiplier >= 3) {
            const spacing = elementConfig.spacing || 0.198
            // Wzór: (widthMultiplier - 2) * spacing * 0.5
            // Dla 3x: (3-2) * spacing * 0.5 = 0.5 * spacing = 0.099
            // Dla 4x: (4-2) * spacing * 0.5 = 1.0 * spacing = 0.198
            // Dla 5x: (5-2) * spacing * 0.5 = 1.5 * spacing = 0.297
            const reduction = (widthMultiplier - 2) * spacing * 0.5
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Royal S50 ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Royal S50 ${widthMultiplier}x): odjęto (${widthMultiplier}-2)*spacing*0.5=${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x i 2x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Royal S50): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Royal S50): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else if (fenceConfig.id === 'royal_p50') {
          // 🎯 Dla Royal P50: specjalna logika - dla widthMultiplier >= 3 odejmij część spacing od rightEdge
          // Odstęp rośnie liniowo: 3x ma odstęp, 4x ma 2x większy, 5x ma 3x większy
          if (widthMultiplier >= 3) {
            const spacing = elementConfig.spacing || 0.198
            // Wzór: (widthMultiplier - 2) * spacing * 0.5
            // Dla 3x: (3-2) * spacing * 0.5 = 0.5 * spacing = 0.099
            // Dla 4x: (4-2) * spacing * 0.5 = 1.0 * spacing = 0.198
            // Dla 5x: (5-2) * spacing * 0.5 = 1.5 * spacing = 0.297
            const reduction = (widthMultiplier - 2) * spacing * 0.5
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Royal P50 ${widthMultiplier}x): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Royal P50 ${widthMultiplier}x): odjęto (${widthMultiplier}-2)*spacing*0.5=${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x i 2x: użyj prostego rightEdge bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Royal P50): widthMultiplier=${widthMultiplier}, dimensions.width=${dimensions.width.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Royal P50): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
        } else {
          // Standardowa logika dla innych ogrodzeń (włączając vision-bet-long, vision-bet-max, vision-bet-medium, gladkie-max, gladkie-long i slim-3d gate/door)
          rightEdgeX = position.x + (dimensions.width / 2) - (elementConfig.spacing || 0)
          
          // 🎯 ODEJMIJ część rightOffset od rightEdge dla WSZYSTKICH widthMultiplier
          const rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, widthMultiplier)
          // 🎯 Pobierz coefficient z konfiguracji
          const coefficient = this.getCoefficientForWidthMultiplier(elementConfig, widthMultiplier)
          
          const rightOffsetReduction = rightOffset * coefficient
          rightEdgeX -= rightOffsetReduction
          DEBUG_LOGS && console.log(`🔍 DEBUG coefficient: widthMultiplier=${widthMultiplier}, rightOffset=${rightOffset.toFixed(3)}, coefficient=${coefficient}, reduction=${rightOffsetReduction.toFixed(3)}`)
          DEBUG_LOGS && console.log(`🎯 rightEdge: odjęto ${coefficient} * rightOffset ${rightOffsetReduction.toFixed(3)}m dla widthMultiplier ${widthMultiplier}`)
          if (isVisionBetGateDoor) {
            let fenceType = ''
            if (fenceConfig.id === 'vision-bet-long') fenceType = 'Vision-Bet-Long'
            else if (fenceConfig.id === 'vision-bet-max') fenceType = 'Vision-Bet-Max'
            else if (fenceConfig.id === 'vision-bet-medium') fenceType = 'Vision-Bet-Medium'
            else if (fenceConfig.id === 'gladkie_max') fenceType = 'Gladkie-Max'
            else if (fenceConfig.id === 'gladkie_long' || fenceConfig.id === 'slim3d-kopia') fenceType = 'Gladkie-Long'
            else if (fenceConfig.id === 'slim-3d') fenceType = 'Slim-3D'
            DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType} rightEdge: ${rightEdgeX.toFixed(3)}m (jak section)`)
          }
        }
      } else if ((elementType === 'gate' || elementType === 'door') && !isVisionBetGateDoor) {
        // 🚪 Dla gate/door (nie vision-bet-long/max/medium/gladkie-max/gladkie-long/slim-3d): rightEdge to środek + połowa szerokości
        // Gate/door to elementy metalowe bezpośrednio obok siebie, bez odstępów jak w section
        // dimensions.width już zawiera rzeczywistą szerokość wszystkich elementów (widthFromDAE * widthMultiplier)
        rightEdgeX = position.x + (dimensions.width / 2)
        
        // 🚪 Dla gate/door (nie vision-bet-long/max/medium/gladkie-max/gladkie-long/slim-3d): NIE odejmuj kompensacji, bo gate/door jest pozycjonowane na podstawie rightEdge poprzedniego elementu
        // rightEdge poprzedniego elementu (post/section) ma kompensację (jest przesunięte w lewo)
        // calculateNextElementPosition używa tego rightEdge jako contactPoint i dodaje halfWidth, co ustawia lewą krawędź gate/door w contactPoint
        // Więc gate/door powinno mieć rightEdge bez kompensacji, żeby się nie nachodziło
        DEBUG_LOGS && console.log(`🚪 ${elementType} rightEdge: ${rightEdgeX.toFixed(3)}m (dimensions.width=${dimensions.width.toFixed(3)}m, fenceConfig=${fenceConfig.id})`)
      }
      
      // ⚠️ Dla gate/door w gladkie_long: NIE nadpisuj rightEdge, bo już zostało poprawnie obliczone wcześniej (linie 994-1018)
      let rightEdge: THREE.Vector3
      DEBUG_LOGS && console.log(`🔍 DEBUG ${elementType}: isGateDoorAlreadyCalculated=${isGateDoorAlreadyCalculated}, existingRightEdge=${elementGroup.userData.rightEdge ? 'OK' : 'NULL'}`)
      if (!isGateDoorAlreadyCalculated) {
        // 🎯 Dla royal_s50 i royal_p50 section: obróć rightEdge tak samo jak dla gate/door
        if ((fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50') && elementType === 'section') {
          const absoluteRotation = elementGroup.userData?.absoluteRotation || 0
          const absoluteRotationRad = (absoluteRotation * Math.PI) / 180
          // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją)
          const rightEdgeOffsetX = rightEdgeX - position.x
          const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
          // ⚠️ Użyj position (bez przesunięcia) zamiast elementGroup.position (z przesunięciem dla Royal Mix P50)
          rightEdge = new THREE.Vector3(position.x, position.y, position.z).add(rightEdgeOffset)
          const royalType = fenceConfig.id === 'royal_s50' ? 'S50' : 'P50'
          DEBUG_LOGS && console.log(`🔍 DEBUG (Royal ${royalType} section): rightEdge obrócony o ${absoluteRotation}°, X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)}`)
        } else if ((fenceConfig.id === 'lupane-muro-p22' || fenceConfig.id === 'lupane-muro-p38' || fenceConfig.id === 'lupane-muro-s22' || fenceConfig.id === 'lupane-muro-s38') && elementType === 'section') {
          // 🎯 Dla Muro sections: obróć rightEdge tak samo jak dla Royal S50/P50
          const absoluteRotation = elementGroup.userData?.absoluteRotation || 0
          const absoluteRotationRad = (absoluteRotation * Math.PI) / 180
          // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją)
          const rightEdgeOffsetX = rightEdgeX - position.x
          const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
          // ⚠️ Użyj position (bez przesunięcia) zamiast elementGroup.position
          rightEdge = new THREE.Vector3(position.x, position.y, position.z).add(rightEdgeOffset)
          
          // 🎯 Dla P22 section 4x: zwiększ odstęp po prawej
          if (fenceConfig.id === 'lupane-muro-p22' && widthMultiplier === 4) {
            const spacing = elementConfig.spacing || 0.074
            const additionalOffset = spacing * 1.0 // 1.0x spacing = ~0.074m
            // Utwórz wektor przesunięcia w kierunku rotacji (w prawo)
            const additionalOffsetVector = new THREE.Vector3(additionalOffset, 0, 0)
            additionalOffsetVector.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            rightEdge.add(additionalOffsetVector)
            DEBUG_LOGS && console.log(`🎯 Muro P22 section 4x: dodano odstęp po prawej: ${additionalOffset.toFixed(3)}m (1.0x spacing)`)
          }
          
          // 🎯 Dla P22 section 5x: zwiększ odstęp po prawej
          if (fenceConfig.id === 'lupane-muro-p22' && widthMultiplier === 5) {
            const spacing = elementConfig.spacing || 0.074
            const additionalOffset = spacing * 2.0 // 2.0x spacing = ~0.148m
            // Utwórz wektor przesunięcia w kierunku rotacji (w prawo)
            const additionalOffsetVector = new THREE.Vector3(additionalOffset, 0, 0)
            additionalOffsetVector.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            rightEdge.add(additionalOffsetVector)
            DEBUG_LOGS && console.log(`🎯 Muro P22 section 5x: dodano odstęp po prawej: ${additionalOffset.toFixed(3)}m (2.0x spacing)`)
          }
          
          const muroType = fenceConfig.id === 'lupane-muro-p22' ? 'P22' : fenceConfig.id === 'lupane-muro-p38' ? 'P38' : fenceConfig.id === 'lupane-muro-s22' ? 'S22' : 'S38'
          console.log(`🔍 DEBUG (Muro ${muroType} section): rightEdge obrócony o ${absoluteRotation}°, rightEdgeX=${rightEdgeX.toFixed(3)}, position.x=${position.x.toFixed(3)}, rightEdgeOffsetX=${rightEdgeOffsetX.toFixed(3)}, rightEdge X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)}`)
          DEBUG_LOGS && console.log(`🔍 DEBUG (Muro ${muroType} section): rightEdge obrócony o ${absoluteRotation}°, X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)}`)
        } else {
          // 🎯 Dla postów P22/P38/S22/S38: rightEdge powinien używać position.z (przed przesunięciem postOffsetZ)
          // postOffsetZ jest przesunięciem wizualnym, więc nie powinno wpływać na rightEdge
          // Dla sekcji: rightEdge już używa position.z (linia 2113), więc jest poprawne
          // ⚠️ WAŻNE: rightEdge używa position.z (przed przesunięciem), więc jest poprawne dla niezrotowanych postów
          rightEdge = new THREE.Vector3(rightEdgeX, position.y, position.z)
          if (elementType === 'post' && (fenceConfig.id === 'lupane-muro-p22' || fenceConfig.id === 'lupane-muro-p38' || fenceConfig.id === 'lupane-muro-s22' || fenceConfig.id === 'lupane-muro-s38')) {
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro ${fenceConfig.id} post): rightEdge używa position.z=${position.z.toFixed(6)} (przed przesunięciem postOffsetZ=${(fenceConfig.elements?.post as any)?.postOffsetZ || 0})`)
          }
        }
        elementGroup.userData.rightEdge = rightEdge
        DEBUG_LOGS && console.log(`🔍 DEBUG ${elementType}: utworzono nowe rightEdge: X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)}`)
      } else {
        // rightEdge już zostało obliczone wcześniej dla gate/door w gladkie_long, użyj istniejącego
        const existingRightEdge = elementGroup.userData.rightEdge
        if (existingRightEdge) {
          rightEdge = existingRightEdge as THREE.Vector3
          DEBUG_LOGS && console.log(`✅ rightEdge dla ${elementType} w ${fenceConfig.id} już zostało obliczone wcześniej: X=${existingRightEdge.x.toFixed(3)}, Z=${existingRightEdge.z.toFixed(3)}`)
        } else {
          // Fallback na wypadek problemu - ale to oznacza, że coś poszło nie tak w liniach 994-1018!
          console.error(`❌ BŁĄD dla ${elementType}: isGateDoorAlreadyCalculated=true, ale rightEdge nie zostało obliczone! Używam fallbacka.`)
          rightEdge = new THREE.Vector3(rightEdgeX, position.y, position.z)
          elementGroup.userData.rightEdge = rightEdge
        }
      }
      
      // 🎯 Dla Muro P22: zapisz originalRightEdge (przed rotacją) żeby móc go obracać później
      if (elementType === 'section' && fenceConfig.id === 'lupane-muro-p22') {
        elementGroup.userData.originalRightEdge = rightEdge.clone()
        DEBUG_LOGS && console.log(`💾 Zapisano originalRightEdge dla Muro P22: X=${rightEdgeX.toFixed(3)}, Z=${position.z.toFixed(3)}`)
      }
      
      // 🎯 Dla Muro P38: zapisz originalRightEdge (przed rotacją) żeby móc go obracać później
      if (elementType === 'section' && fenceConfig.id === 'lupane-muro-p38') {
        elementGroup.userData.originalRightEdge = rightEdge.clone()
        DEBUG_LOGS && console.log(`💾 Zapisano originalRightEdge dla Muro P38: X=${rightEdgeX.toFixed(3)}, Z=${position.z.toFixed(3)}`)
      }
      
      // 🎯 Dla Muro S22: zapisz originalRightEdge (przed rotacją) żeby móc go obracać później
      if (elementType === 'section' && fenceConfig.id === 'lupane-muro-s22') {
        elementGroup.userData.originalRightEdge = rightEdge.clone()
        DEBUG_LOGS && console.log(`💾 Zapisano originalRightEdge dla Muro S22: X=${rightEdgeX.toFixed(3)}, Z=${position.z.toFixed(3)}`)
      }
      
      // 🎯 Dla Muro S38: zapisz originalRightEdge (przed rotacją) żeby móc go obracać później
      if (elementType === 'section' && fenceConfig.id === 'lupane-muro-s38') {
        elementGroup.userData.originalRightEdge = rightEdge.clone()
        DEBUG_LOGS && console.log(`💾 Zapisano originalRightEdge dla Muro S38: X=${rightEdgeX.toFixed(3)}, Z=${position.z.toFixed(3)}`)
      }
      
      DEBUG_LOGS && console.log(`💾 Zapisano rightEdge (PRAWA krawędź): X=${rightEdgeX.toFixed(3)}, Z=${position.z.toFixed(3)}`)

      // Dodaj do sceny i mapy
      scene.add(elementGroup)
      
      // 🎯 FIX: Dla gate i door w S38 (zarówno zwykły S38, jak i Muro Mix S38) - przesuń wszystkie dzieci w dół, aby skompensować przesunięcie Y grup 3D
      // ⚠️ WAŻNE: To musi być PO dodaniu do sceny, aby bounding box działał poprawnie
      if ((elementType === 'gate' || elementType === 'door') && (fenceConfig.id === 'lupane-muro-s38' || isMuroMixS38)) {
        // Przesuń wszystkie dzieci w dół o stałą wartość (0.25m), aby skompensować przesunięcie Y w geometrii
        const offsetY = -0.25
        elementGroup.traverse((child: THREE.Object3D) => {
          if (child !== elementGroup && child.position) {
            child.position.y += offsetY
          }
        })
        console.log(`🎯 Muro-S38 ${elementType}: przesunięto wszystkie dzieci w dół o ${offsetY.toFixed(3)}m`)
      }
      this.elements.set(elementId, elementGroup)

      if (elementType === 'section') {
        if (fenceConfig.id === 'gladkie_max') {
          this.adjustGladkieMaxSectionDecoration(elementGroup, fenceConfig)
        }

        // 🎯 Sekcje wielokolumnowe (dekoracja powinna być w każdej kolumnie, nie tylko w pierwszej)
        const isMultiColumnSectionFamily =
          fenceConfig.id === 'slim-3d' ||
          fenceConfig.id === 'gladkie_long' ||
          fenceConfig.id === 'gladkie_max' ||
          fenceConfig.id === 'gladkie_medium' ||
          fenceConfig.id === 'vision-bet-long' ||
          fenceConfig.id === 'vision-bet-max' ||
          fenceConfig.id === 'vision-bet-medium'

        if (isMultiColumnSectionFamily && widthMultiplier > 1) {
          const sectionGroups = fenceConfig.elements?.section?.groups || []
          const primaryGroup = sectionGroups[0]
          const decorationGroupName = fenceConfig.elements?.section?.decorationGroup
          if (primaryGroup && decorationGroupName) {
            this.ensureSectionDecorationsForColumns(
              elementGroup,
              fenceConfig,
              [primaryGroup],
              decorationGroupName,
              preparedModel
            )
          }
        }
      }

      return { dimensions }
    } catch (error) {
      console.error('Błąd dodawania elementu:', error)
      return null
    } finally {
      this.pendingElements.delete(elementId)
    }
  }

  /**
   * Usuwa element ze sceny
   */
  removeFenceElement(elementId: string, scene: THREE.Scene): boolean {
    const element = this.elements.get(elementId)
    if (!element) return false

    scene.remove(element)
    this.elements.delete(elementId)
    return true
  }

  /**
   * Pobiera element po ID
   */
  getElement(elementId: string): THREE.Group | undefined {
    return this.elements.get(elementId)
  }

  /**
   * Pobiera wszystkie elementy
   */
  getAllElements(): Map<string, THREE.Group> {
    return this.elements
  }

  /**
   * Czyści wszystkie elementy
   */
  clearAll(scene: THREE.Scene): void {
    this.elements.forEach(element => {
      scene.remove(element)
    })
    this.elements.clear()
    this.pendingElements.clear()
  }

  /**
   * Aktualizuje pozycję elementu
   */
  updateElementPosition(elementId: string, position: Position3D): boolean {
    const element = this.elements.get(elementId)
    if (!element) return false

    let finalPositionY = position.y
    
    // 🚪 Dla gate i door w royal_s50, royal_p50, muro p22 i muro s22: obniż do poziomu ziemi (Y=0)
    const elementType = element.userData?.originalElementType || element.userData?.elementType
    const fenceConfigId = element.userData?.fenceConfig?.id
    if ((elementType === 'gate' || elementType === 'door') && (fenceConfigId === 'royal_s50' || fenceConfigId === 'royal_p50' || fenceConfigId === 'lupane-muro-p22' || fenceConfigId === 'lupane-muro-s22' || fenceConfigId === 'lupane-muro-p38')) {
      finalPositionY = 0
      let fenceType = ''
      if (fenceConfigId === 'royal_s50') fenceType = 'Royal-S50'
      else if (fenceConfigId === 'royal_p50') fenceType = 'Royal-P50'
      else if (fenceConfigId === 'lupane-muro-p22') fenceType = 'Muro-P22'
      else if (fenceConfigId === 'lupane-muro-s22') fenceType = 'Muro-S22'
      else if (fenceConfigId === 'lupane-muro-p38') fenceType = 'Muro-P38'
      DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType}: obniżono do poziomu ziemi (Y=0, było ${position.y.toFixed(3)})`)
    }

    element.position.set(position.x, finalPositionY, position.z)
    return true
  }

  /**
   * Aktualizuje wysokość słupka/przęsła
   */
  async updatePostHeight(
    elementId: string,
    newHeight: number,
    fenceConfig: FenceConfig,
    preparedModel: THREE.Scene,
    color?: string
  ): Promise<boolean> {
    const elementGroup = this.elements.get(elementId)
    if (!elementGroup || (elementGroup.name !== 'post_group' && elementGroup.name !== 'section_group')) {
      return false
    }

    const isSection = elementGroup.name === 'section_group'
    const availableGroups = isSection
      ? (fenceConfig.elements?.section?.groups || ['group_0'])
      : (fenceConfig.elements?.post?.groups || ['group_0'])
    const elementConfig = isSection ? fenceConfig.elements?.section : fenceConfig.elements?.post
    let decorationGroupName = elementConfig?.decorationGroup || availableGroups[1]
    if (!isSection && fenceConfig.id === 'lupane-muro-s22') {
      decorationGroupName = elementConfig?.decorationGroup || 'group_63'
    }
    const metalGroupName = elementConfig?.metalGroup

    const baseGroupNames = availableGroups.filter(groupName => (
      groupName &&
      groupName !== decorationGroupName &&
      groupName !== metalGroupName
    ))

    const isBaseBlock = (childName: string): boolean => {
      return baseGroupNames.some(groupName =>
        childName === groupName || childName.startsWith(`${groupName}_copy_`)
      )
    }

    const widthMultiplier = elementGroup.userData?.widthMultiplier || 1
    const currentBlocks = elementGroup.children.filter(child => isBaseBlock(child.name)).length

    // 🎯 Dla sections: jeśli mamy zapisaną wysokość w blokach (heightInBlocks), użyj jej zamiast liczyć z currentBlocks
    // to uwzględnia dekorację która już jest w początkowym elemencie
    let currentHeightInBlocks: number | undefined
    if (isSection && elementGroup.userData?.heightInBlocks !== undefined) {
      currentHeightInBlocks = elementGroup.userData.heightInBlocks
      DEBUG_LOGS && console.log(`🎯 Section ${elementId}: Używam zapisanej wysokości w blokach: ${currentHeightInBlocks} (z userData.heightInBlocks)`)
    }

    // 🎯 Specjalne traktowanie dla Muro S22 post (dwie kolumny w jednej strukturze)
    if (!isSection && fenceConfig.id === 'lupane-muro-s22') {
      const decorationGroupName = elementConfig?.decorationGroup || 'group_41'
      
      // 🎯 Zbuduj listę WSZYSTKICH możliwych nazw dekoracji
      const allDecorationNames = new Set<string>()
      allDecorationNames.add('group_41')
      allDecorationNames.add('group_63')
      if (decorationGroupName) {
        allDecorationNames.add(decorationGroupName)
      }
      const postTextures = elementConfig?.textures
      if (postTextures) {
        Object.keys(postTextures).forEach(textureGroupName => {
          if (!baseGroupNames.includes(textureGroupName)) {
            allDecorationNames.add(textureGroupName)
          }
        })
      }
      
      const decorationNamesArray = Array.from(allDecorationNames)
      DEBUG_LOGS && console.log(`🔍 S22: Lista wszystkich możliwych nazw dekoracji:`, decorationNamesArray)
      
      const isDecorationName = (childName: string): boolean => {
        if (!childName) return false
        return decorationNamesArray.some(decName => {
          return childName === decName || childName.startsWith(`${decName}_copy_`)
        })
      }
      
      const blocksPerLayer = Math.max(1, baseGroupNames.length || 1)
      // 🎯 FIX: Użyj userData.height gdy dostępne (po pierwszej zmianie),
      // a przy pierwszym wywołaniu dodaj decorationBlocksCount do currentBlocks
      let currentLayers: number
      if (elementGroup.userData?.height !== undefined) {
        currentLayers = elementGroup.userData.height
      } else {
        const postDecorationBlocksCount = elementConfig?.decorationBlocksCount ?? 1
        currentLayers = Math.round(currentBlocks / blocksPerLayer) + postDecorationBlocksCount
      }
      const layersDiff = newHeight - currentLayers

      DEBUG_LOGS && console.log(`🔧 updatePostHeight (Muro S22 post): blocksPerLayer=${blocksPerLayer}, currentBlocks=${currentBlocks}, currentLayers=${currentLayers}, newHeight=${newHeight}, layersDiff=${layersDiff}`)

      // 🎯 KROK 1: Znajdź i ZACHOWAJ główną dekorację (bezpośrednie dziecko elementGroup)
      let primaryDecorationBefore = elementGroup.children.find(child => {
        const childName = child.name || ''
        return isDecorationName(childName) && child.parent === elementGroup
      }) as THREE.Object3D | undefined
      
      // Zapisz offset głównej dekoracji, jeśli istnieje
      let savedOffsets: { x: number; z: number } | undefined
      if (primaryDecorationBefore) {
        const baseBlocksBefore = elementGroup.children.filter(child => isBaseBlock(child.name))
        if (baseBlocksBefore.length > 0) {
          baseBlocksBefore.sort((a, b) => a.position.y - b.position.y)
          const highestBlockBefore = baseBlocksBefore[baseBlocksBefore.length - 1]
          const topPairBlocksBefore = baseBlocksBefore.filter(block => Math.abs(block.position.y - highestBlockBefore.position.y) < 0.001)
          if (topPairBlocksBefore.length > 0) {
            const avgXBefore = topPairBlocksBefore.reduce((sum, block) => sum + block.position.x, 0) / topPairBlocksBefore.length
            const avgZBefore = topPairBlocksBefore.reduce((sum, block) => sum + block.position.z, 0) / topPairBlocksBefore.length
            savedOffsets = primaryDecorationBefore.userData.muroS22Offsets as { x: number; z: number } | undefined
            if (!savedOffsets) {
              savedOffsets = {
                x: primaryDecorationBefore.position.x - avgXBefore,
                z: primaryDecorationBefore.position.z - avgZBefore
              }
            }
          }
        }
        DEBUG_LOGS && console.log(`✅ S22 PRZED: Znaleziono główną dekorację ${primaryDecorationBefore.name}, zapisano offsets:`, savedOffsets)
      }

      // 🎯 KROK 2: Usuń TYLKO zagnieżdżone dekoracje z bloków bazowych (NIE główną dekorację!)
      const nestedDecorationsBefore: THREE.Object3D[] = []
      // Znajdź wszystkie bloki bazowe
      const baseBlocksList = elementGroup.children.filter(child => isBaseBlock(child.name))
      
      // Przeszukaj tylko bloki bazowe, nie całe elementGroup (żeby nie znaleźć głównej dekoracji)
      baseBlocksList.forEach(baseBlock => {
        baseBlock.traverse(obj => {
          if (obj === baseBlock) return // Pomiń sam blok
          if (isDecorationName(obj.name || '')) {
            // To jest zagnieżdżona dekoracja w bloku bazowym
            nestedDecorationsBefore.push(obj)
          }
        })
      })
      
      DEBUG_LOGS && console.log(`🧹 S22 PRZED: Znaleziono ${nestedDecorationsBefore.length} zagnieżdżonych dekoracji w blokach bazowych do usunięcia`)
      nestedDecorationsBefore.forEach(dec => {
        if (dec.parent) {
          dec.parent.remove(dec)
          DEBUG_LOGS && console.log(`🧹 S22 PRZED: Usunięto zagnieżdżoną dekorację ${dec.name || 'unnamed'} z bloku bazowego ${dec.parent.name || 'unknown'}`)
        }
      })

      // 🎯 KROK 3: Wykonaj modyfikację bloków
      if (layersDiff > 0) {
        this.addBlocksToPost(elementGroup, layersDiff, elementId, fenceConfig, preparedModel)
      } else if (layersDiff < 0) {
        const blocksToRemove = Math.abs(layersDiff) * blocksPerLayer
        this.removeBlocksFromPost(elementGroup, blocksToRemove, fenceConfig)
      }

      // 🎯 KROK 4: PO modyfikacji - usuń WSZYSTKIE zagnieżdżone dekoracje z bloków bazowych (NIE z głównej dekoracji)
      const nestedDecorationsAfter: THREE.Object3D[] = []
      // Znajdź wszystkie bloki bazowe PO modyfikacji
      const baseBlocksListAfter = elementGroup.children.filter(child => isBaseBlock(child.name))
      
      // Przeszukaj tylko bloki bazowe, nie całe elementGroup (żeby nie znaleźć głównej dekoracji)
      baseBlocksListAfter.forEach(baseBlock => {
        baseBlock.traverse(obj => {
          if (obj === baseBlock) return // Pomiń sam blok
          if (isDecorationName(obj.name || '')) {
            // To jest zagnieżdżona dekoracja w bloku bazowym
            nestedDecorationsAfter.push(obj)
          }
        })
      })
      
      DEBUG_LOGS && console.log(`🧹 S22 PO MODYFIKACJI: Znaleziono ${nestedDecorationsAfter.length} zagnieżdżonych dekoracji w blokach bazowych do usunięcia`)
      nestedDecorationsAfter.forEach(dec => {
        if (dec.parent) {
          dec.parent.remove(dec)
          DEBUG_LOGS && console.log(`🧹 S22 PO MODYFIKACJI: Usunięto zagnieżdżoną dekorację ${dec.name || 'unnamed'} z bloku bazowego ${dec.parent.name || 'unknown'}`)
        }
      })

      // 🎯 KROK 5: Sprawdź czy główna dekoracja nadal istnieje (może zostać usunięta przez removeBlocksFromPost)
      let primaryDecoration = elementGroup.children.find(child => {
        const childName = child.name || ''
        return isDecorationName(childName) && child.parent === elementGroup
      }) as THREE.Object3D | undefined

      // 🎯 KROK 6: Jeśli główna dekoracja nie istnieje, ale była przed modyfikacją, przywróć ją z modelu
      if (!primaryDecoration) {
        if (primaryDecorationBefore) {
          // Główna dekoracja została usunięta - przywróć ją z modelu
          const sketchUpGroup = preparedModel.children.find((child: any) => child.name === 'SketchUp')
          if (sketchUpGroup) {
            const decorGroup = this.findGroupRecursively(sketchUpGroup, decorationGroupName)
            if (decorGroup) {
              primaryDecoration = decorGroup.clone(true)
              primaryDecoration.name = decorationGroupName
              // Usuń zagnieżdżone dekoracje z nowo dodanej dekoracji
              this.removeNestedDecorations(primaryDecoration, decorationNamesArray)
              // Przywróć zapisane offsets
              if (savedOffsets) {
                primaryDecoration.userData.muroS22Offsets = savedOffsets
              }
              elementGroup.add(primaryDecoration)
              DEBUG_LOGS && console.log(`✅ S22: Przywrócono główną dekorację ${decorationGroupName} z modelu (offsets:`, savedOffsets, ')')
            } else {
              console.warn(`⚠️ S22: Nie znaleziono dekoracji ${decorationGroupName} w modelu`)
            }
          }
        } else {
          // Nie było głównej dekoracji przed modyfikacją - dodaj ją z modelu
          const sketchUpGroup = preparedModel.children.find((child: any) => child.name === 'SketchUp')
          if (sketchUpGroup) {
            const decorGroup = this.findGroupRecursively(sketchUpGroup, decorationGroupName)
            if (decorGroup) {
              primaryDecoration = decorGroup.clone(true)
              primaryDecoration.name = decorationGroupName
              this.removeNestedDecorations(primaryDecoration, decorationNamesArray)
              elementGroup.add(primaryDecoration)
              DEBUG_LOGS && console.log(`✅ S22: Dodano główną dekorację ${decorationGroupName} z modelu`)
            } else {
              console.warn(`⚠️ S22: Nie znaleziono dekoracji ${decorationGroupName} w modelu`)
            }
          }
        }
      } else {
        // Główna dekoracja istnieje - upewnij się, że nazwa jest poprawna i usuń zagnieżdżone dekoracje
        primaryDecoration.name = decorationGroupName
        this.removeNestedDecorations(primaryDecoration, decorationNamesArray)
        // Przywróć zapisane offsets, jeśli były
        if (savedOffsets) {
          primaryDecoration.userData.muroS22Offsets = savedOffsets
        }
        DEBUG_LOGS && console.log(`✅ S22: Użyto istniejącą główną dekorację ${primaryDecoration.name} (offsets:`, primaryDecoration.userData.muroS22Offsets, ')')
      }

      // 🎯 KROK 7: Pozycjonuj główną dekorację na górze
      if (primaryDecoration) {
        const baseBlocks = elementGroup.children.filter(child => isBaseBlock(child.name))
        if (baseBlocks.length > 0) {
          baseBlocks.sort((a, b) => a.position.y - b.position.y)
          const highestBlock = baseBlocks[baseBlocks.length - 1]
          const topPairBlocks = baseBlocks.filter(block => Math.abs(block.position.y - highestBlock.position.y) < 0.001)
          
          if (topPairBlocks.length > 0) {
            const avgX = topPairBlocks.reduce((sum, block) => sum + block.position.x, 0) / topPairBlocks.length
            const avgZ = topPairBlocks.reduce((sum, block) => sum + block.position.z, 0) / topPairBlocks.length

            // Użyj zapisanych offsets lub oblicz nowe
            const existingOffsets = primaryDecoration.userData.muroS22Offsets as { x: number; z: number } | undefined
            const offsets = existingOffsets ?? savedOffsets ?? {
              x: primaryDecoration.position.x - avgX,
              z: primaryDecoration.position.z - avgZ
            }

            primaryDecoration.userData.muroS22Offsets = offsets
            primaryDecoration.position.x = avgX + offsets.x
            primaryDecoration.position.z = avgZ + offsets.z

            DEBUG_LOGS && console.log(`🎯 S22: Blok dekoracyjny ${decorationGroupName} wyrównany: avgX=${avgX.toFixed(3)}, avgZ=${avgZ.toFixed(3)}, offsetX=${offsets.x.toFixed(3)}, offsetZ=${offsets.z.toFixed(3)}`)
          }

          const blockSpacing = fenceConfig.elements?.post?.blockSpacing ?? 0.1
          const decorationSpacing = elementConfig?.decorationSpacing ?? fenceConfig.elements?.post?.decorationSpacing ?? 0.0
          primaryDecoration.position.y = highestBlock.position.y + blockSpacing + decorationSpacing
          primaryDecoration.updateMatrixWorld(true)

          DEBUG_LOGS && console.log(`🎯 S22: Blok dekoracyjny ${decorationGroupName} ustawiony na Y=${primaryDecoration.position.y.toFixed(3)} (najwyższy blok Y=${highestBlock.position.y.toFixed(3)})`)
        }
      }

      // 🎯 KROK 8: FINALNA WERYFIKACJA - upewnij się, że jest TYLKO jedna główna dekoracja (bezpośrednie dziecko elementGroup)
      const finalMainDecorations = elementGroup.children.filter(child => {
        const childName = child.name || ''
        return isDecorationName(childName) && child.parent === elementGroup
      })
      
      if (finalMainDecorations.length !== 1) {
        console.warn(`⚠️ S22: FINALNA WERYFIKACJA - Znaleziono ${finalMainDecorations.length} głównych dekoracji (powinna być 1)`)
        // Usuń wszystkie oprócz primaryDecoration
        finalMainDecorations.forEach(dec => {
          if (dec !== primaryDecoration) {
            elementGroup.remove(dec)
            DEBUG_LOGS && console.log(`🗑️ S22 FINALNA: Usunięto duplikat głównej dekoracji ${dec.name || 'unnamed'}`)
          }
        })
      }
      
      // Usuń WSZYSTKIE zagnieżdżone dekoracje z bloków bazowych (które mogły pozostać)
      const finalNestedDecorations: THREE.Object3D[] = []
      // Przeszukaj tylko bloki bazowe, nie całe elementGroup
      const finalBaseBlocks = elementGroup.children.filter(child => isBaseBlock(child.name))
      finalBaseBlocks.forEach(baseBlock => {
        baseBlock.traverse(obj => {
          if (obj === baseBlock) return // Pomiń sam blok
          if (isDecorationName(obj.name || '')) {
            // To jest zagnieżdżona dekoracja w bloku bazowym
            finalNestedDecorations.push(obj)
          }
        })
      })
      
      if (finalNestedDecorations.length > 0) {
        console.warn(`⚠️ S22: FINALNA WERYFIKACJA - Znaleziono ${finalNestedDecorations.length} zagnieżdżonych dekoracji do usunięcia`)
        finalNestedDecorations.forEach(dec => {
          if (dec.parent) {
            dec.parent.remove(dec)
            DEBUG_LOGS && console.log(`🗑️ S22 FINALNA: Usunięto zagnieżdżoną dekorację ${dec.name || 'unnamed'}`)
          }
        })
      }
      
      const finalCount = elementGroup.children.filter(child => {
        const childName = child.name || ''
        return isDecorationName(childName) && child.parent === elementGroup
      }).length
      DEBUG_LOGS && console.log(`✅ S22: FINALNA WERYFIKACJA - Pozostało ${finalCount} główna(ych) dekoracja(cji)`)

      elementGroup.userData.height = newHeight
      return true
    }

    // 🎯 Specjalne traktowanie dla Muro P22 section (wiele kolumn w jednym elemencie)
    if (isSection && fenceConfig.id === 'lupane-muro-p22') {
      const updated = this.updateMuroP22SectionHeight(
        elementGroup,
        newHeight,
        elementId,
        fenceConfig,
        preparedModel,
        baseGroupNames,
        decorationGroupName
      )
      if (updated) {
        elementGroup.userData.height = newHeight
        return true
      }
      // Jeśli specjalna logika zawiodła, kontynuuj standardową ścieżką jako awaryjną
    } else if (isSection && fenceConfig.id === 'lupane-muro-s22') {
      const updated = this.updateMuroS22SectionHeight(
        elementGroup,
        newHeight,
        elementId,
        fenceConfig,
        preparedModel,
        baseGroupNames,
        decorationGroupName
      )
      if (updated) {
        elementGroup.userData.height = newHeight
        return true
      }
    } else if (isSection && fenceConfig.id === 'lupane-muro-p38') {
      const updated = this.updateMuroP38SectionHeight(
        elementGroup,
        newHeight,
        elementId,
        fenceConfig,
        preparedModel,
        baseGroupNames,
        decorationGroupName
      )
      if (updated) {
        elementGroup.userData.height = newHeight
        return true
      }
    } else if (isSection && fenceConfig.id === 'lupane-muro-s38') {
      const updated = this.updateMuroS38SectionHeight(
        elementGroup,
        newHeight,
        elementId,
        fenceConfig,
        preparedModel,
        baseGroupNames,
        decorationGroupName
      )
      if (updated) {
        elementGroup.userData.height = newHeight
        return true
      }
    } else if (isSection && fenceConfig.id === 'royal_s50') {
      const updated = this.updateRoyalS50SectionHeight(
        elementGroup,
        newHeight,
        elementId,
        fenceConfig,
        preparedModel,
        baseGroupNames,
        decorationGroupName
      )
      if (updated) {
        elementGroup.userData.height = newHeight
        return true
      }
    } else if (isSection && fenceConfig.id === 'royal_p50') {
      const updated = this.updateRoyalP50SectionHeight(
        elementGroup,
        newHeight,
        elementId,
        fenceConfig,
        preparedModel,
        baseGroupNames,
        decorationGroupName
      )
      if (updated) {
        elementGroup.userData.height = newHeight
        return true
      }
    }

    // 🎯 Ustal ile bloczków tworzy jedną „warstwę” w zależności od typu ogrodzenia
    const isMultiColumnSectionFamily =
      isSection &&
      (fenceConfig.id === 'slim' ||
        fenceConfig.id === 'slim-3d' ||
        fenceConfig.id === 'gladkie_long' ||
        fenceConfig.id === 'gladkie_max' ||
        fenceConfig.id === 'vision-bet-long' ||
        fenceConfig.id === 'vision-bet-max' ||
        fenceConfig.id === 'vision-bet-medium' ||
        fenceConfig.id === 'gladkie_medium')

    const blocksPerLayerForHeight = isSection
      ? (isMultiColumnSectionFamily && widthMultiplier > 1
          ? Math.max(1, widthMultiplier * (baseGroupNames.length || 1))
          : Math.max(1, baseGroupNames.length || 1))
      : 1

    // 🎯 FIX: Dla postów i sekcji użyj userData.height gdy dostępne,
    // zamiast liczyć z currentBlocks (który nie uwzględnia bloków dekoracyjnych).
    // currentBlocks liczy TYLKO bloki bazowe (bez dekoracyjnych),
    // ale newHeight to TOTAL (bazowe + dekoracyjne), więc bezpośrednie porównanie
    // dawało błędną wartość layersToAdd.
    let currentLayers: number
    if (!isSection && elementGroup.userData?.height !== undefined) {
      // 🎯 Post z wcześniej zapisaną wysokością — użyj jej
      currentLayers = elementGroup.userData.height
    } else if (isSection && currentHeightInBlocks !== undefined) {
      // 🎯 Section z zapisaną wysokością w blokach
      currentLayers = currentHeightInBlocks
    } else if (isSection) {
      currentLayers = Math.round(currentBlocks / blocksPerLayerForHeight)
    } else {
      // 🎯 FIX: Post bez zapisanej wysokości (pierwszy raz) —
      // currentBlocks to TYLKO bloki bazowe, a newHeight to TOTAL (z dekoracją),
      // więc musimy dodać decorationBlocksCount do currentBlocks aby porównanie było poprawne.
      const postDecorationBlocksCount = elementConfig?.decorationBlocksCount ?? 1
      currentLayers = currentBlocks + postDecorationBlocksCount
      console.log(`🎯 Post ${elementId}: Pierwsze wywołanie - currentBlocks=${currentBlocks} + decorationBlocks=${postDecorationBlocksCount} = currentLayers=${currentLayers}`)
    }
    const layersToAdd = newHeight - currentLayers

    console.log(
      `🔧 updatePostHeight: currentBlocks=${currentBlocks}, currentLayers=${currentLayers}, widthMultiplier=${widthMultiplier}, blocksPerLayer=${blocksPerLayerForHeight}, newHeight=${newHeight}, layersToAdd=${layersToAdd}, fenceId=${fenceConfig.id}, isSection=${isSection}`
    )

    if (layersToAdd > 0) {
      const blocksToAdd = isSection ? layersToAdd * blocksPerLayerForHeight : layersToAdd
      const textures = await loadTexturesForFence(fenceConfig, color)
      this.addBlocksToPost(elementGroup, blocksToAdd, elementId, fenceConfig, preparedModel, textures)
    } else if (layersToAdd < 0) {
      const blocksToRemove = isSection ? Math.abs(layersToAdd) * blocksPerLayerForHeight : Math.abs(layersToAdd)
      this.removeBlocksFromPost(elementGroup, blocksToRemove, fenceConfig)
    }

    // 🎯 SPECJALNIE DLA SLIM 3D SEKCJI: PO ZMIANIE WYSOKOŚCI UTRZYMAJ DEKORACJĘ W TEJ SAMEJ KOLUMNIE (X/Z)
    if (isSection && fenceConfig.id === 'slim-3d') {
      const sectionConfig = fenceConfig.elements?.section
      const decorationGroupName = sectionConfig?.decorationGroup
      if (decorationGroupName) {
        // 🎯 DLA SLIM 3D UŻYJ TEJ SAMEJ LOGIKI CO DLA WIELOKOLUMNOWYCH SEKCJI (PO JEDNEJ DEKORACJI NA KOLUMNĘ)
        this.ensureSectionDecorationsForColumns(
          elementGroup,
          fenceConfig,
          baseGroupNames,
          decorationGroupName,
          preparedModel
        )
      }
    }

    // Zapisz zaktualizowaną wysokość (liczba warstw) w userData
    elementGroup.userData.height = newHeight
    // 🎯 Dla sections: zapisz też wysokość w blokach
    if (isSection) {
      elementGroup.userData.heightInBlocks = newHeight
    }

    return true
  }

  /**
   * Pomocnicza: Pobiera elementy do dodania na podstawie typu
   */
  /**
   * Rekurencyjnie wyszukuje grupę w drzewie obiektów
   */
  private findGroupRecursively(
    parent: THREE.Object3D,
    groupName: string
  ): THREE.Object3D | null {
    // Sprawdź bezpośrednie dzieci
    const directChild = parent.children.find((child: any) => child.name === groupName)
    if (directChild) return directChild

    // Przeszukaj rekurencyjnie zagnieżdżone grupy
    for (const child of parent.children) {
      const found = this.findGroupRecursively(child, groupName)
      if (found) return found
    }

    return null
  }

  /**
   * Znajduje wszystkie wystąpienia grupy w modelu (dla debugowania)
   */
  private findAllGroupsRecursively(
    parent: THREE.Object3D,
    groupName: string,
    path: string = ''
  ): Array<{ element: THREE.Object3D; path: string }> {
    const results: Array<{ element: THREE.Object3D; path: string }> = []
    const currentPath = path ? `${path} > ${parent.name || 'unnamed'}` : (parent.name || 'root')

    // Sprawdź bezpośrednie dzieci
    for (const child of parent.children) {
      if (child.name === groupName) {
        results.push({ element: child, path: currentPath })
      }
    }

    // Przeszukaj rekurencyjnie zagnieżdżone grupy
    for (const child of parent.children) {
      const nestedResults = this.findAllGroupsRecursively(child, groupName, currentPath)
      results.push(...nestedResults)
    }

    return results
  }

  /**
   * Znajduje grupę w kontekście innej grupy (np. group_56 w kontekście group_21 dla P38)
   */
  private findGroupInContext(
    sketchUpGroup: THREE.Object3D,
    targetGroupName: string,
    contextGroupName: string
  ): THREE.Object3D | null {
    // Najpierw znajdź grupę kontekstową
    const contextGroup = this.findGroupRecursively(sketchUpGroup, contextGroupName)
    if (!contextGroup) {
      console.warn(`⚠️ Nie znaleziono grupy kontekstowej ${contextGroupName}`)
      return null
    }

    // Szukaj targetGroupName w kontekście contextGroup (jako dziecko lub w zagnieżdżonych grupach)
    return this.findGroupRecursively(contextGroup, targetGroupName)
  }

  private getElementsToAdd(
    model: THREE.Scene,
    elementType: string,
    config: any,
    fenceConfig: FenceConfig,
    postHeight?: number // 🎯 Wysokość słupka w blokach (z suwaka w ElementToolbar)
  ): Array<{ element: THREE.Object3D; name: string; type: string }> {
    const elementsToAdd: Array<{ element: THREE.Object3D; name: string; type: string }> = []
    const sketchUpGroup = model.children.find((child: any) => child.name === 'SketchUp')

    if (!sketchUpGroup) return elementsToAdd

    if (elementType === 'post') {
      // 🎯 DEBUG dla S22
      if (fenceConfig.id === 'lupane-muro-s22') {
        DEBUG_LOGS && console.log(`🔍 S22 DEBUG: config.decorationGroup = ${config.decorationGroup}`)
        DEBUG_LOGS && console.log(`🔍 S22 DEBUG: config.groups =`, config.groups)
        DEBUG_LOGS && console.log(`🔍 S22 DEBUG: Wszystkie dostępne grupy w modelu DAE:`, sketchUpGroup.children.map((c: any) => c.name).filter((n: string) => n.startsWith('group_')))
        DEBUG_LOGS && console.log(`🔍 S22 DEBUG: Szukam grup dekoracyjnych dla S22 (group_42, group_56, group_43, group_54, group_63, group_66)...`)
        // Sprawdź różne możliwe grupy dekoracyjne (rekurencyjnie, bo mogą być zagnieżdżone)
        const possibleDecoGroups = ['group_42', 'group_56', 'group_43', 'group_54', 'group_63', 'group_66', 'group_57']
        possibleDecoGroups.forEach(grp => {
          const found = this.findGroupRecursively(sketchUpGroup, grp)
          DEBUG_LOGS && console.log(`🔍 S22 DEBUG: ${grp}: ${found ? 'ZNALEZIONO (rekurencyjnie)' : 'BRAK'}`)
          // Jeśli znaleziono group_57, sprawdź jego dzieci
          if (grp === 'group_57' && found) {
            DEBUG_LOGS && console.log(`🔍 S22 DEBUG: group_57 dzieci:`, (found as any).children.map((c: any) => c.name))
          }
        })
      }
      
      // Użyj rekurencyjnego wyszukiwania dla baseBlock (może być zagnieżdżony)
      const baseBlock = this.findGroupRecursively(sketchUpGroup, config.groups[0])
      // 🎯 KLUCZOWE: Użyj postHeight z suwaka zamiast defaultHeight z konfiguracji dla słupków
      const defaultHeight = postHeight !== undefined ? postHeight : (config.defaultHeight || 6)
      DEBUG_LOGS && console.log(`🎯 getElementsToAdd POST: używam wysokości ${defaultHeight} bloków (z suwaka: ${postHeight}, z konfiguracji: ${config.defaultHeight || 6})`)

      if (baseBlock) {
        for (let i = 0; i < defaultHeight - 1; i++) {
          elementsToAdd.push({ element: baseBlock, name: `${config.groups[0]}_copy_${i}`, type: 'base' })
        }
      }

      // 🎯 Używaj decorationGroup z configu, a jeśli nie ma, to groups[1]
      let decorationGroupName = config.decorationGroup || config.groups[1]
      if (fenceConfig.id === 'lupane-muro-s22' && decorationGroupName) {
        // Sprawdź czy decorationGroup z config istnieje w modelu (rekurencyjnie, bo może być zagnieżdżony)
        const decorGroup = this.findGroupRecursively(sketchUpGroup, decorationGroupName)
        if (decorGroup) {
          DEBUG_LOGS && console.log(`🔧 S22: Używam ${decorationGroupName} jako decorationGroup (z config, znaleziony rekurencyjnie)`)
        } else {
          console.warn(`⚠️ S22: ${decorationGroupName} nie istnieje w modelu (nawet rekurencyjnie), używam z config: ${decorationGroupName}`)
        }
      }
      
      // 🎯 Dla P38 i S38: znajdź właściwą grupę decorationGroup w kontekście pierwszej grupy (group_19 dla post, group_21 dla section)
      let p38DecorGroup: THREE.Object3D | null = null
      if ((fenceConfig.id === 'lupane-muro-p38' || fenceConfig.id === 'lupane-muro-s38') && decorationGroupName) {
        const baseGroupName = config.groups[0] // group_19 dla post, group_21 dla section
        const baseGroup = this.findGroupRecursively(sketchUpGroup, baseGroupName)
        const sectionConfig = fenceConfig.elements?.section
        const sectionGroups = sectionConfig?.groups || []
        
        DEBUG_LOGS && console.log(`🔍 P38/S38 DEBUG: Szukam ${decorationGroupName} w kontekście ${baseGroupName} (elementType=${elementType})`)
        DEBUG_LOGS && console.log(`🔍 P38/S38 DEBUG: Znaleziono ${baseGroupName}:`, !!baseGroup)
        
        if (baseGroup) {
          // 1. Sprawdź, czy decorationGroup jest bezpośrednim dzieckiem baseGroup
          const baseGroupChildren = (baseGroup as any).children || []
          DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Liczba dzieci ${baseGroupName}:`, baseGroupChildren.length)
          DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Dzieci ${baseGroupName} (z nazwami):`, baseGroupChildren.filter((c: any) => c.name).map((c: any) => c.name))
          DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Wszystkie dzieci ${baseGroupName} (typy):`, baseGroupChildren.map((c: any) => c.type || c.constructor.name))
          const directChild = baseGroupChildren.find((c: any) => c.name === decorationGroupName)
          if (directChild) {
            p38DecorGroup = directChild
            DEBUG_LOGS && console.log(`✅ P38 DEBUG: Znaleziono ${decorationGroupName} jako bezpośrednie dziecko ${baseGroupName}`)
          }
          
          // 2. Jeśli nie, sprawdź czy decorationGroup jest zagnieżdżony w strukturze baseGroup
          if (!p38DecorGroup) {
            const findInBaseGroup = (obj: THREE.Object3D, depth: number = 0): THREE.Object3D | null => {
              if (depth > 10) return null
              if (obj.name === decorationGroupName) return obj
              for (const child of obj.children) {
                const found = findInBaseGroup(child, depth + 1)
                if (found) return found
              }
              return null
            }
            p38DecorGroup = findInBaseGroup(baseGroup) || this.findGroupInContext(sketchUpGroup, decorationGroupName, config.groups[0])
            if (p38DecorGroup) {
              DEBUG_LOGS && console.log(`✅ P38 DEBUG: Znaleziono ${decorationGroupName} zagnieżdżony w ${baseGroupName}`)
            }
          }
          
          // 3. Jeśli nie, sprawdź czy decorationGroup jest rodzeństwem baseGroup (ten sam rodzic)
          if (!p38DecorGroup && baseGroup.parent) {
            const siblings = (baseGroup.parent as any).children || []
            DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Rodzeństwo ${baseGroupName}:`, siblings.map((c: any) => c.name))
            const siblingDecorationGroup = siblings.find((c: any) => c.name === decorationGroupName)
            if (siblingDecorationGroup) {
              p38DecorGroup = siblingDecorationGroup
              DEBUG_LOGS && console.log(`✅ P38 DEBUG: Znaleziono ${decorationGroupName} jako rodzeństwo ${baseGroupName}`)
            }
            
            // 3b. Jeśli nie znaleziono, sprawdź czy decorationGroup jest dzieckiem grupy będącej rodzeństwem baseGroup
            // (np. group_58 jest dzieckiem group_57, a group_57 jest rodzeństwem baseGroup)
            if (!p38DecorGroup) {
              DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Szukam ${decorationGroupName} w dzieciach rodzeństwa ${baseGroupName}...`)
              for (const sibling of siblings) {
                if (sibling.name && sibling.name !== config.groups[0]) {
                  const findInSibling = (obj: THREE.Object3D, depth: number = 0): THREE.Object3D | null => {
                    if (depth > 5) return null
                    if (obj.name === decorationGroupName) return obj
                    for (const child of obj.children) {
                      const found = findInSibling(child, depth + 1)
                      if (found) return found
                    }
                    return null
                  }
                  const foundInSibling = findInSibling(sibling)
                  if (foundInSibling) {
                    p38DecorGroup = foundInSibling
                    DEBUG_LOGS && console.log(`✅ P38 DEBUG: Znaleziono ${decorationGroupName} w ${sibling.name} (rodzeństwo ${baseGroupName})`)
                    break
                  }
                }
              }
            }
          }
        }
        
        // 4. Jeśli nadal nie znaleziono, użyj standardowego wyszukiwania, preferując tę bez grup sekcji
        if (!p38DecorGroup) {
          DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Szukam ${decorationGroupName} we wszystkich grupach...`)
          const allGroup56 = this.findAllGroupsRecursively(sketchUpGroup, decorationGroupName)
          DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Znaleziono ${allGroup56.length} wystąpień ${decorationGroupName}`)
          if (allGroup56.length > 0) {
            // Preferuj decorationGroup, które jest rodzeństwem baseGroup, ale nie ma grup sekcji w rodzeństwie
            for (const result of allGroup56) {
              const parent = result.element.parent
              if (parent && baseGroup) {
                const siblings = (parent as any).children || []
                const hasBaseGroup = siblings.some((s: any) => s.name === config.groups[0])
                const hasSectionGroups = sectionGroups.some((sg: string) => 
                  siblings.some((s: any) => s.name === sg)
                )
                DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Sprawdzam wystąpienie ${decorationGroupName}, hasBaseGroup=${hasBaseGroup}, hasSectionGroups=${hasSectionGroups}`)
                if (hasBaseGroup && !hasSectionGroups) {
                  p38DecorGroup = result.element
                  DEBUG_LOGS && console.log(`✅ P38 DEBUG: Użyto preferowanego ${decorationGroupName}`)
                  break
                }
              }
            }
            // Jeśli nie znaleziono preferowanej, użyj pierwszego znalezionego
            if (!p38DecorGroup) {
              p38DecorGroup = allGroup56[0].element
              DEBUG_LOGS && console.log(`⚠️ P38 DEBUG: Użyto pierwszego znalezionego ${decorationGroupName}`)
            }
          } else {
            p38DecorGroup = this.findGroupRecursively(sketchUpGroup, decorationGroupName)
            DEBUG_LOGS && console.log(`⚠️ P38 DEBUG: Standardowe wyszukiwanie ${decorationGroupName}:`, !!p38DecorGroup)
          }
        }
        
        if (p38DecorGroup) {
          DEBUG_LOGS && console.log(`✅ P38 DEBUG: FINALNA grupa dekoracyjna: ${p38DecorGroup.name}`)
        } else {
          console.warn(`❌ P38 DEBUG: NIE ZNALEZIONO grupy dekoracyjnej ${decorationGroupName}!`)
        }
      }
      
      if (defaultHeight > 1 && decorationGroupName) {
        // Dla P38 i S38 użyj znalezionej wcześniej grupy, dla innych użyj standardowego wyszukiwania
        let decorGroup: THREE.Object3D | null = null
        DEBUG_LOGS && console.log(`🔍 P38/S38 DEBUG SECTION: fenceId=${fenceConfig.id}, elementType=${elementType}, defaultHeight=${defaultHeight}, decorationGroupName=${decorationGroupName}, p38DecorGroup=${!!p38DecorGroup}`)
        if ((fenceConfig.id === 'lupane-muro-p38' || fenceConfig.id === 'lupane-muro-s38') && p38DecorGroup) {
          decorGroup = p38DecorGroup
          DEBUG_LOGS && console.log(`✅ P38 DEBUG SECTION: Używam p38DecorGroup dla ${elementType}`)
          
          // 🎯 Dla P38: Jeśli istnieje decorationSubgroup, użyj całego group_57 ale ukryj wszystko oprócz decorationSubgroup
          const decorationSubgroup = config.decorationSubgroup
          if (decorationSubgroup) {
            DEBUG_LOGS && console.log(`🔍 P38: Ukrywam wszystkie dzieci ${decorGroup.name} OPRÓCZ ${decorationSubgroup}`)
            
            // Funkcja do ukrycia wszystkich dzieci oprócz określonego
            const hideAllExcept = (parent: THREE.Object3D, keepName: string): boolean => {
              let foundTarget = false
              
              parent.traverse((child) => {
                if (child === parent) return // Pomiń samego parenta
                
                if (child.name === keepName) {
                  // To jest ten który chcemy zachować - pokaż go
                  child.visible = true
                  foundTarget = true
                  DEBUG_LOGS && console.log(`✅ P38: Zachowuję widoczność ${keepName}`)
                } else if (child.parent === parent || (child as any).isGroup) {
                  // Ukryj inne bezpośrednie dzieci lub grupy
                  // ALE nie ukrywaj dzieci z keepName
                  let isChildOfKeepName = false
                  let checkParent = child.parent
                  while (checkParent && checkParent !== parent) {
                    if (checkParent.name === keepName) {
                      isChildOfKeepName = true
                      break
                    }
                    checkParent = checkParent.parent
                  }
                  
                  if (!isChildOfKeepName && child.name !== keepName) {
                    child.visible = false
                  }
                }
              })
              
              return foundTarget
            }
            
            const found = hideAllExcept(decorGroup, decorationSubgroup)
            if (found) {
              DEBUG_LOGS && console.log(`✅ P38: Używam cały ${decorGroup.name}, ale widoczny tylko ${decorationSubgroup}`)
              
              // 🎯 Dla P38: Ręcznie przesuń decorGroup w dół (decorationSpacing z config)
              const postDecorationSpacing = config.decorationSpacing || 0
              if (postDecorationSpacing !== 0) {
                // Zapisz oryginalną pozycję Y przed modyfikacją
                const originalY = decorGroup.position.y
                // Przesuń cały group_57 w dół
                decorGroup.position.y += postDecorationSpacing
                DEBUG_LOGS && console.log(`✅ P38: Przesunięto ${decorGroup.name} w dół: Y=${originalY.toFixed(3)} → ${decorGroup.position.y.toFixed(3)} (decorationSpacing=${postDecorationSpacing})`)
              }
            } else {
              console.error(`❌ P38: Nie znaleziono ${decorationSubgroup} w ${decorGroup.name}!`)
            }
          }
        } else {
          decorGroup = this.findGroupRecursively(sketchUpGroup, decorationGroupName)
        }
        
        if (!decorGroup && fenceConfig.id === 'slim-3d' && decorationGroupName) {
          const allDecorGroups = this.findAllGroupsRecursively(sketchUpGroup, decorationGroupName)
          if (allDecorGroups.length > 0) {
            const preferredGroupEntry = allDecorGroups.find(candidate => {
              const size = new THREE.Vector3()
              new THREE.Box3().setFromObject(candidate.element).getSize(size)
              return size.x >= size.z
            }) || allDecorGroups[0]
            decorGroup = preferredGroupEntry.element
            DEBUG_LOGS && console.log(`✅ Slim 3D: Wybrano dekorację ${decorGroup.name} (kandydatów=${allDecorGroups.length}, ścieżka='${preferredGroupEntry.path}')`)
          }
        }

        if (decorGroup) {
          const childrenDetails = decorGroup.children.map((c: any) => ({ 
            name: c.name, 
            type: c.type, 
            hasGeometry: !!(c as any).geometry,
            visible: c.visible,
            childrenCount: c.children ? c.children.length : 0
          }))
          DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Struktura ${decorGroup.name}:`, {
            name: decorGroup.name,
            type: decorGroup.type,
            childrenCount: decorGroup.children.length,
            hasGeometry: !!(decorGroup as any).geometry,
            visible: decorGroup.visible
          })
          DEBUG_LOGS && console.log(`🔍 P38 DEBUG: Dzieci ${decorGroup.name}:`, childrenDetails)
          elementsToAdd.push({ element: decorGroup, name: decorGroup.name, type: 'decoration' })
          DEBUG_LOGS && console.log(`✅ getElementsToAdd (post): Dodano bloczek dekoracyjny ${decorGroup.name} (znaleziony rekurencyjnie)`)
        } else {
          console.warn(`⚠️ getElementsToAdd (post): Nie znaleziono bloku dekoracyjnego ${decorationGroupName} w modelu DAE (nawet rekurencyjnie)!`)
          console.warn(`   Dostępne grupy bezpośrednio w modelu:`, sketchUpGroup.children.map((c: any) => c.name))
        }
      }
    } else {
      // 🔍 DEBUG: Szczegółowe logi dla sekcji (szczególnie dla Muro Mix)
      if (elementType === 'section') {
        console.log(`🔍 DEBUG getElementsToAdd SECTION: elementType=${elementType}, fenceConfig.id=${fenceConfig.id}`)
        console.log(`🔍 DEBUG getElementsToAdd SECTION: config:`, config)
        console.log(`🔍 DEBUG getElementsToAdd SECTION: config.groups:`, config?.groups)
        console.log(`🔍 DEBUG getElementsToAdd SECTION: fenceConfig.elements?.section:`, fenceConfig.elements?.section)
        console.log(`🔍 DEBUG getElementsToAdd SECTION: fenceConfig.elements?.section?.groups:`, fenceConfig.elements?.section?.groups)
      }
      
      DEBUG_LOGS && console.log(`🔧 DEBUG: getElementsToAdd dla ${elementType}, config.groups:`, config.groups)
      DEBUG_LOGS && console.log(`🔧 DEBUG: sketchUpGroup.children nazwy (WSZYSTKIE dostępne grupy w modelu):`, sketchUpGroup.children.map((c: any) => c.name))
      DEBUG_LOGS && console.log(`🔧 DEBUG: decorationGroup z config:`, config.decorationGroup)
      
      // 🔍 DEBUG: Sprawdź, czy config.groups istnieje i nie jest pusty
      if (!config.groups || config.groups.length === 0) {
        console.error(`❌ DEBUG: getElementsToAdd dla ${elementType}: config.groups jest pusty lub undefined!`)
        console.error(`   config:`, config)
        console.error(`   fenceConfig.id:`, fenceConfig.id)
        console.error(`   fenceConfig.elements:`, fenceConfig.elements)
        console.error(`   fenceConfig.elements?.section:`, fenceConfig.elements?.section)
        return elementsToAdd
      }
      
      // 🎯 DEBUG: Loguj dla gate/door w Royal Mix
      if ((elementType === 'gate' || elementType === 'door') && (fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50')) {
        console.log(`🔍 DEBUG getElementsToAdd Royal Mix ${elementType}: fenceConfig.id=${fenceConfig.id}, elementType=${elementType}, config.groups=`, config.groups)
      }
      
      config.groups.forEach((groupName: string) => {
        // Użyj rekurencyjnego wyszukiwania (grupy mogą być zagnieżdżone)
        const foundGroup = this.findGroupRecursively(sketchUpGroup, groupName)
        // 🔍 DEBUG: Zawsze loguj dla sekcji (szczególnie dla Muro Mix)
        if (elementType === 'section') {
          console.log(`🔍 DEBUG getElementsToAdd: Szukam grupy ${groupName} dla ${elementType} (fenceConfig.id=${fenceConfig.id}), znaleziono:`, !!foundGroup)
          if (!foundGroup) {
            console.error(`❌ DEBUG: Nie znaleziono grupy ${groupName} w modelu DAE!`)
            console.error(`   fenceConfig.id:`, fenceConfig.id)
            console.error(`   elementType:`, elementType)
            console.error(`   Dostępne grupy bezpośrednio w modelu:`, sketchUpGroup.children.map((c: any) => c.name))
          } else {
            console.log(`✅ DEBUG: Znaleziono grupę ${groupName}, dodaję do elementsToAdd`)
          }
        }
        // 🎯 DEBUG: Loguj dla gate/door w Royal Mix
        if ((elementType === 'gate' || elementType === 'door') && (fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50')) {
          console.log(`🔍 DEBUG getElementsToAdd Royal Mix ${elementType}: Szukam grupy ${groupName}, znaleziono:`, !!foundGroup)
        }
        DEBUG_LOGS && console.log(`🔧 DEBUG: Szukam grupy ${groupName}, znaleziono (rekurencyjnie):`, !!foundGroup)
        if (foundGroup) {
          elementsToAdd.push({ element: foundGroup, name: groupName, type: elementType })
          DEBUG_LOGS && console.log(`✅ Dodano grupę ${groupName} do elementsToAdd (znaleziona rekurencyjnie)`)
        } else {
          console.error(`❌ DEBUG: Nie znaleziono grupy ${groupName} w modelu DAE (nawet rekurencyjnie)!`)
          console.error(`   Dostępne grupy bezpośrednio w modelu:`, sketchUpGroup.children.map((c: any) => c.name))
        }
      })
      
      // 🔍 DEBUG: Zawsze loguj końcowy wynik dla sekcji
      if (elementType === 'section') {
        console.log(`🔍 DEBUG getElementsToAdd FINAL: dla ${elementType} (fenceConfig.id=${fenceConfig.id}), elementsToAdd.length=${elementsToAdd.length}`)
        console.log(`   elementsToAdd:`, elementsToAdd.map(e => ({ name: e.name, type: e.type })))
      }
      
      // 🎯 TYLKO DLA S22, Royal S50, Royal P50, P38, S38 SECTION (oraz Royal S50/P50/Muro P22/Muro S22 gate/door traktowanych jak section):
      //     dodaj bloczek dekoracyjny jeśli istnieje decorationGroup
      const isRoyalS50SectionLike =
        fenceConfig.id === 'royal_s50' &&
        (elementType === 'section' || elementType === 'gate' || elementType === 'door')
      const isRoyalP50SectionLike =
        fenceConfig.id === 'royal_p50' &&
        (elementType === 'section' || elementType === 'gate' || elementType === 'door')
      const isMuroP22SectionLike =
        fenceConfig.id === 'lupane-muro-p22' &&
        (elementType === 'gate' || elementType === 'door')
      const isMuroS22SectionLike =
        fenceConfig.id === 'lupane-muro-s22' &&
        (elementType === 'gate' || elementType === 'door')
      const isMuroP38SectionLike =
        fenceConfig.id === 'lupane-muro-p38' &&
        (elementType === 'gate' || elementType === 'door')
      // ⚠️ WAŻNE: Dla muro p22 section NIE dodawaj dekoracji - tylko dla gate/door
      // Dla muro s22 section MA dekoracje (tak jak inne ogrodzenia)
      // Dla muro p38 section MA dekoracje (tak jak inne ogrodzenia)
      if (
        ((elementType === 'section' && fenceConfig.id !== 'lupane-muro-p22') || isRoyalS50SectionLike || isRoyalP50SectionLike || isMuroP22SectionLike || isMuroS22SectionLike || isMuroP38SectionLike) &&
        (fenceConfig.id === 'lupane-muro-s22' ||
          fenceConfig.id === 'royal_s50' ||
          fenceConfig.id === 'royal_p50' ||
          fenceConfig.id === 'lupane-muro-p38' ||
          fenceConfig.id === 'lupane-muro-s38' ||
          fenceConfig.id === 'slim-3d' ||
          (fenceConfig.id === 'lupane-muro-p22' && (elementType === 'gate' || elementType === 'door')) ||
          (fenceConfig.id === 'lupane-muro-s22' && (elementType === 'gate' || elementType === 'door')) ||
          (fenceConfig.id === 'lupane-muro-p38' && (elementType === 'gate' || elementType === 'door'))) &&
        config.decorationGroup
      ) {
        const decorationGroupName = config.decorationGroup
        let decorGroup: THREE.Object3D | null = null
        
        // 🎯 Dla P38 i S38 section: użyj tej samej logiki co dla post
        if (fenceConfig.id === 'lupane-muro-p38' || fenceConfig.id === 'lupane-muro-s38') {
          DEBUG_LOGS && console.log(`🔍 P38 SECTION: Szukam ${decorationGroupName} w kontekście ${config.groups[0]}`)
          
          const baseGroupName = config.groups[0] // group_21 dla section
          const baseGroup = this.findGroupRecursively(sketchUpGroup, baseGroupName)
          DEBUG_LOGS && console.log(`🔍 P38 SECTION: Znaleziono ${baseGroupName}:`, !!baseGroup)
          
          if (baseGroup) {
            // 1. Sprawdź, czy decorationGroup jest bezpośrednim dzieckiem baseGroup
            const baseGroupChildren = (baseGroup as any).children || []
            DEBUG_LOGS && console.log(`🔍 P38 SECTION: Dzieci ${baseGroupName}:`, baseGroupChildren.filter((c: any) => c.name).map((c: any) => c.name))
            const directChild = baseGroupChildren.find((c: any) => c.name === decorationGroupName)
            if (directChild) {
              decorGroup = directChild
              DEBUG_LOGS && console.log(`✅ P38 SECTION: Znaleziono ${decorationGroupName} jako bezpośrednie dziecko ${baseGroupName}`)
            }
            
            // 2. Jeśli nie, sprawdź czy decorationGroup jest rodzeństwem baseGroup
            if (!decorGroup && baseGroup.parent) {
              const siblings = (baseGroup.parent as any).children || []
              DEBUG_LOGS && console.log(`🔍 P38 SECTION: Rodzeństwo ${baseGroupName}:`, siblings.map((c: any) => c.name))
              const siblingDecorationGroup = siblings.find((c: any) => c.name === decorationGroupName)
              if (siblingDecorationGroup) {
                decorGroup = siblingDecorationGroup
                DEBUG_LOGS && console.log(`✅ P38 SECTION: Znaleziono ${decorationGroupName} jako rodzeństwo ${baseGroupName}`)
              }
              
              // 3. Jeśli nie znaleziono, sprawdź w dzieciach rodzeństwa
              if (!decorGroup) {
                for (const sibling of siblings) {
                  if (sibling.name && sibling.name !== config.groups[0]) {
                    const findInSibling = (obj: THREE.Object3D, depth: number = 0): THREE.Object3D | null => {
                      if (depth > 5) return null
                      if (obj.name === decorationGroupName) return obj
                      for (const child of obj.children) {
                        const found = findInSibling(child, depth + 1)
                        if (found) return found
                      }
                      return null
                    }
                    const foundInSibling = findInSibling(sibling)
                    if (foundInSibling) {
                      decorGroup = foundInSibling
                      DEBUG_LOGS && console.log(`✅ P38 SECTION: Znaleziono ${decorationGroupName} w ${sibling.name} (rodzeństwo ${baseGroupName})`)
                      break
                    }
                  }
                }
              }
            }
          }
          
          if (decorGroup) {
            DEBUG_LOGS && console.log(`✅ P38 SECTION: FINALNA grupa dekoracyjna: ${decorGroup.name}`)
            
            // 🎯 Dla P38: Jeśli istnieje decorationSubgroup, użyj całego group_57 ale ukryj wszystko oprócz decorationSubgroup
            const decorationSubgroup = config.decorationSubgroup
            if (decorationSubgroup) {
              DEBUG_LOGS && console.log(`🔍 P38 SECTION: Ukrywam wszystkie dzieci ${decorGroup.name} OPRÓCZ ${decorationSubgroup}`)
              
              // Funkcja do ukrycia wszystkich dzieci oprócz określonego
              const hideAllExcept = (parent: THREE.Object3D, keepName: string): boolean => {
                let foundTarget = false
                
                parent.traverse((child) => {
                  if (child === parent) return // Pomiń samego parenta
                  
                  if (child.name === keepName) {
                    child.visible = true
                    foundTarget = true
                    DEBUG_LOGS && console.log(`✅ P38 SECTION: Zachowuję widoczność ${keepName}`)
                  } else if (child.parent === parent || (child as any).isGroup) {
                    let isChildOfKeepName = false
                    let checkParent = child.parent
                    while (checkParent && checkParent !== parent) {
                      if (checkParent.name === keepName) {
                        isChildOfKeepName = true
                        break
                      }
                      checkParent = checkParent.parent
                    }
                    
                    if (!isChildOfKeepName && child.name !== keepName) {
                      child.visible = false
                    }
                  }
                })
                
                return foundTarget
              }
              
              const found = hideAllExcept(decorGroup, decorationSubgroup)
              if (found) {
                DEBUG_LOGS && console.log(`✅ P38 SECTION: Używam cały ${decorGroup.name}, ale widoczny tylko ${decorationSubgroup}`)
                
                // 🎯 Dla P38 SECTION: Ręcznie przesuń decorGroup w dół (decorationSpacing z config)
                const sectionDecorationSpacing = config.decorationSpacing || 0
                if (sectionDecorationSpacing !== 0) {
                  const originalY = decorGroup.position.y
                  decorGroup.position.y += sectionDecorationSpacing
                  DEBUG_LOGS && console.log(`✅ P38 SECTION: Przesunięto ${decorGroup.name} w dół: Y=${originalY.toFixed(3)} → ${decorGroup.position.y.toFixed(3)} (decorationSpacing=${sectionDecorationSpacing})`)
                }
              } else {
                console.error(`❌ P38 SECTION: Nie znaleziono ${decorationSubgroup} w ${decorGroup.name}!`)
              }
            }
          } else {
            console.warn(`❌ P38 SECTION: NIE ZNALEZIONO grupy dekoracyjnej ${decorationGroupName}!`)
          }
        } else {
          // Dla innych (S22, Royal S50, Royal P50) - standardowe wyszukiwanie
          decorGroup = this.findGroupRecursively(sketchUpGroup, decorationGroupName)
        }
        
        if (decorGroup) {
          elementsToAdd.push({ element: decorGroup, name: decorationGroupName, type: 'decoration' })
          DEBUG_LOGS && console.log(`✅ ${fenceConfig.id} section: Dodano bloczek dekoracyjny ${decorationGroupName} do elementsToAdd (znaleziony rekurencyjnie)`)
        } else {
          console.warn(`⚠️ ${fenceConfig.id} section: Nie znaleziono bloku dekoracyjnego ${decorationGroupName} w modelu DAE (nawet rekurencyjnie)!`)
        }
      }
      
      DEBUG_LOGS && console.log(`🔧 DEBUG: getElementsToAdd zwraca ${elementsToAdd.length} elementów:`, elementsToAdd.map(e => e.name))
    }

    return elementsToAdd
  }

  /**
   * Pomocnicza: Oblicza offset Y dla elementu
   */
  private calculateOffsetY(
    item: any,
    index: number,
    elementType: string,
    fenceConfig: FenceConfig
  ): number {
    // 🚪 Dla gate i door w gladkie_max, gladkie_medium, gladkie_long, slim-3d, slim, vision-bet-long, vision-bet-max, vision-bet-medium, royal_s50, royal_p50, muro p22 i muro s22: wszystkie bloki na poziomie ziemi (Y=0)
    // ⚠️ WAŻNE: Ten warunek MUSI być PRZED sprawdzeniem config, bo dla royal_s50, royal_p50, muro p22 i muro s22 gate/door nie mają własnej konfiguracji (używają section)
    if ((elementType === 'gate' || elementType === 'door') && (fenceConfig.id === 'gladkie_max' || fenceConfig.id === 'gladkie_medium' || fenceConfig.id === 'gladkie_long' || fenceConfig.id === 'slim-3d' || fenceConfig.id === 'slim' || fenceConfig.id === 'vision-bet-long' || fenceConfig.id === 'vision-bet-max' || fenceConfig.id === 'vision-bet-medium' || fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50' || fenceConfig.id === 'lupane-muro-p22' || fenceConfig.id === 'lupane-muro-s22' || fenceConfig.id === 'lupane-muro-p38')) {
      let fenceType = ''
      if (fenceConfig.id === 'gladkie_max') fenceType = 'Gladkie-Max'
      else if (fenceConfig.id === 'gladkie_long') fenceType = 'Gladkie-Long'
      else if (fenceConfig.id === 'slim-3d') fenceType = 'Slim-3D'
      else if (fenceConfig.id === 'slim') fenceType = 'Slim'
      else if (fenceConfig.id === 'vision-bet-long') fenceType = 'Vision-Bet-Long'
      else if (fenceConfig.id === 'vision-bet-max') fenceType = 'Vision-Bet-Max'
      else if (fenceConfig.id === 'vision-bet-medium') fenceType = 'Vision-Bet-Medium'
      else if (fenceConfig.id === 'gladkie_medium') fenceType = 'Gladkie-Medium'
      else if (fenceConfig.id === 'royal_s50') fenceType = 'Royal-S50'
      else if (fenceConfig.id === 'royal_p50') fenceType = 'Royal-P50'
      else if (fenceConfig.id === 'lupane-muro-p22') fenceType = 'Muro-P22'
      else if (fenceConfig.id === 'lupane-muro-p38') fenceType = 'Muro-P38'
      else if (fenceConfig.id === 'lupane-muro-s22') fenceType = 'Muro-S22'
      DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType}: bloczek ${item.name} na poziomie ziemi (Y=0)`)
      return 0
    }
    
    const config = fenceConfig.elements?.[elementType as 'post' | 'section' | 'gate' | 'door']
    if (!config) return 0

    // Debug: sprawdź wszystkie wartości
    DEBUG_LOGS && console.log(`🔧 calculateOffsetY DEBUG:`, {
      itemName: item.name,
      metalGroup: config.metalGroup,
      metalSpacing: config.metalSpacing,
      blockSpacing: config.blockSpacing,
      decorationSpacing: config.decorationSpacing,
      isMetalGroup: config.metalGroup && item.name === config.metalGroup
    })

    if (item.type === 'decoration') {
      const decorationSpacing = config.decorationSpacing || 0.15
      const blockSpacing = config.blockSpacing || 0.1
      return index * blockSpacing + decorationSpacing
    }

    // Sprawdź czy to element metalowy - użyj metalSpacing zamiast blockSpacing
    if (config.metalGroup && item.name === config.metalGroup) {
      const metalSpacing = config.metalSpacing || 0.09
      DEBUG_LOGS && console.log(`🔧 calculateOffsetY: Element metalowy ${item.name} na pozycji ${metalSpacing}`)
      return metalSpacing
    }

    // Debug: sprawdź czy element to metalowy na podstawie nazwy grupy
    if (item.name === 'group_4') {
      const metalSpacing = config.metalSpacing || 0.09
      DEBUG_LOGS && console.log(`🔧 calculateOffsetY: Element group_4 (metalowy) na pozycji ${metalSpacing}`)
      return metalSpacing
    }

    const blockSpacing = config.blockSpacing || 0.1
    return index * blockSpacing
  }

  /**
   * Pobiera rightOffset dla danego widthMultiplier z konfiguracji
   */
  private getRightOffsetForWidthMultiplier(elementConfig: any, widthMultiplier: number): number {
    // Sprawdź czy rightOffset jest obiektem z różnymi wartościami dla widthMultiplier
    if (elementConfig.rightOffset && typeof elementConfig.rightOffset === 'object') {
      const widthMultiplierKey = widthMultiplier.toString()
      return elementConfig.rightOffset[widthMultiplierKey] || elementConfig.rightOffset['1'] || 0.198
    }
    
    // Fallback: użyj pojedynczej wartości rightOffset
    return elementConfig.rightOffset || 0.198
  }

  /**
   * Pobiera metalRightOffset dla danego widthMultiplier z konfiguracji
   */
  public getMetalRightOffsetForWidthMultiplier(elementConfig: any, widthMultiplier: number): number {
    // Sprawdź czy metalRightOffset jest obiektem z różnymi wartościami dla widthMultiplier
    if (elementConfig.metalRightOffset && typeof elementConfig.metalRightOffset === 'object') {
      const widthMultiplierKey = widthMultiplier.toString()
      return elementConfig.metalRightOffset[widthMultiplierKey] || elementConfig.metalRightOffset['1'] || 0.198
    }
    
    // Fallback: użyj pojedynczej wartości metalRightOffset lub rightOffset
    return elementConfig.metalRightOffset || elementConfig.rightOffset || 0.198
  }

  /**
   * Pobiera coefficient dla danego widthMultiplier z konfiguracji
   */
  private getCoefficientForWidthMultiplier(elementConfig: any, widthMultiplier: number): number {
    // Sprawdź czy coefficient jest obiektem z różnymi wartościami dla widthMultiplier
    if (elementConfig.coefficient && typeof elementConfig.coefficient === 'object') {
      const widthMultiplierKey = widthMultiplier.toString()
      return elementConfig.coefficient[widthMultiplierKey] || elementConfig.coefficient['1'] || 0.5
    }
    
    // Fallback: użyj domyślnych wartości
    let coefficient = 0.5 // domyślnie połowa
    if (widthMultiplier >= 4) coefficient = 0.2 // 20% rightOffset
    if (widthMultiplier >= 5) coefficient = 0.15
    
    return coefficient
  }


  /**
   * Pomocnicza: Oblicza wymiary elementu z modelu 3D
   */
  private calculateDimensionsFromModel(model: THREE.Scene, modelScale: number): { width: number; height: number } | null {
    try {
      // Znajdź SketchUp group
      const sketchUpGroup = model.children.find((child: any) => child.name === 'SketchUp')
      if (!sketchUpGroup) return null

      // Sklonuj i zastosuj skalę
      const tempClone = sketchUpGroup.clone()
      tempClone.scale.setScalar(modelScale)
      tempClone.rotation.set(Math.PI / 2, Math.PI, 0)
      tempClone.updateMatrixWorld(true)

      // Oblicz bounding box
      const bbox = new THREE.Box3().setFromObject(tempClone)
      const size = new THREE.Vector3()
      bbox.getSize(size)

      // Width to rozmiar w osi X, height w osi Y
      return {
        width: Math.abs(size.x),
        height: Math.abs(size.y)
      }
    } catch (error) {
      console.error('❌ Błąd podczas obliczania wymiarów z modelu:', error)
      return null
    }
  }

  /**
   * Pomocnicza: Oblicza wymiary elementu
   */
  private calculateDimensions(elementType: string, fenceConfig: FenceConfig, model?: THREE.Scene, postHeight?: number): ElementDimensions {
    const config = fenceConfig.elements?.[elementType as 'post' | 'section' | 'gate' | 'door']
    const spacing = config?.spacing || 0.16

    DEBUG_LOGS && console.log(`🔍 calculateDimensions for ${elementType}:`, {
      fenceId: fenceConfig.id,
      fenceName: fenceConfig.name,
      configExists: !!config,
      spacing,
      configSpacing: config?.spacing,
      fallbackSpacing: 0.16
    })

    // 🎯 NOWE: Spróbuj obliczyć wymiary z rzeczywistego modelu 3D
    let width: number
    // 🎯 Dla section: użyj section.defaultHeight, dla post: użyj postHeight z suwaka lub post.defaultHeight
    let height: number
    if (elementType === 'section') {
      height = config?.defaultHeight || 6
    } else if (elementType === 'post') {
      // 🎯 KLUCZOWE: Użyj postHeight z suwaka (ElementToolbar) zamiast defaultHeight z konfiguracji
      height = postHeight !== undefined ? postHeight : (fenceConfig.elements?.post?.defaultHeight || 6)
      DEBUG_LOGS && console.log(`🎯 addFenceElement POST: używam wysokości ${height} bloków (z suwaka: ${postHeight}, z konfiguracji: ${fenceConfig.elements?.post?.defaultHeight || 6})`)
    } else {
      height = fenceConfig.elements?.post?.defaultHeight || 6
    }
    
    if (model && fenceConfig.modelScale) {
      const modelDimensions = this.calculateDimensionsFromModel(model, fenceConfig.modelScale)
      if (modelDimensions) {
        width = modelDimensions.width
        // NIE nadpisuj height z modelu 3D - używaj defaultHeight z konfiguracji
        DEBUG_LOGS && console.log(`✅ Użyto RZECZYWISTYCH wymiarów z modelu 3D: width=${width.toFixed(3)}m, height=${height} (z konfiguracji)`)
        return { width, height }
      }
    }
    
    // Fallback: oblicz z konfiguracji spacing
    if (elementType === 'post') {
      width = spacing * 2
    } else if (elementType === 'section') {
      // 🎯 Dla section: użyj defaultWidth jeśli istnieje, w przeciwnym razie spacing * 2
      width = config?.defaultWidth || (spacing * 2)
    } else if (elementType === 'gate') {
      // 🚪 Dla bramy: użyj defaultWidth jeśli istnieje, w przeciwnym razie spacing * 4
      width = config?.defaultWidth || (spacing * 4)
    } else if (elementType === 'door') {
      // 🚪 Dla drzwi: użyj defaultWidth jeśli istnieje, w przeciwnym razie spacing * 2
      width = config?.defaultWidth || (spacing * 2)
    } else {
      width = spacing * 2
    }

    DEBUG_LOGS && console.log(`📐 Calculated dimensions for ${elementType}: width=${width.toFixed(3)}m (spacing=${spacing.toFixed(3)}m), height=2.0m`)
    DEBUG_LOGS && console.log(`⚠️ WARNING: If this width seems wrong, check that the correct fence config is loaded!`)

    return { width, height }
  }

  /**
   * Pomocnicza: Grupuje bloczki sekcji po pozycjach X (kolumny) – używane dla Muro P22
   */
  private collectMuroSectionColumns(
    elementGroup: THREE.Group,
    baseGroupNames: string[]
  ): Array<{
    key: string
    baseName: string
    x: number
    z: number
    blocks: THREE.Object3D[]
    template: THREE.Object3D | null
  }> {
    const matchesGroup = (childName: string, groupName: string | undefined): boolean => {
      if (!groupName) return false
      return childName === groupName || childName.startsWith(`${groupName}_copy_`)
    }

    const columnsMap = new Map<
      string,
      {
        key: string
        baseName: string
        x: number
        z: number
        blocks: THREE.Object3D[]
        template: THREE.Object3D | null
      }
    >()

    elementGroup.children.forEach(child => {
      const isBase = baseGroupNames.some(groupName => matchesGroup(child.name, groupName))
      if (!isBase) return

      const baseName = child.name.replace(/_copy_.*/, '')
      const key = `${baseName}|${child.position.x.toFixed(4)}|${child.position.z.toFixed(4)}`
      if (!columnsMap.has(key)) {
        columnsMap.set(key, {
          key,
          baseName,
          x: child.position.x,
          z: child.position.z,
          blocks: [],
          template: null
        })
      }
      const column = columnsMap.get(key)!
      column.blocks.push(child)
    })

    const columns = Array.from(columnsMap.values())
    columns.forEach(column => {
      column.blocks.sort((a, b) => a.position.y - b.position.y)
      if (column.blocks.length > 0) {
        // Zapisz wzorcowy blok do klonowania (poza sceną)
        column.template = column.blocks[0].clone(true)
      }
    })

    columns.sort((a, b) => a.x - b.x)
    return columns
  }

  private ensureSectionDecorationsForColumns(
    elementGroup: THREE.Group,
    fenceConfig: FenceConfig,
    baseGroupNames: string[],
    decorationGroupName: string,
    model: THREE.Scene
  ): void {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return

    const matchesGroup = (childName: string, groupName: string | undefined): boolean => {
      if (!groupName) return false
      return childName === groupName || childName.startsWith(`${groupName}_copy_`)
    }

    const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
    if (columns.length === 0) return

    const modelScale = fenceConfig.modelScale || 0.01
    const elementId = elementGroup.userData?.fenceElementId || 'unknown'
    const elementWidth =
      elementGroup.userData?.width ||
      sectionConfig.defaultWidth ||
      fenceConfig.elements?.section?.spacing ||
      0.4

    const getTemplateDecoration = (): THREE.Object3D | null => {
      const existing = elementGroup.children.find(child => matchesGroup(child.name, decorationGroupName))
      if (existing) return existing
      const source = model.getObjectByName(decorationGroupName)
      return source ?? null
    }

    const decorationBlocks = elementGroup.children.filter(child =>
      matchesGroup(child.name, decorationGroupName)
    )

    const templateForPrepare = model.getObjectByName(decorationGroupName)

    const createDecorationClone = (index: number): THREE.Object3D | null => {
      const template = getTemplateDecoration()
      if (!template) return null
      const cloned = template.clone(true)
      if (template === templateForPrepare && templateForPrepare) {
        prepareElement(cloned, {
          modelScale,
          positionY: 0,
          elementId,
          elementWidth,
          fenceConfig
        })
      }
      cloned.name = `${decorationGroupName}_copy_column_${index}`
      elementGroup.add(cloned)
      return cloned
    }

    while (decorationBlocks.length < columns.length) {
      const newDeco = createDecorationClone(decorationBlocks.length)
      if (!newDeco) break
      decorationBlocks.push(newDeco)
    }

    while (decorationBlocks.length > columns.length) {
      const excess = decorationBlocks.pop()
      if (excess) {
        elementGroup.remove(excess)
      }
    }

    decorationBlocks.sort((a, b) => a.position.x - b.position.x)
    columns.sort((a, b) => a.x - b.x)

    let decorationSpacing =
      sectionConfig.decorationSpacing ??
      sectionConfig.blockSpacing ??
      0.1

    // 🎯 Vision-Bet Medium/Max: użyj blockSpacing, żeby dekoracja nie wchodziła w górny bloczek
    if (
      fenceConfig.id === 'vision-bet-medium' ||
      fenceConfig.id === 'gladkie_medium' ||
      fenceConfig.id === 'vision-bet-max'
    ) {
      decorationSpacing = sectionConfig.blockSpacing || decorationSpacing
    }
    const decorationOffsetX = (sectionConfig as any)?.decorationOffset || 0
    const decorationOffsetZ = (sectionConfig as any)?.decorationOffsetZ || 0

    columns.forEach((column, index) => {
      const decorationBlock = decorationBlocks[index]
      const lastBasicBlock = column.blocks[column.blocks.length - 1]
      if (!decorationBlock || !lastBasicBlock) return

      decorationBlock.position.x = column.x + decorationOffsetX
      decorationBlock.position.z = column.z + decorationOffsetZ
      decorationBlock.position.y = lastBasicBlock.position.y + decorationSpacing
    })
  }

  /**
   * Specjalna logika: aktualizuje wysokość sekcji dla Łupane Muro P22 (kilka kolumn w jednym elemencie)
   */
  private updateMuroP22SectionHeight(
    elementGroup: THREE.Group,
    targetLayers: number,
    elementId: string,
    fenceConfig: FenceConfig,
    model: THREE.Scene,
    baseGroupNames: string[],
    decorationGroupName?: string
  ): boolean {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return false

    const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
    if (columns.length === 0) {
      console.warn(`⚠️ updateMuroP22SectionHeight: Brak kolumn bazowych w elemencie ${elementId}`)
      return false
    }

    const blockSpacing = sectionConfig.blockSpacing || 0.064
    const modelScale = fenceConfig.modelScale || 0.01
    const baseGroupName = baseGroupNames[0]
    if (!baseGroupName) return false

    const currentLayers = Math.max(...columns.map(column => column.blocks.length))
    const target = Math.max(0, targetLayers)
    const layersDiff = target - currentLayers

    if (layersDiff > 0) {
      const sourceElement = model?.getObjectByName(baseGroupName) || null

      for (let layer = 0; layer < layersDiff; layer++) {
        columns.forEach(column => {
          const referenceBlock =
            column.blocks[column.blocks.length - 1] ||
            column.template ||
            (sourceElement ? sourceElement : null)

          if (!referenceBlock) {
            console.warn(`⚠️ updateMuroP22SectionHeight: Brak wzorcowego bloku do klonowania dla kolumny ${column.key}`)
            return
          }

          const clonedElement =
            referenceBlock === sourceElement
              ? (sourceElement as THREE.Object3D).clone(true)
              : referenceBlock.clone(true)

          // Jeżeli klonujemy bezpośrednio z modelu, przygotuj go (skalowanie/rotacja)
          if (referenceBlock === sourceElement) {
            prepareElement(clonedElement, {
              modelScale,
              positionY: 0,
              elementId,
              elementWidth: sectionConfig.defaultWidth || elementGroup.userData?.baseWidth || 0.148,
              fenceConfig
            })
          }

          const topBlock = column.blocks[column.blocks.length - 1]
          const newY = topBlock ? topBlock.position.y + blockSpacing : 0

          clonedElement.position.set(
            column.x,
            newY,
            topBlock ? topBlock.position.z : column.blocks[0]?.position.z ?? column.z
          )

          clonedElement.updateMatrixWorld(true)

          elementGroup.add(clonedElement)
          column.blocks.push(clonedElement)
        })
      }
    } else if (layersDiff < 0) {
      const layersToRemove = Math.abs(layersDiff)
      for (let layer = 0; layer < layersToRemove; layer++) {
        columns.forEach(column => {
          if (column.blocks.length === 0) return
          const blockToRemove = column.blocks.pop()
          if (blockToRemove) {
            elementGroup.remove(blockToRemove)
          }
        })
      }

      // Przypisz równe odstępy po usunięciu warstw
      columns.forEach(column => {
        column.blocks.forEach((block, index) => {
          block.position.y = index * blockSpacing
        })
      })
    }

    // Ustaw dekorację na nowej wysokości
    const decorationBlocks = decorationGroupName
      ? elementGroup.children.filter(child => child.name === decorationGroupName)
      : []
    decorationBlocks.forEach(decorationBlock => {
      const column = columns.find(col =>
        col.blocks.some(block => Math.abs(block.position.x - decorationBlock.position.x) < 0.01)
      )
      if (!column || column.blocks.length === 0) return

      const topBlock = column.blocks[column.blocks.length - 1]
      const decorationSpacing =
        sectionConfig?.decorationSpacing ??
        sectionConfig?.blockSpacing ??
        0.1

      decorationBlock.position.y = topBlock.position.y + decorationSpacing
    })

    elementGroup.userData.height = target

    DEBUG_LOGS && console.log(`✅ updateMuroP22SectionHeight: Ustawiono wysokość ${target} warstw dla elementu ${elementId}`)
    return true
  }

  /**
   * Specjalna logika: aktualizuje wysokość sekcji dla Łupane Muro S22 (kilka kolumn w jednym elemencie)
   */
  private updateMuroS22SectionHeight(
    elementGroup: THREE.Group,
    targetLayers: number,
    elementId: string,
    fenceConfig: FenceConfig,
    model: THREE.Scene,
    baseGroupNames: string[],
    decorationGroupName?: string
  ): boolean {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return false

    const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
    if (columns.length === 0) {
      console.warn(`⚠️ updateMuroS22SectionHeight: Brak kolumn bazowych w elemencie ${elementId}`)
      return false
    }

    const blockSpacing = sectionConfig.blockSpacing || 0.064
    const modelScale = fenceConfig.modelScale || 0.01
    const baseGroupName = baseGroupNames[0]
    if (!baseGroupName) return false

    const currentLayers = Math.max(...columns.map(column => column.blocks.length))
    const target = Math.max(0, targetLayers)
    const layersDiff = target - currentLayers

    if (layersDiff > 0) {
      const sourceElement = model?.getObjectByName(baseGroupName) || null

      for (let layer = 0; layer < layersDiff; layer++) {
        columns.forEach(column => {
          const referenceBlock =
            column.blocks[column.blocks.length - 1] ||
            column.template ||
            (sourceElement ? sourceElement : null)

          if (!referenceBlock) {
            console.warn(`⚠️ updateMuroS22SectionHeight: Brak wzorcowego bloku do klonowania dla kolumny ${column.key}`)
            return
          }

          const clonedElement =
            referenceBlock === sourceElement
              ? (sourceElement as THREE.Object3D).clone(true)
              : referenceBlock.clone(true)

          if (referenceBlock === sourceElement) {
            prepareElement(clonedElement, {
              modelScale,
              positionY: 0,
              elementId,
              elementWidth: sectionConfig.defaultWidth || elementGroup.userData?.baseWidth || 0.148,
              fenceConfig
            })
          }

          const topBlock = column.blocks[column.blocks.length - 1]
          const newY = topBlock ? topBlock.position.y + blockSpacing : 0

          clonedElement.position.set(
            column.x,
            newY,
            topBlock ? topBlock.position.z : column.blocks[0]?.position.z ?? column.z
          )

          clonedElement.updateMatrixWorld(true)

          elementGroup.add(clonedElement)
          column.blocks.push(clonedElement)
        })
      }
    } else if (layersDiff < 0) {
      const layersToRemove = Math.abs(layersDiff)
      for (let layer = 0; layer < layersToRemove; layer++) {
        columns.forEach(column => {
          if (column.blocks.length === 0) return
          const blockToRemove = column.blocks.pop()
          if (blockToRemove) {
            elementGroup.remove(blockToRemove)
          }
        })
      }

      columns.forEach(column => {
        column.blocks.forEach((block, index) => {
          block.position.y = index * blockSpacing
        })
      })
    }

    const decorationBlocks = decorationGroupName
      ? elementGroup.children.filter(child => child.name === decorationGroupName)
      : []
    decorationBlocks.forEach(decorationBlock => {
      const column = columns.find(col =>
        col.blocks.some(block => Math.abs(block.position.x - decorationBlock.position.x) < 0.01)
      )
      if (!column || column.blocks.length === 0) return

      const topBlock = column.blocks[column.blocks.length - 1]
      const decorationSpacing =
        sectionConfig?.decorationSpacing ??
        sectionConfig?.blockSpacing ??
        0.1

      decorationBlock.position.y = topBlock.position.y + decorationSpacing
    })

    elementGroup.userData.height = target

    DEBUG_LOGS && console.log(`✅ updateMuroS22SectionHeight: Ustawiono wysokość ${target} warstw dla elementu ${elementId}`)
    return true
  }

  /**
   * Specjalna logika: aktualizuje wysokość sekcji dla Łupane Muro P38 (kilka kolumn + dekoracja z podgrupą)
   */
  private updateMuroP38SectionHeight(
    elementGroup: THREE.Group,
    targetLayers: number,
    elementId: string,
    fenceConfig: FenceConfig,
    model: THREE.Scene,
    baseGroupNames: string[],
    decorationGroupName?: string
  ): boolean {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return false

    const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
    if (columns.length === 0) {
      console.warn(`⚠️ updateMuroP38SectionHeight: Brak kolumn bazowych w elemencie ${elementId}`)
      return false
    }

    const blockSpacing = sectionConfig.blockSpacing || 0.064
    const modelScale = fenceConfig.modelScale || 0.01
    const baseGroupName = baseGroupNames[0]
    if (!baseGroupName) return false

    const currentLayers = Math.max(...columns.map(column => column.blocks.length))
    const target = Math.max(0, targetLayers)
    const layersDiff = target - currentLayers

    if (layersDiff > 0) {
      const sourceElement = model?.getObjectByName(baseGroupName) || null

      for (let layer = 0; layer < layersDiff; layer++) {
        columns.forEach(column => {
          const referenceBlock =
            column.blocks[column.blocks.length - 1] ||
            column.template ||
            (sourceElement ? sourceElement : null)

          if (!referenceBlock) {
            console.warn(`⚠️ updateMuroP38SectionHeight: Brak wzorcowego bloku do klonowania dla kolumny ${column.key}`)
            return
          }

          const clonedElement =
            referenceBlock === sourceElement
              ? (sourceElement as THREE.Object3D).clone(true)
              : referenceBlock.clone(true)

          if (referenceBlock === sourceElement) {
            prepareElement(clonedElement, {
              modelScale,
              positionY: 0,
              elementId,
              elementWidth: sectionConfig.defaultWidth || elementGroup.userData?.baseWidth || 0.148,
              fenceConfig
            })
          }

          const topBlock = column.blocks[column.blocks.length - 1]
          const newY = topBlock ? topBlock.position.y + blockSpacing : 0

          clonedElement.position.set(
            column.x,
            newY,
            topBlock ? topBlock.position.z : column.blocks[0]?.position.z ?? column.z
          )

          clonedElement.updateMatrixWorld(true)

          elementGroup.add(clonedElement)
          column.blocks.push(clonedElement)
        })
      }
    } else if (layersDiff < 0) {
      const layersToRemove = Math.abs(layersDiff)
      for (let layer = 0; layer < layersToRemove; layer++) {
        columns.forEach(column => {
          if (column.blocks.length === 0) return
          const blockToRemove = column.blocks.pop()
          if (blockToRemove) {
            elementGroup.remove(blockToRemove)
          }
        })
      }

      columns.forEach(column => {
        column.blocks.forEach((block, index) => {
          block.position.y = index * blockSpacing
        })
      })
    }

    if (decorationGroupName) {
      const isDecorationInstance = (childName: string) =>
        childName === decorationGroupName || childName.startsWith(`${decorationGroupName}_copy_`)

      const decorationBlocks = elementGroup.children.filter(child => isDecorationInstance(child.name || ''))
      decorationBlocks.forEach(decorationBlock => {
        const column = columns.find(col =>
          col.blocks.some(block => Math.abs(block.position.x - decorationBlock.position.x) < 0.01)
        )
        if (!column || column.blocks.length === 0) return

        const topBlock = column.blocks[column.blocks.length - 1]
        const decorationSpacing =
          sectionConfig?.decorationSpacing ??
          sectionConfig?.blockSpacing ??
          0.1

        decorationBlock.position.y = topBlock.position.y + decorationSpacing
      })
    }

    elementGroup.userData.height = target

    DEBUG_LOGS && console.log(`✅ updateMuroP38SectionHeight: Ustawiono wysokość ${target} warstw dla elementu ${elementId}`)
    return true
  }

  /**
   * Specjalna logika: aktualizuje wysokość sekcji dla Łupane Muro S38 (kilka kolumn z dodatkowymi offsetami)
   */
  private updateMuroS38SectionHeight(
    elementGroup: THREE.Group,
    targetLayers: number,
    elementId: string,
    fenceConfig: FenceConfig,
    model: THREE.Scene,
    baseGroupNames: string[],
    decorationGroupName?: string
  ): boolean {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return false

    const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
    if (columns.length === 0) {
      console.warn(`⚠️ updateMuroS38SectionHeight: Brak kolumn bazowych w elemencie ${elementId}`)
      return false
    }

    const blockSpacing = sectionConfig.blockSpacing || 0.064
    const modelScale = fenceConfig.modelScale || 0.01
    const baseGroupName = baseGroupNames[0]
    if (!baseGroupName) return false

    const currentLayers = Math.max(...columns.map(column => column.blocks.length))
    const target = Math.max(0, targetLayers)
    const layersDiff = target - currentLayers

    if (layersDiff > 0) {
      const sourceElement = model?.getObjectByName(baseGroupName) || null

      for (let layer = 0; layer < layersDiff; layer++) {
        columns.forEach(column => {
          const referenceBlock =
            column.blocks[column.blocks.length - 1] ||
            column.template ||
            (sourceElement ? sourceElement : null)

          if (!referenceBlock) {
            console.warn(`⚠️ updateMuroS38SectionHeight: Brak wzorcowego bloku do klonowania dla kolumny ${column.key}`)
            return
          }

          const clonedElement =
            referenceBlock === sourceElement
              ? (sourceElement as THREE.Object3D).clone(true)
              : referenceBlock.clone(true)

          if (referenceBlock === sourceElement) {
            prepareElement(clonedElement, {
              modelScale,
              positionY: 0,
              elementId,
              elementWidth: sectionConfig.defaultWidth || elementGroup.userData?.baseWidth || 0.148,
              fenceConfig
            })
          }

          const topBlock = column.blocks[column.blocks.length - 1]
          const newY = topBlock ? topBlock.position.y + blockSpacing : 0

          clonedElement.position.set(
            column.x,
            newY,
            topBlock ? topBlock.position.z : column.blocks[0]?.position.z ?? column.z
          )

          clonedElement.updateMatrixWorld(true)

          elementGroup.add(clonedElement)
          column.blocks.push(clonedElement)
        })
      }
    } else if (layersDiff < 0) {
      const layersToRemove = Math.abs(layersDiff)
      for (let layer = 0; layer < layersToRemove; layer++) {
        columns.forEach(column => {
          if (column.blocks.length === 0) return
          const blockToRemove = column.blocks.pop()
          if (blockToRemove) {
            elementGroup.remove(blockToRemove)
          }
        })
      }

      columns.forEach(column => {
        column.blocks.forEach((block, index) => {
          block.position.y = index * blockSpacing
        })
      })
    }

    if (decorationGroupName) {
      const decorationBlocks = elementGroup.children.filter(child => child.name === decorationGroupName)
      decorationBlocks.forEach(decorationBlock => {
        const column = columns.find(col =>
          col.blocks.some(block => Math.abs(block.position.x - decorationBlock.position.x) < 0.01)
        )
        if (!column || column.blocks.length === 0) return

        const topBlock = column.blocks[column.blocks.length - 1]
        const decorationSpacing =
          sectionConfig?.decorationSpacing ??
          sectionConfig?.blockSpacing ??
          0.1

        decorationBlock.position.y = topBlock.position.y + decorationSpacing

        // Dla S38 dekoracja powinna być wyrównana w osi X z kolumną bazową (grupa 21)
        decorationBlock.position.x = column.x
      })
    }

    elementGroup.userData.height = target

    DEBUG_LOGS && console.log(`✅ updateMuroS38SectionHeight: Ustawiono wysokość ${target} warstw dla elementu ${elementId}`)
    return true
  }

  /**
   * Specjalna logika: aktualizuje wysokość sekcji dla Royal S50 (kilka kolumn, dekor na bokach)
   */
  private updateRoyalS50SectionHeight(
    elementGroup: THREE.Group,
    targetLayers: number,
    elementId: string,
    fenceConfig: FenceConfig,
    model: THREE.Scene,
    baseGroupNames: string[],
    decorationGroupName?: string
  ): boolean {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return false

    const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
    if (columns.length === 0) {
      console.warn(`⚠️ updateRoyalS50SectionHeight: Brak kolumn bazowych w elemencie ${elementId}`)
      return false
    }

    const blockSpacing = sectionConfig.blockSpacing || 0.078
    const modelScale = fenceConfig.modelScale || 0.01
    const baseGroupName = baseGroupNames[0]
    if (!baseGroupName) return false

    const currentLayers = Math.max(...columns.map(column => column.blocks.length))
    const target = Math.max(0, targetLayers)
    const layersDiff = target - currentLayers

    if (layersDiff > 0) {
      const sourceElement = model?.getObjectByName(baseGroupName) || null

      for (let layer = 0; layer < layersDiff; layer++) {
        columns.forEach(column => {
          const referenceBlock =
            column.blocks[column.blocks.length - 1] ||
            column.template ||
            (sourceElement ? sourceElement : null)

          if (!referenceBlock) {
            console.warn(`⚠️ updateRoyalS50SectionHeight: Brak wzorcowego bloku do klonowania dla kolumny ${column.key}`)
            return
          }

          const clonedElement =
            referenceBlock === sourceElement
              ? (sourceElement as THREE.Object3D).clone(true)
              : referenceBlock.clone(true)

          if (referenceBlock === sourceElement) {
            prepareElement(clonedElement, {
              modelScale,
              positionY: 0,
              elementId,
              elementWidth: sectionConfig.defaultWidth || elementGroup.userData?.baseWidth || 0.198,
              fenceConfig
            })
          }

          const topBlock = column.blocks[column.blocks.length - 1]
          const newY = topBlock ? topBlock.position.y + blockSpacing : 0

          clonedElement.position.set(
            column.x,
            newY,
            topBlock ? topBlock.position.z : column.blocks[0]?.position.z ?? column.z
          )

          clonedElement.updateMatrixWorld(true)

          elementGroup.add(clonedElement)
          column.blocks.push(clonedElement)
        })
      }
    } else if (layersDiff < 0) {
      const layersToRemove = Math.abs(layersDiff)
      for (let layer = 0; layer < layersToRemove; layer++) {
        columns.forEach(column => {
          if (column.blocks.length === 0) return
          const blockToRemove = column.blocks.pop()
          if (blockToRemove) {
            elementGroup.remove(blockToRemove)
          }
        })
      }

      columns.forEach(column => {
        column.blocks.forEach((block, index) => {
          block.position.y = index * blockSpacing
        })
      })
    }

    if (decorationGroupName) {
      const isDecorationInstance = (childName: string) =>
        childName === decorationGroupName || childName.startsWith(`${decorationGroupName}_copy_`)

      const highestColumn = columns.reduce<null | { column: typeof columns[number]; topBlock: THREE.Object3D }>(
        (best, col) => {
          if (col.blocks.length === 0) {
            return best
          }
          const top = col.blocks[col.blocks.length - 1]
          if (!best || top.position.y > best.topBlock.position.y) {
            return { column: col, topBlock: top }
          }
          return best
        },
        null
      )

      const decorationBlocks = elementGroup.children.filter(child => isDecorationInstance(child.name || ''))
      decorationBlocks.forEach(decorationBlock => {
        let column = columns.find(col =>
          col.blocks.some(block => Math.abs(block.position.x - decorationBlock.position.x) < 0.01)
        )

        if ((!column || column.blocks.length === 0) && columns.length > 0) {
          let bestColumn: typeof columns[number] | null = null
          let bestDistance = Infinity

          columns.forEach(col => {
            if (col.blocks.length === 0) return
            const dx = col.x - decorationBlock.position.x
            const dz = col.z - decorationBlock.position.z
            const distance = Math.abs(dx) + Math.abs(dz)
            if (distance < bestDistance) {
              bestDistance = distance
              bestColumn = col
            }
          })

          column = bestColumn ?? column
        }

        if ((!column || column.blocks.length === 0) && highestColumn) {
          column = highestColumn.column
        }

        if (!column || column.blocks.length === 0) return

        const topBlock = column.blocks[column.blocks.length - 1]
        const blockHeight =
          column.blocks.length > 1
            ? Math.abs(
                column.blocks[column.blocks.length - 1].position.y -
                  column.blocks[column.blocks.length - 2].position.y
              )
            : sectionConfig?.blockSpacing ?? blockSpacing
        const topSurfaceY = topBlock.position.y + blockHeight / 2
        const decorationSpacingValue =
          typeof sectionConfig?.decorationSpacing === 'number'
            ? sectionConfig.decorationSpacing
            : sectionConfig?.blockSpacing ?? blockHeight

        const targetY = topSurfaceY + decorationSpacingValue
        const minVisibleY = topSurfaceY + 0.04

        decorationBlock.position.y = Math.max(minVisibleY, targetY)
      })
    }

    elementGroup.userData.height = target

    DEBUG_LOGS && console.log(`✅ updateRoyalS50SectionHeight: Ustawiono wysokość ${target} warstw dla elementu ${elementId}`)
    return true
  }

  /**
   * Specjalna logika: aktualizuje wysokość sekcji dla Royal P50 (podobna struktura jak S50)
   */
  private updateRoyalP50SectionHeight(
    elementGroup: THREE.Group,
    targetLayers: number,
    elementId: string,
    fenceConfig: FenceConfig,
    model: THREE.Scene,
    baseGroupNames: string[],
    decorationGroupName?: string
  ): boolean {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return false

    const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
    if (columns.length === 0) {
      console.warn(`⚠️ updateRoyalP50SectionHeight: Brak kolumn bazowych w elemencie ${elementId}`)
      return false
    }

    const blockSpacing = sectionConfig.blockSpacing || 0.078
    const modelScale = fenceConfig.modelScale || 0.01
    const baseGroupName = baseGroupNames[0]
    if (!baseGroupName) return false

    const currentLayers = Math.max(...columns.map(column => column.blocks.length))
    const target = Math.max(0, targetLayers)
    const layersDiff = target - currentLayers

    if (layersDiff > 0) {
      const sourceElement = model?.getObjectByName(baseGroupName) || null

      for (let layer = 0; layer < layersDiff; layer++) {
        columns.forEach(column => {
          const referenceBlock =
            column.blocks[column.blocks.length - 1] ||
            column.template ||
            (sourceElement ? sourceElement : null)

          if (!referenceBlock) {
            console.warn(`⚠️ updateRoyalP50SectionHeight: Brak wzorcowego bloku do klonowania dla kolumny ${column.key}`)
            return
          }

          const clonedElement =
            referenceBlock === sourceElement
              ? (sourceElement as THREE.Object3D).clone(true)
              : referenceBlock.clone(true)

          if (referenceBlock === sourceElement) {
            prepareElement(clonedElement, {
              modelScale,
              positionY: 0,
              elementId,
              elementWidth: sectionConfig.defaultWidth || elementGroup.userData?.baseWidth || 0.198,
              fenceConfig
            })
          }

          const topBlock = column.blocks[column.blocks.length - 1]
          const newY = topBlock ? topBlock.position.y + blockSpacing : 0

          clonedElement.position.set(
            column.x,
            newY,
            topBlock ? topBlock.position.z : column.blocks[0]?.position.z ?? column.z
          )

          clonedElement.updateMatrixWorld(true)

          elementGroup.add(clonedElement)
          column.blocks.push(clonedElement)
        })
      }
    } else if (layersDiff < 0) {
      const layersToRemove = Math.abs(layersDiff)
      for (let layer = 0; layer < layersToRemove; layer++) {
        columns.forEach(column => {
          if (column.blocks.length === 0) return
          const blockToRemove = column.blocks.pop()
          if (blockToRemove) {
            elementGroup.remove(blockToRemove)
          }
        })
      }

      columns.forEach(column => {
        column.blocks.forEach((block, index) => {
          block.position.y = index * blockSpacing
        })
      })
    }

    if (decorationGroupName) {
      const isDecorationInstance = (name: string | undefined) =>
        name === decorationGroupName || name?.startsWith(`${decorationGroupName}_copy_`)

      const highestColumn = columns.reduce<
        null | { column: typeof columns[number]; topBlock: THREE.Object3D }
      >((best, column) => {
        if (column.blocks.length === 0) return best
        const candidate = column.blocks[column.blocks.length - 1]
        if (!best || candidate.position.y > best.topBlock.position.y) {
          return { column, topBlock: candidate }
        }
        return best
      }, null)

      const decorationBlocks = elementGroup.children.filter(child => isDecorationInstance(child.name))
      decorationBlocks.forEach(decorationBlock => {
        let column =
          columns.find(col => col.blocks.some(block => Math.abs(block.position.x - decorationBlock.position.x) < 0.01)) ??
          null

        if ((!column || column.blocks.length === 0) && columns.length > 0) {
          let bestColumn: typeof columns[number] | null = null
          let bestDistance = Infinity

          columns.forEach(col => {
            if (col.blocks.length === 0) return
            const dx = col.x - decorationBlock.position.x
            const dz = col.z - decorationBlock.position.z
            const distance = Math.sqrt(dx * dx + dz * dz)
            if (distance < bestDistance) {
              bestDistance = distance
              bestColumn = col
            }
          })

          column = bestColumn ?? column
        }

        if ((!column || column.blocks.length === 0) && highestColumn) {
          column = highestColumn.column
        }

        if (!column || column.blocks.length === 0) return

        const topBlock = column.blocks[column.blocks.length - 1]
        const blockHeight =
          column.blocks.length > 1
            ? Math.abs(
                column.blocks[column.blocks.length - 1].position.y -
                  column.blocks[column.blocks.length - 2].position.y
              )
            : sectionConfig?.blockSpacing ?? blockSpacing
        const topSurfaceY = topBlock.position.y + blockHeight / 2
        const decorationSpacingValue =
          typeof sectionConfig?.decorationSpacing === 'number'
            ? sectionConfig.decorationSpacing
            : sectionConfig?.blockSpacing ?? blockHeight

        const targetY = topSurfaceY + decorationSpacingValue
        const minVisibleY = topSurfaceY + 0.04

        decorationBlock.position.set(
          topBlock.position.x,
          Math.max(minVisibleY, targetY),
          topBlock.position.z
        )
      })
    }

    elementGroup.userData.height = target

    DEBUG_LOGS && console.log(`✅ updateRoyalP50SectionHeight: Ustawiono wysokość ${target} warstw dla elementu ${elementId}`)
    return true
  }

  /**
   * Pomocnicza: Usuwa zagnieżdżone dekoracje z klonowanego bloku (dla S22)
   */
  private removeNestedDecorations(obj: THREE.Object3D, decorationNames: string[]): void {
    const toRemove: THREE.Object3D[] = []
    obj.traverse(child => {
      if (child === obj) return
      const childName = child.name || ''
      // Sprawdź czy childName pasuje do którejkolwiek z nazw dekoracji
      // Używamy dokładnego dopasowania lub prefiksów _copy_
      if (decorationNames.some(decName => {
        if (!decName) return false
        return childName === decName || childName.startsWith(`${decName}_copy_`)
      })) {
        toRemove.push(child)
      }
    })
    
    // Usuń znalezione dekoracje
    toRemove.forEach(child => {
      if (child.parent) {
        child.parent.remove(child)
        DEBUG_LOGS && console.log(`🧹 Usunięto zagnieżdżoną dekorację: ${child.name || 'unnamed'} z ${child.parent.name || 'unknown'}`)
      }
    })
    
    if (toRemove.length > 0) {
      DEBUG_LOGS && console.log(`✅ removeNestedDecorations: Usunięto ${toRemove.length} zagnieżdżonych dekoracji`)
    }
  }

  /**
   * Pomocnicza: wyrównuje blok dekoracyjny Gładkie Max zaraz po dodaniu sekcji
   */
  private adjustGladkieMaxSectionDecoration(elementGroup: THREE.Group, fenceConfig: FenceConfig): void {
    const sectionConfig = fenceConfig.elements?.section
    if (!sectionConfig) return

    const decorationGroupName = sectionConfig.decorationGroup
    if (!decorationGroupName) return

    const baseGroupNames = (sectionConfig.groups || []).filter(
      groupName => groupName && groupName !== decorationGroupName && groupName !== sectionConfig.metalGroup
    )
    if (baseGroupNames.length === 0) return

    const matchesGroup = (childName: string | undefined, groupName: string) =>
      !!childName && (childName === groupName || childName.startsWith(`${groupName}_copy_`))

    const basicBlocks = elementGroup.children.filter(child =>
      baseGroupNames.some(groupName => matchesGroup(child.name, groupName))
    )

    if (basicBlocks.length === 0) return

    basicBlocks.sort((a, b) => a.position.y - b.position.y)
    const highestBlock = basicBlocks[basicBlocks.length - 1]

    const decorationBlocks = elementGroup.children.filter(child => matchesGroup(child.name, decorationGroupName))
    if (decorationBlocks.length === 0) return

    const decorationSpacing =
      typeof sectionConfig.decorationSpacing === 'number'
        ? sectionConfig.decorationSpacing
        : sectionConfig.blockSpacing ?? 0.1

    const targetY = highestBlock.position.y + decorationSpacing

    decorationBlocks.forEach(decorationBlock => {
      decorationBlock.position.y = targetY
    })

    console.log(
      `🎯 Gladkie Max: dekoracja ${decorationGroupName} ustawiona na Y=${targetY.toFixed(3)} (najwyższy blok Y=${highestBlock.position.y.toFixed(
        3
      )}, spacing=${decorationSpacing})`
    )
  }

  /**
   * Pomocnicza: Dodaje bloki do słupka
   */
  private addBlocksToPost(
    elementGroup: THREE.Group,
    blocksToAdd: number,
    elementId: string,
    fenceConfig: FenceConfig,
    model: THREE.Scene,
    textures?: any
  ): void {
    // 🎯 Użyj odpowiednich grup w zależności od typu elementu
    const isSection = elementGroup.name === 'section_group'
    const availableGroups = isSection 
      ? (fenceConfig.elements?.section?.groups || ['group_0'])
      : (fenceConfig.elements?.post?.groups || ['group_0'])
    const basicGroupName = availableGroups[0]
    
    // 🎯 Sprawdź czy jest drugi element bloku (nie dekoracyjny)
    // Jeśli groups[1] to decorationGroup, to groups[2] to drugi element bloku
    // Jeśli groups[1] nie jest decorationGroup, to groups[1] to drugi element bloku
    const elementConfig = isSection
      ? fenceConfig.elements?.section
      : fenceConfig.elements?.post
    let decorationGroupName = elementConfig?.decorationGroup || availableGroups[1]
    if (!isSection && fenceConfig.id === 'lupane-muro-s22') {
      decorationGroupName = elementConfig?.decorationGroup || 'group_63'
    }
    let secondaryGroupName: string | undefined
    
    if (availableGroups.length >= 2) {
      // Jeśli groups[1] to dekoracyjna, sprawdź groups[2]
      if (availableGroups[1] === decorationGroupName && availableGroups.length >= 3) {
        secondaryGroupName = availableGroups[2]
      } else if (availableGroups[1] !== decorationGroupName) {
        // groups[1] nie jest dekoracyjna, więc to drugi element bloku
        secondaryGroupName = availableGroups[1]
      }
    }
    
    const metalGroupName = elementConfig?.metalGroup
    if (secondaryGroupName && decorationGroupName && secondaryGroupName === decorationGroupName) {
      secondaryGroupName = undefined
    }
    if (secondaryGroupName && metalGroupName && secondaryGroupName === metalGroupName) {
      DEBUG_LOGS && console.log(`🔧 addBlocksToPost: Pomijam secondaryGroup ${secondaryGroupName} (metalGroup)`)
      secondaryGroupName = undefined
    }
    
    const matchesGroup = (childName: string, groupName: string | undefined): boolean => {
      if (!groupName) return false
      return childName === groupName || childName.startsWith(`${groupName}_copy_`)
    }
    
    DEBUG_LOGS && console.log(`🔧 addBlocksToPost: basicGroupName=${basicGroupName}, secondaryGroupName=${secondaryGroupName}, decorationGroupName=${decorationGroupName}`)

    const isPost = elementGroup.name === 'post_group'
    // 🎯 Dla S22: przygotuj listę nazw dekoracji do usunięcia (unikalne wartości)
    // Używamy wszystkich możliwych grup dekoracyjnych z konfiguracji
    const decorationNamesToRemove: string[] = []
    if (isPost && fenceConfig.id === 'lupane-muro-s22') {
      const uniqueNames = new Set<string>()
      // Dodaj główną grupę dekoracyjną
      if (decorationGroupName) {
        uniqueNames.add(decorationGroupName)
      }
      // Dodaj wszystkie grupy z textures, które mogą być dekoracjami (oprócz grup bazowych)
      const postTextures = elementConfig?.textures
      if (postTextures) {
        Object.keys(postTextures).forEach(textureGroupName => {
          // Pomiń grupy bazowe (group_44, group_55)
          if (textureGroupName !== basicGroupName && textureGroupName !== secondaryGroupName) {
            uniqueNames.add(textureGroupName)
          }
        })
      }
      // Dodaj znane grupy dekoracyjne jako fallback
      uniqueNames.add('group_41')
      uniqueNames.add('group_63')
      
      decorationNamesToRemove.push(...Array.from(uniqueNames))
      DEBUG_LOGS && console.log(`🧹 S22: Lista dekoracji do usunięcia z bloków:`, decorationNamesToRemove)
    }

    const widthMultiplier = elementGroup.userData?.widthMultiplier || 1
    // 🎯 Rozkład po kolumnach tylko dla prawdziwie wielokolumnowych systemów (slim-3d / gladkie_long / gladkie_max / vision-bet-*)
    const isMultiColumnSectionFamily =
      isSection &&
      (fenceConfig.id === 'slim' ||
        fenceConfig.id === 'slim-3d' ||
        fenceConfig.id === 'gladkie_long' ||
        fenceConfig.id === 'gladkie_max' ||
        fenceConfig.id === 'vision-bet-long' ||
        fenceConfig.id === 'vision-bet-max' ||
        fenceConfig.id === 'vision-bet-medium' ||
        fenceConfig.id === 'gladkie_medium')
    const shouldDistributeByColumns = isMultiColumnSectionFamily && widthMultiplier > 1

    const findSectionColumnForPlacement = (): { x: number; z: number; topY: number } | null => {
      if (!shouldDistributeByColumns) return null
      const columns = this.collectMuroSectionColumns(elementGroup, [basicGroupName])
      if (columns.length === 0) return null

      let targetColumn = columns[0]
      let targetTopY =
        targetColumn.blocks.length > 0
          ? targetColumn.blocks[targetColumn.blocks.length - 1].position.y
          : 0

      columns.forEach(column => {
        if (column.blocks.length === 0) return
        const topY = column.blocks[column.blocks.length - 1].position.y
        if (topY < targetTopY) {
          targetColumn = column
          targetTopY = topY
        }
      })

      return {
        x: targetColumn.x,
        z: targetColumn.z,
        topY: targetTopY
      }
    }

    for (let i = 0; i < blocksToAdd; i++) {
      const sourceElement = model.getObjectByName(basicGroupName)
      if (sourceElement) {
        const clonedElement = sourceElement.clone(true)
        
        // 🎯 Dla S22: usuń wszystkie zagnieżdżone dekoracje z klonowanego bloku
        if (isPost && fenceConfig.id === 'lupane-muro-s22' && decorationNamesToRemove.length > 0) {
          this.removeNestedDecorations(clonedElement, decorationNamesToRemove)
        }
        
        // Znajdź najwyższy istniejący blok podstawowy (pomiń dekoracyjny jeśli istnieje)
        const existingBasicBlocks = elementGroup.children.filter(child =>
          matchesGroup(child.name, basicGroupName) ||
          (secondaryGroupName && matchesGroup(child.name, secondaryGroupName))
        )
        const blockSpacing = isSection
          ? (fenceConfig.elements?.section?.blockSpacing || 0.1)
          : (fenceConfig.elements?.post?.blockSpacing || 0.1)

        const placementColumn = findSectionColumnForPlacement()
        let offsetY = 0
        if (placementColumn) {
          offsetY = placementColumn.topY + blockSpacing
        } else if (existingBasicBlocks.length > 0) {
          // Posortuj bloki od najniższego do najwyższego
          existingBasicBlocks.sort((a, b) => a.position.y - b.position.y)
          const highestBlock = existingBasicBlocks[existingBasicBlocks.length - 1]
          offsetY = highestBlock.position.y + blockSpacing
        } else {
          // Jeśli nie ma bloków, zacznij od ziemi
          offsetY = 0
        }
        

        prepareElement(clonedElement, {
          modelScale: fenceConfig.modelScale || 0.01,
          positionY: offsetY,
          elementId,
          elementWidth: elementGroup.userData?.width || 0.4, // Używaj zapisanej szerokości
          fenceConfig, // Pass fence config for dynamic texture loading
          textures
        })

        if (placementColumn) {
          clonedElement.position.x = placementColumn.x
          clonedElement.position.z = placementColumn.z
        } else if (elementConfig) {
          const rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, elementGroup.userData?.widthMultiplier || 1)
          clonedElement.position.x += rightOffset
        }

        // 🎯 Odwróć post w pionie dla S38
        if (isPost && fenceConfig.id === 'lupane-muro-s38') {
          clonedElement.scale.y *= -1
          DEBUG_LOGS && console.log(`🔄 Odwrócono blok ${basicGroupName} w pionie dla S38: scale.y = ${clonedElement.scale.y}`)
        }

        elementGroup.add(clonedElement)
        DEBUG_LOGS && console.log(`🎯 Dodano blok ${basicGroupName} ${i + 1} na pozycji Y: ${offsetY}`)
        
        // 🎯 Dla S22: PO DODANIU bloku do sceny, usuń WSZYSTKIE zagnieżdżone dekoracje z tego bloku
        if (isPost && fenceConfig.id === 'lupane-muro-s22' && decorationNamesToRemove.length > 0) {
          this.removeNestedDecorations(clonedElement, decorationNamesToRemove)
          // Dodatkowe czyszczenie - sprawdź całe drzewo i usuń wszystkie zagnieżdżone dekoracje
          const nestedDecorations: THREE.Object3D[] = []
          clonedElement.traverse(child => {
            if (child === clonedElement) return
            const childName = child.name || ''
            if (decorationNamesToRemove.some(decName => {
              if (!decName) return false
              return childName === decName || childName.startsWith(`${decName}_copy_`)
            })) {
              nestedDecorations.push(child)
            }
          })
          nestedDecorations.forEach(dec => {
            if (dec.parent) {
              dec.parent.remove(dec)
              DEBUG_LOGS && console.log(`🧹 PO DODANIU: Usunięto zagnieżdżoną dekorację ${dec.name || 'unnamed'} z ${dec.parent.name || 'unknown'}`)
            }
          })
        }
        
        // 🎯 Jeśli istnieje drugi element bloku, dodaj go obok pierwszego
        if (secondaryGroupName) {
          const secondarySourceElement = model.getObjectByName(secondaryGroupName)
          if (secondarySourceElement) {
            const clonedSecondaryElement = secondarySourceElement.clone(true)
            
            // 🎯 Dla S22: usuń wszystkie zagnieżdżone dekoracje z klonowanego secondary bloku
            if (isPost && fenceConfig.id === 'lupane-muro-s22' && decorationNamesToRemove.length > 0) {
              this.removeNestedDecorations(clonedSecondaryElement, decorationNamesToRemove)
            }
            
            prepareElement(clonedSecondaryElement, {
              modelScale: fenceConfig.modelScale || 0.01,
              positionY: offsetY, // Ta sama pozycja Y
              elementId,
              elementWidth: elementGroup.userData?.width || 0.4,
              fenceConfig
            })
            
            // 🎯 Ustaw group_25 obok group_24 tak jak bloczki w sekcji
            // group_24 ma już pozycję X ustawioną na rightOffset
            // group_25 powinien być obok niego - użyj elementBaseWidth (szerokość bloku) zamiast obliczać bounding box
            // Pobierz szerokość z elementWidth użytego w prepareElement (lub elementGroup.userData?.width)
            const blockWidth = elementGroup.userData?.width || 0.148
            // Pozycja środka group_25 = pozycja środka group_24 + szerokość bloku
            // (ponieważ oba bloki mają tę samą szerokość i powinny się stykać)
            clonedSecondaryElement.position.x = clonedElement.position.x - blockWidth
            
            DEBUG_LOGS && console.log(`🎯 S38 post (addBlocksToPost): group_24 X=${clonedElement.position.x.toFixed(3)}m, group_25 X=${clonedSecondaryElement.position.x.toFixed(3)}m (odległość=${blockWidth.toFixed(3)}m)`)
            
            // 🎯 Odwróć drugi element bloku w pionie dla S38
            // if (isPost && fenceConfig.id === 'lupane-muro-s38') {
            //   clonedSecondaryElement.scale.y *= -1
            // }
            
            elementGroup.add(clonedSecondaryElement)
            DEBUG_LOGS && console.log(`🎯 Dodano blok ${secondaryGroupName} ${i + 1} obok ${basicGroupName} na pozycji Y: ${offsetY}`)
            
            // 🎯 Dla S22: PO DODANIU secondary bloku do sceny, usuń WSZYSTKIE zagnieżdżone dekoracje
            if (isPost && fenceConfig.id === 'lupane-muro-s22' && decorationNamesToRemove.length > 0) {
              this.removeNestedDecorations(clonedSecondaryElement, decorationNamesToRemove)
              // Dodatkowe czyszczenie - sprawdź całe drzewo i usuń wszystkie zagnieżdżone dekoracje
              const nestedDecorationsSecondary: THREE.Object3D[] = []
              clonedSecondaryElement.traverse(child => {
                if (child === clonedSecondaryElement) return
                const childName = child.name || ''
                if (decorationNamesToRemove.some(decName => {
                  if (!decName) return false
                  return childName === decName || childName.startsWith(`${decName}_copy_`)
                })) {
                  nestedDecorationsSecondary.push(child)
                }
              })
              nestedDecorationsSecondary.forEach(dec => {
                if (dec.parent) {
                  dec.parent.remove(dec)
                  DEBUG_LOGS && console.log(`🧹 PO DODANIU (secondary): Usunięto zagnieżdżoną dekorację ${dec.name || 'unnamed'} z ${dec.parent.name || 'unknown'}`)
                }
              })
            }
          }
        }
      }
    }
    
    // 🎯 Dla S22: PO DODANIU WSZYSTKICH BLOKÓW, wykonaj finalne czyszczenie wszystkich zagnieżdżonych dekoracji
    if (isPost && fenceConfig.id === 'lupane-muro-s22' && decorationNamesToRemove.length > 0) {
      const allNestedDecorations: THREE.Object3D[] = []
      elementGroup.traverse(obj => {
        if (obj === elementGroup) return
        // Sprawdź czy obiekt jest zagnieżdżony w bloku bazowym (nie jest bezpośrednim dzieckiem elementGroup)
        if (obj.parent && obj.parent !== elementGroup) {
          const objName = obj.name || ''
          if (decorationNamesToRemove.some(decName => {
            if (!decName) return false
            return objName === decName || objName.startsWith(`${decName}_copy_`)
          })) {
            // Sprawdź czy rodzic jest blokiem bazowym
            let current: THREE.Object3D | null = obj.parent
            let isNestedInBlock = false
            while (current && current !== elementGroup) {
              const parentName = current.name || ''
              if (matchesGroup(parentName, basicGroupName) || (secondaryGroupName && matchesGroup(parentName, secondaryGroupName))) {
                isNestedInBlock = true
                break
              }
              current = current.parent
            }
            if (isNestedInBlock) {
              allNestedDecorations.push(obj)
            }
          }
        }
      })
      
      if (allNestedDecorations.length > 0) {
        DEBUG_LOGS && console.log(`🧹 FINALNE CZYSZCZENIE (S22): Znaleziono ${allNestedDecorations.length} zagnieżdżonych dekoracji w blokach`)
        allNestedDecorations.forEach(dec => {
          if (dec.parent) {
            dec.parent.remove(dec)
            DEBUG_LOGS && console.log(`🧹 FINALNE: Usunięto zagnieżdżoną dekorację ${dec.name || 'unnamed'} z ${dec.parent.name || 'unknown'}`)
          }
        })
      }
    }

    // Przemieść blok dekoracyjny na najwyższą pozycję - PO DODANIU WSZYSTKICH BLOKÓW
    // 🎯 Dla S22 post: NIE przesuwaj dekoracyjnego tutaj - logika w updatePostHeight to obsłuży
    // Używaj decorationGroup z config, a jeśli nie ma, to availableGroups[1]
    const finalDecorationGroupName = decorationGroupName || availableGroups[1]
    if (finalDecorationGroupName && !(isPost && fenceConfig.id === 'lupane-muro-s22')) {
      // 🎯 Sekcje wielokolumnowe (np. slim / slim-3d / gładkie long/max) – dekoracja w każdej kolumnie
      const isMultiColumnSection =
        isSection &&
        (fenceConfig.id === 'slim' ||
          fenceConfig.id === 'slim-3d' ||
          fenceConfig.id === 'gladkie_long' ||
          fenceConfig.id === 'gladkie_max' ||
          fenceConfig.id === 'vision-bet-long' ||
          fenceConfig.id === 'vision-bet-max' ||
          fenceConfig.id === 'vision-bet-medium' ||
          fenceConfig.id === 'gladkie_medium')

      if (isMultiColumnSection && widthMultiplier > 1) {
        this.ensureSectionDecorationsForColumns(
          elementGroup,
          fenceConfig,
          [basicGroupName],
          finalDecorationGroupName,
          model
        )
      } else {
        const decorationBlock = elementGroup.children.find(child => child.name === finalDecorationGroupName)
        if (decorationBlock) {
          // Pobierz WSZYSTKIE podstawowe bloki (włącznie z nowo dodanymi i secondaryGroupName jeśli istnieje)
          const basicBlocks = elementGroup.children.filter(child => 
            child.name === basicGroupName || (secondaryGroupName && child.name === secondaryGroupName)
          )
          basicBlocks.sort((a, b) => a.position.y - b.position.y)

          if (basicBlocks.length > 0) {
            const lastBasicBlock = basicBlocks[basicBlocks.length - 1]
          let decorationSpacing = isSection
              ? (fenceConfig.elements?.section?.decorationSpacing || 0.15)
              : (fenceConfig.elements?.post?.decorationSpacing || 0.15)

          // 🎯 Vision-Bet Medium SECTION: użyj blockSpacing, żeby dekoracja stykała się z górnym bloczkiem
          if (isSection && (fenceConfig.id === 'vision-bet-medium' || fenceConfig.id === 'vision-bet-max')) {
            decorationSpacing = fenceConfig.elements?.section?.blockSpacing || decorationSpacing
          }
            
            // Uwzględnij wysokość bloczka podstawowego + odstęp dekoracyjny
            // Dla słupka: dodajemy blockSpacing (wysokość bloku), bo dekoracja musi być POZA blokiem
            // Dla section: NIE dodajemy blockSpacing, bo dekoracja jest NAD blokiem
            let decorationY = lastBasicBlock.position.y
            if (!isSection) {
            const blockSpacing = fenceConfig.elements?.post?.blockSpacing || 0.1
              decorationY += blockSpacing
            }
            decorationY += decorationSpacing
            
            decorationBlock.position.y = decorationY
            
            // 🎯 Dla S38 post: ustaw bloczek dekoracyjny na tej samej linii X co group_24
            if (!isSection && fenceConfig.id === 'lupane-muro-s38' && secondaryGroupName) {
              // Znajdź bloki group_24 i group_25 na tej samej wysokości co lastBasicBlock
              const blocksAtSameY = basicBlocks.filter(block => Math.abs(block.position.y - lastBasicBlock.position.y) < 0.001)
              const group24Block = blocksAtSameY.find(block => block.name === basicGroupName)
              
              if (group24Block) {
                // Bloczek dekoracyjny powinien być na tej samej linii X co group_24
                decorationBlock.position.x = group24Block.position.x
                DEBUG_LOGS && console.log(`🎯 Blok dekoracyjny ${finalDecorationGroupName} ustawiony dla S38: X=${decorationBlock.position.x.toFixed(3)} (ta sama linia X co group_24: ${group24Block.position.x.toFixed(3)})`)
              }
            }
            
            // 🎯 Dla S22 post: przesuń bloczek dekoracyjny do prawej o 1.25cm i do lewej o 5cm
            if (!isSection && fenceConfig.id === 'lupane-muro-s22') {
              decorationBlock.position.x += 0.0125 // Do prawej (1.25cm)
              decorationBlock.position.z -= 0.05 // Do lewej/do przodu (5cm)
              DEBUG_LOGS && console.log(`🎯 Blok dekoracyjny ${finalDecorationGroupName} przesunięty dla S22: X=${decorationBlock.position.x.toFixed(3)} (+0.0125), Z=${decorationBlock.position.z.toFixed(3)} (-0.05)`)
            }
            
            DEBUG_LOGS && console.log(`🎯 Blok dekoracyjny ${finalDecorationGroupName} przemieszczony na Y: ${decorationY} (nad ostatnim blokiem na Y: ${lastBasicBlock.position.y}, decorationSpacing=${decorationSpacing})`)
          }
        }
      }
    }
  }

  /**
   * Pomocnicza: Usuwa bloki ze słupka
   */
  private removeBlocksFromPost(
    elementGroup: THREE.Group,
    blocksToRemove: number,
    fenceConfig: FenceConfig
  ): void {
    // 🎯 Użyj odpowiednich grup w zależności od typu elementu
    const isSection = elementGroup.name === 'section_group'
    const availableGroups = isSection 
      ? (fenceConfig.elements?.section?.groups || ['group_0'])
      : (fenceConfig.elements?.post?.groups || ['group_0'])
    const elementConfig = isSection ? fenceConfig.elements?.section : fenceConfig.elements?.post
    let decorationGroupName = elementConfig?.decorationGroup || availableGroups[1]
    if (!isSection && fenceConfig.id === 'lupane-muro-s22') {
      decorationGroupName = elementConfig?.decorationGroup || 'group_63'
    }
    const metalGroupName = elementConfig?.metalGroup

    const baseGroupNames = availableGroups.filter(groupName => (
      groupName &&
      groupName !== decorationGroupName &&
      groupName !== metalGroupName
    ))

    const matchesGroup = (childName: string, groupName: string | undefined): boolean => {
      if (!groupName) return false
      return childName === groupName || childName.startsWith(`${groupName}_copy_`)
    }
 
    const widthMultiplier = elementGroup.userData?.widthMultiplier || 1

    let blocksRemoved = 0
    // 🎯 FIX: Sortuj OD NAJNIŻSZEGO (od dołu) - usuń najniższe bloki
    const blocksToRemoveArray = [...elementGroup.children]
    blocksToRemoveArray.sort((a, b) => b.position.y - a.position.y)
 
    for (const block of blocksToRemoveArray) {
      if (blocksRemoved >= blocksToRemove) break
 
      // 🎯 FIX: Usuwaj TYLKO podstawowe bloki (nie dekoracyjne!)
      if (baseGroupNames.some(groupName => matchesGroup(block.name, groupName))) {
        elementGroup.remove(block)
        blocksRemoved++
      }
    }
 
    // Przemieść pozostałe bloki (pobierz PONOWNIE po usunięciu!)
    const allRemainingChildren = [...elementGroup.children]
    const basicBlocks = allRemainingChildren.filter(block =>
      baseGroupNames.some(groupName => matchesGroup(block.name, groupName))
    )
    basicBlocks.sort((a, b) => a.position.y - b.position.y)
 
    // 🎯 Dla sekcji wielokolumnowych (slim / slim-3d / gladkie_long / gladkie_max / vision-bet-*)
    // po usunięciu warstw przestaw bloki niezależnie w każdej kolumnie, żeby układ był jak w 1x, tylko powielony
    const isMultiColumnSectionFamilyForReflow =
      isSection &&
      widthMultiplier > 1 &&
      (fenceConfig.id === 'slim' ||
        fenceConfig.id === 'slim-3d' ||
        fenceConfig.id === 'gladkie_long' ||
        fenceConfig.id === 'gladkie_max' ||
        fenceConfig.id === 'vision-bet-long' ||
        fenceConfig.id === 'vision-bet-max' ||
        fenceConfig.id === 'vision-bet-medium' ||
        fenceConfig.id === 'gladkie_medium')

    if (isMultiColumnSectionFamilyForReflow) {
      const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
      const blockSpacing = fenceConfig.elements?.section?.blockSpacing || 0.1

      columns.forEach(column => {
        // Posortuj bloki w kolumnie po Y i ustaw Y w równych odstępach
        column.blocks.sort((a, b) => a.position.y - b.position.y)
        column.blocks.forEach((block, idx) => {
          block.position.y = idx * blockSpacing
        })
      })
    } else {
      // Klasyczny przypadek: jedna kolumna – traktuj wszystkie bloki jako jedną listę
      const blocksPerLayer = Math.max(1, baseGroupNames.length || 1)
      basicBlocks.forEach((block, index) => {
        const blockSpacing = isSection
          ? (fenceConfig.elements?.section?.blockSpacing || 0.1)
          : (fenceConfig.elements?.post?.blockSpacing || 0.1)
        const layerIndex = Math.floor(index / blocksPerLayer)
        block.position.y = layerIndex * blockSpacing
      })
    }

    // Ustaw bloki dekoracyjne nad ostatnim podstawowym
    // 🎯 Dla S22 post: NIE przesuwaj dekoracyjnego tutaj - logika w updatePostHeight to obsłuży
    if (decorationGroupName && !(!isSection && fenceConfig.id === 'lupane-muro-s22')) {
      const ensureSlimDecorationCount = (): THREE.Object3D[] => {
        const initialDecorations = allRemainingChildren.filter(block => matchesGroup(block.name, decorationGroupName))
        const isMultiColumnSectionFamily =
          fenceConfig.id === 'slim-3d' ||
          fenceConfig.id === 'gladkie_long' ||
          fenceConfig.id === 'gladkie_max' ||
          fenceConfig.id === 'gladkie_medium' ||
          fenceConfig.id === 'vision-bet-long' ||
          fenceConfig.id === 'vision-bet-max' ||
          fenceConfig.id === 'vision-bet-medium'

        if (!(isSection && isMultiColumnSectionFamily)) {
          return initialDecorations
        }

        const baseColumns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
        if (baseColumns.length <= 1) {
          return initialDecorations
        }

        const decorations = [...initialDecorations]

        const ensureDecoration = () => {
          if (decorations.length > 0) return decorations[0]
          const template = elementGroup.children.find(child => matchesGroup(child.name, decorationGroupName))
          return template || null
        }

        while (decorations.length < baseColumns.length) {
          const template = ensureDecoration()
          if (!template) break
          const clonedDeco = template.clone(true)
          clonedDeco.name = `${decorationGroupName}_copy_auto_${decorations.length}`
          elementGroup.add(clonedDeco)
          decorations.push(clonedDeco)
        }

        while (decorations.length > baseColumns.length) {
          const excess = decorations.pop()
          if (excess && excess !== decorations[0]) {
            elementGroup.remove(excess)
          }
        }

        return decorations
      }

      const decorationBlocks = ensureSlimDecorationCount()

      const assignDecorationToColumns = (): Array<{ block: THREE.Object3D; column: (ReturnType<ElementManager['collectMuroSectionColumns']>[number]) | null }> => {
        const isMultiColumnSectionFamily =
          fenceConfig.id === 'slim' ||
          fenceConfig.id === 'slim-3d' ||
          fenceConfig.id === 'gladkie_long' ||
          fenceConfig.id === 'gladkie_max' ||
          fenceConfig.id === 'gladkie_medium' ||
          fenceConfig.id === 'vision-bet-long' ||
          fenceConfig.id === 'vision-bet-max' ||
          fenceConfig.id === 'vision-bet-medium'

        if (!(isSection && isMultiColumnSectionFamily)) {
          return decorationBlocks.map(block => ({ block, column: null }))
        }

        const columns = this.collectMuroSectionColumns(elementGroup, baseGroupNames)
        if (columns.length === 0) {
          return decorationBlocks.map(block => ({ block, column: null }))
        }

        decorationBlocks.sort((a, b) => a.position.x - b.position.x)
        columns.sort((a, b) => a.x - b.x)

        const assignments: Array<{ block: THREE.Object3D; column: typeof columns[number] | null }> = []

        decorationBlocks.forEach((block, index) => {
          const column = columns[index % columns.length]
          assignments.push({ block, column })
        })

        return assignments
      }

      const decorationAssignments = assignDecorationToColumns()

      decorationAssignments.forEach(({ block: decorationBlock, column }) => {
        const targetBlocks = column
          ? column.blocks
          : basicBlocks

        if (targetBlocks.length > 0) {
          const sortedTargets = [...targetBlocks].sort((a, b) => a.position.y - b.position.y)
          const lastBasicBlock = sortedTargets[sortedTargets.length - 1]
          let decorationSpacing = isSection
            ? (fenceConfig.elements?.section?.decorationSpacing || 0.15)
            : (fenceConfig.elements?.post?.decorationSpacing || 0.15)

          // 🎯 Vision-Bet Medium SECTION: użyj blockSpacing, żeby dekoracja stykała się z górnym bloczkiem
          if (isSection && (fenceConfig.id === 'vision-bet-medium' || fenceConfig.id === 'vision-bet-max')) {
            decorationSpacing = fenceConfig.elements?.section?.blockSpacing || decorationSpacing
          }
          
          // Dla słupka: dodajemy blockSpacing (wysokość bloku), bo dekoracja musi być POZA blokiem
          // Dla section: NIE dodajemy blockSpacing, bo dekoracja jest NAD blokiem
          let decorationY = lastBasicBlock.position.y
          if (!isSection) {
            const postBlockSpacing = fenceConfig.elements?.post?.blockSpacing || 0.1
            decorationY += postBlockSpacing
          }
          decorationY += decorationSpacing
          
          decorationBlock.position.y = decorationY

          if (column) {
            decorationBlock.position.x = column.x
            decorationBlock.position.z = column.z
          }
          
          // 🎯 Dla S38 post: wycentruj bloczek dekoracyjny między group_24 i group_25
          if (!isSection && fenceConfig.id === 'lupane-muro-s38') {
            const availableGroups = fenceConfig.elements?.post?.groups || ['group_0']
            const primaryGroupName = availableGroups[0]
            const secondaryGroupName = availableGroups.find((name: string) => name !== primaryGroupName && name !== decorationGroupName)
            
            if (secondaryGroupName) {
              // Znajdź bloki group_24 i group_25 na tej samej wysokości co lastBasicBlock
              const allBasicBlocks = elementGroup.children.filter(child => 
                child.name === primaryGroupName || child.name === secondaryGroupName
              )
              const blocksAtSameY = allBasicBlocks.filter(block => Math.abs(block.position.y - lastBasicBlock.position.y) < 0.001)
              const group24Block = blocksAtSameY.find(block => matchesGroup(block.name, primaryGroupName))
              const group25Block = blocksAtSameY.find(block => matchesGroup(block.name, secondaryGroupName))
              
              if (group24Block && group25Block) {
                // Wycentruj bloczek dekoracyjny między nimi
                const centerX = (group24Block.position.x + group25Block.position.x) / 2
                decorationBlock.position.x = centerX
                DEBUG_LOGS && console.log(`🎯 Blok dekoracyjny ${decorationGroupName} wycentrowany dla S38 (po usunięciu): X=${centerX.toFixed(3)} (między ${group24Block.position.x.toFixed(3)} i ${group25Block.position.x.toFixed(3)})`)
              }
            }
          }
          
          // 🎯 Dla S22 post: przesuń bloczek dekoracyjny do prawej o 1.25cm i do lewej o 5cm
          if (!isSection && fenceConfig.id === 'lupane-muro-s22') {
            decorationBlock.position.x += 0.0125 // Do prawej (1.25cm)
            decorationBlock.position.z -= 0.05 // Do lewej/do przodu (5cm)
            DEBUG_LOGS && console.log(`🎯 Blok dekoracyjny ${decorationGroupName} przesunięty dla S22 (po usunięciu): X=${decorationBlock.position.x.toFixed(3)} (+0.0125), Z=${decorationBlock.position.z.toFixed(3)} (-0.05)`)
          }
        } else {
          decorationBlock.position.y = 0
        }
      })
    }
  }

  // 🎯 Aktualizuj szerokość przęsła
  async updateSectionWidth(elementId: string, newWidthMultiplier: number, fenceConfig: FenceConfig) {
    DEBUG_LOGS && console.log(`🎯 ElementManager.updateSectionWidth: ${elementId} → ${newWidthMultiplier}x`)
    
    const element = this.getElement(elementId)
    if (!element) {
      console.error(`❌ ElementManager.updateSectionWidth: Element ${elementId} nie znaleziony`)
      return
    }

    // Zaktualizuj widthMultiplier w userData
    element.userData.widthMultiplier = newWidthMultiplier

    // Oblicz nową szerokość na podstawie mnożnika
    // 🎯 Użyj baseWidth z userData jeśli jest dostępne (zapisywane w addFenceElement)
    // W przeciwnym razie użyj defaultWidth z configu (fallback)
    const baseWidth = element.userData?.baseWidth || fenceConfig.elements?.section?.defaultWidth || 2.0
    const newWidth = baseWidth * newWidthMultiplier
    
    // Zaktualizuj szerokość w userData
    element.userData.width = newWidth

    // Zaktualizuj skalę X elementu
    element.scale.x = newWidthMultiplier

    // 🎯 Zaktualizuj rightEdge (dla gate w gladkie_long itp. - ważne dla następnego elementu!)
    const elementType = element.userData?.elementType || 'section'
    const isVisionBetLongGateDoor = fenceConfig.id === 'vision-bet-long' && (elementType === 'gate' || elementType === 'door')
    const isVisionBetMaxGateDoor = fenceConfig.id === 'vision-bet-max' && (elementType === 'gate' || elementType === 'door')
    const isVisionBetMediumGateDoor = fenceConfig.id === 'vision-bet-medium' && (elementType === 'gate' || elementType === 'door')
    const isGladkieMaxGateDoor = fenceConfig.id === 'gladkie_max' && (elementType === 'gate' || elementType === 'door')
    const isGladkieLongGateDoor = fenceConfig.id === 'gladkie_long' && (elementType === 'gate' || elementType === 'door')
    const isSlim3dGateDoor = fenceConfig.id === 'slim-3d' && (elementType === 'gate' || elementType === 'door')
    const isSlimGateDoor = fenceConfig.id === 'slim' && (elementType === 'gate' || elementType === 'door')
    const isRoyalS50GateDoor = fenceConfig.id === 'royal_s50' && (elementType === 'gate' || elementType === 'door')
    const isRoyalP50GateDoor = fenceConfig.id === 'royal_p50' && (elementType === 'gate' || elementType === 'door')
    const isMuroP22GateDoor = fenceConfig.id === 'lupane-muro-p22' && (elementType === 'gate' || elementType === 'door')
    const isMuroS22GateDoor = fenceConfig.id === 'lupane-muro-s22' && (elementType === 'gate' || elementType === 'door')
    const isMuroP38GateDoor = fenceConfig.id === 'lupane-muro-p38' && (elementType === 'gate' || elementType === 'door')
    const isGladkieMediumGateDoor = fenceConfig.id === 'gladkie_medium' && (elementType === 'gate' || elementType === 'door')
    const isVisionBetGateDoor = isVisionBetLongGateDoor || isVisionBetMaxGateDoor || isVisionBetMediumGateDoor || isGladkieMediumGateDoor || isGladkieMaxGateDoor || isGladkieLongGateDoor || isSlim3dGateDoor || isSlimGateDoor || isRoyalS50GateDoor || isRoyalP50GateDoor || isMuroP22GateDoor || isMuroS22GateDoor || isMuroP38GateDoor
    
    let elementConfig = fenceConfig.elements?.[elementType as 'section' | 'gate' | 'door']
    if (isVisionBetGateDoor) {
      elementConfig = fenceConfig.elements?.section
    }
    
    if (elementConfig) {
      // 🎯 Uwzględnij rotację elementu (jak w rotationManager.updateElementRotation)
      const absoluteRotation = element.userData?.absoluteRotation || 0
      const absoluteRotationRad = (absoluteRotation * Math.PI) / 180
      
      // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją)
      let rightEdgeOffsetX = newWidth / 2
      
      if (elementType === 'section' || isVisionBetGateDoor) {
        // Standardowa logika dla sections i gate/door traktowanych jak section
        if ((fenceConfig.id === 'lupane-muro-p22' || fenceConfig.id === 'lupane-muro-p38' || fenceConfig.id === 'lupane-muro-s22' || fenceConfig.id === 'lupane-muro-s38') && elementType === 'section') {
          // 🎯 Dla Muro sections: użyj DOKŁADNIE tej samej logiki co w addFenceElement
          // ⚠️ WAŻNE: Użyj originalPosition z userData (zapisanego w addFenceElement) zamiast element.position
          // element.position może być modyfikowane podczas aktualizacji szerokości
          const originalPosition = element.userData?.originalPosition || element.position
          let rightEdgeX = originalPosition.x + (newWidth / 2)
          const absoluteRotation = element.userData?.absoluteRotation || 0
          const absoluteRotationRad = (absoluteRotation * Math.PI) / 180
          // Oblicz rightEdgeOffset w lokalnych współrzędnych (przed rotacją)
          const rightEdgeOffsetX = rightEdgeX - originalPosition.x
          const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
          rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
          // ⚠️ Użyj originalPosition (bez przesunięcia) zamiast element.position (które może być modyfikowane)
          let rightEdge = new THREE.Vector3(originalPosition.x, originalPosition.y, originalPosition.z).add(rightEdgeOffset)
          
          // 🎯 Dla P22 section 4x: zwiększ odstęp po prawej
          if (fenceConfig.id === 'lupane-muro-p22' && newWidthMultiplier === 4) {
            const spacing = elementConfig.spacing || 0.074
            const additionalOffset = spacing * 1.0 // 1.0x spacing = ~0.074m
            // Utwórz wektor przesunięcia w kierunku rotacji (w prawo)
            const additionalOffsetVector = new THREE.Vector3(additionalOffset, 0, 0)
            additionalOffsetVector.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            rightEdge.add(additionalOffsetVector)
            DEBUG_LOGS && console.log(`🎯 Muro P22 section 4x (update): dodano odstęp po prawej: ${additionalOffset.toFixed(3)}m (1.0x spacing)`)
          }
          
          // 🎯 Dla P22 section 5x: zwiększ odstęp po prawej
          if (fenceConfig.id === 'lupane-muro-p22' && newWidthMultiplier === 5) {
            const spacing = elementConfig.spacing || 0.074
            const additionalOffset = spacing * 2.0 // 2.0x spacing = ~0.148m
            // Utwórz wektor przesunięcia w kierunku rotacji (w prawo)
            const additionalOffsetVector = new THREE.Vector3(additionalOffset, 0, 0)
            additionalOffsetVector.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
            rightEdge.add(additionalOffsetVector)
            DEBUG_LOGS && console.log(`🎯 Muro P22 section 5x (update): dodano odstęp po prawej: ${additionalOffset.toFixed(3)}m (2.0x spacing)`)
          }
          
          element.userData.rightEdge = rightEdge
          const muroType = fenceConfig.id === 'lupane-muro-p22' ? 'P22' : fenceConfig.id === 'lupane-muro-p38' ? 'P38' : fenceConfig.id === 'lupane-muro-s22' ? 'S22' : 'S38'
          DEBUG_LOGS && console.log(`🔍 DEBUG (Muro ${muroType} section ${newWidthMultiplier}x): rightEdge obrócony o ${absoluteRotation}°, rightEdgeX=${rightEdgeX.toFixed(3)}, originalPosition.x=${originalPosition.x.toFixed(3)}, rightEdgeOffsetX=${rightEdgeOffsetX.toFixed(3)}, rightEdge X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)}`)
          DEBUG_LOGS && console.log(`💾 Zaktualizowano rightEdge dla Muro ${muroType} section: X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)} (rotacja=${absoluteRotation}°)`)
        } else if (fenceConfig.id === 'royal_s50' || fenceConfig.id === 'royal_p50') {
          // 🎯 Dla royal_s50 i royal_p50 gate/door: specjalna logika (identyczna jak w addFenceElement)
          // 🚪 WAŻNE: Dla gate i door w Royal S50/P50: odejmij spacing od rightEdge dla widthMultiplier >= 2
          // żeby zachować odstępy takie same jak dla bram (które mają domyślnie 2x i działają poprawnie)
          const royalType = fenceConfig.id === 'royal_s50' ? 'S50' : 'P50'
          let rightEdgeX = element.position.x + (newWidth / 2)
          const spacing = elementConfig.spacing || 0.198
          if (newWidthMultiplier >= 2) {
            // Dla 2x: odejmij spacing (zachowaj odstęp jak dla bram)
            // Dla 3x+: użyj wzoru (widthMultiplier - 2) * spacing * 0.5 + spacing
            let reduction = spacing // Dla 2x: po prostu spacing
            if (newWidthMultiplier >= 3) {
              // Dla 3x+: (widthMultiplier - 2) * spacing * 0.5 + spacing
              reduction = (newWidthMultiplier - 2) * spacing * 0.5 + spacing
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Royal ${royalType} ${elementType} ${newWidthMultiplier}x): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Royal ${royalType} ${elementType} ${newWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge (zachowaj odstęp jak dla bram)`)
          } else {
            // Dla 1x: bez kompensacji
            DEBUG_LOGS && console.log(`🔍 DEBUG (Royal ${royalType} ${elementType}): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Royal ${royalType} ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
          // Oblicz rightEdgeOffsetX z rightEdgeX (tak samo jak w addFenceElement)
          rightEdgeOffsetX = rightEdgeX - element.position.x
        } else if (fenceConfig.id === 'lupane-muro-p22' && isMuroP22GateDoor) {
          // 🎯 Dla muro p22 gate/door TYLKO: specjalna logika (identyczna jak dla section w addFenceElement)
          // ⚠️ NIE modyfikować logiki dla section - tylko dla gate/door
          // Oblicz rightEdgeX tak samo jak w addFenceElement - bez odejmowania spacing na początku
          let rightEdgeX = element.position.x + (newWidth / 2)
          if (newWidthMultiplier >= 2) {
            // Dla widthMultiplier >= 2: kompensacja
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier
            let reduction = 0
            if (newWidthMultiplier === 2) {
              reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
            } else if (newWidthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (newWidthMultiplier === 4) {
              reduction = spacing * 1.0 // 0.074 dla 4x
            } else if (newWidthMultiplier === 5) {
              reduction = spacing * 2.0 // 0.148 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 ${elementType} ${newWidthMultiplier}x): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 ${elementType} ${newWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: bez kompensacji (jak dla section)
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P22 ${elementType}): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P22 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
          // Oblicz rightEdgeOffsetX z rightEdgeX (tak samo jak w addFenceElement)
          rightEdgeOffsetX = rightEdgeX - element.position.x
        } else if (fenceConfig.id === 'lupane-muro-s22' && isMuroS22GateDoor) {
          // 🎯 Dla muro s22 gate/door TYLKO: specjalna logika (identyczna jak dla section w addFenceElement)
          // ⚠️ NIE modyfikować logiki dla section - tylko dla gate/door
          // Oblicz rightEdgeX tak samo jak w addFenceElement - bez odejmowania spacing na początku
          let rightEdgeX = element.position.x + (newWidth / 2)
          if (newWidthMultiplier >= 2) {
            // Dla widthMultiplier >= 2: kompensacja (takie same jak dla section w Muro S22)
            const spacing = elementConfig.spacing || 0.086
            // Różne kompensacje dla różnych widthMultiplier (takie same jak dla section w Muro S22)
            let reduction = 0
            if (newWidthMultiplier === 2) {
              reduction = spacing * -0.9 // -0.077 dla 2x (dodaje zamiast odejmować)
            } else if (newWidthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (newWidthMultiplier === 4) {
              reduction = spacing * 0.9 // 0.077 dla 4x
            } else if (newWidthMultiplier === 5) {
              reduction = spacing * 1.8 // 0.155 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22 ${elementType} ${newWidthMultiplier}x): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22 ${elementType} ${newWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: bez kompensacji (jak dla section)
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro S22 ${elementType}): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro S22 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
          // Oblicz rightEdgeOffsetX z rightEdgeX (tak samo jak w addFenceElement)
          rightEdgeOffsetX = rightEdgeX - element.position.x
        } else if (fenceConfig.id === 'lupane-muro-p38' && isMuroP38GateDoor) {
          // 🎯 Dla muro p38 gate/door TYLKO: specjalna logika (identyczna jak dla section w addFenceElement)
          // ⚠️ NIE modyfikować logiki dla section - tylko dla gate/door
          // Oblicz rightEdgeX tak samo jak w addFenceElement - bez odejmowania spacing na początku
          let rightEdgeX = element.position.x + (newWidth / 2)
          if (newWidthMultiplier >= 2) {
            // Dla widthMultiplier >= 2: kompensacja (takie same jak dla section w Muro P38)
            const spacing = elementConfig.spacing || 0.074
            // Różne kompensacje dla różnych widthMultiplier (takie same jak dla section w Muro P38)
            let reduction = 0
            if (newWidthMultiplier === 2) {
              reduction = spacing * -1.0 // -0.074 dla 2x (dodaje zamiast odejmować)
            } else if (newWidthMultiplier === 3) {
              reduction = spacing * 0.0 // 0.0 dla 3x
            } else if (newWidthMultiplier === 4) {
              reduction = spacing * 1.0 // 0.074 dla 4x
            } else if (newWidthMultiplier === 5) {
              reduction = spacing * 2.0 // 0.148 dla 5x
            }
            rightEdgeX -= reduction
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38 ${elementType} ${newWidthMultiplier}x): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, spacing=${spacing.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (odjęto ${reduction.toFixed(3)}m)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38 ${elementType} ${newWidthMultiplier}x): odjęto ${reduction.toFixed(3)}m od rightEdge`)
          } else {
            // Dla 1x: bez kompensacji (jak dla section)
            DEBUG_LOGS && console.log(`🔍 DEBUG (Muro P38 ${elementType}): widthMultiplier=${newWidthMultiplier}, newWidth=${newWidth.toFixed(3)}, rightEdgeX=${rightEdgeX.toFixed(3)} (bez kompensacji)`)
            DEBUG_LOGS && console.log(`🎯 rightEdge (Muro P38 ${elementType}): proste obliczenie bez odejmowania spacing i rightOffset`)
          }
          // Oblicz rightEdgeOffsetX z rightEdgeX (tak samo jak w addFenceElement)
          rightEdgeOffsetX = rightEdgeX - element.position.x
        } else if (!['lupane-muro-p22', 'lupane-muro-p38', 'lupane-muro-s22', 'lupane-muro-s38', 'royal_p50'].includes(fenceConfig.id)) {
          rightEdgeOffsetX = (newWidth / 2) - (elementConfig.spacing || 0)
          
          const rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, newWidthMultiplier)
          const coefficient = this.getCoefficientForWidthMultiplier(elementConfig, newWidthMultiplier)
          const rightOffsetReduction = rightOffset * coefficient
          rightEdgeOffsetX -= rightOffsetReduction
          
          if (isVisionBetGateDoor) {
            let fenceType = ''
            if (fenceConfig.id === 'vision-bet-long') fenceType = 'Vision-Bet-Long'
            else if (fenceConfig.id === 'vision-bet-max') fenceType = 'Vision-Bet-Max'
            else if (fenceConfig.id === 'vision-bet-medium') fenceType = 'Vision-Bet-Medium'
            else if (fenceConfig.id === 'gladkie_max') fenceType = 'Gladkie-Max'
            else if (fenceConfig.id === 'gladkie_long') fenceType = 'Gladkie-Long'
            else if (fenceConfig.id === 'slim-3d') fenceType = 'Slim-3D'
            else if (fenceConfig.id === 'slim') fenceType = 'Slim'
            DEBUG_LOGS && console.log(`🚪 ${fenceType} ${elementType} rightEdge zaktualizowany: offsetX=${rightEdgeOffsetX.toFixed(3)}m (jak section)`)
          }
        }
      } else if (elementType === 'gate' || elementType === 'door') {
        // Dla gate/door (nie vision-bet): rightEdge to środek + połowa szerokości
        rightEdgeOffsetX = newWidth / 2
        DEBUG_LOGS && console.log(`🚪 ${elementType} rightEdge zaktualizowany: offsetX=${rightEdgeOffsetX.toFixed(3)}m (width=${newWidth.toFixed(3)}m)`)
      }
      
      // Obróć offset o rotację elementu
      const rightEdgeOffset = new THREE.Vector3(rightEdgeOffsetX, 0, 0)
      rightEdgeOffset.applyEuler(new THREE.Euler(0, absoluteRotationRad, 0))
      
      // Oblicz finalny rightEdge
      const rightEdge = element.position.clone().add(rightEdgeOffset)
      element.userData.rightEdge = rightEdge
      element.userData.rightOffset = this.getRightOffsetForWidthMultiplier(elementConfig, newWidthMultiplier)
      
      DEBUG_LOGS && console.log(`💾 Zaktualizowano rightEdge dla ${elementType}: X=${rightEdge.x.toFixed(3)}, Z=${rightEdge.z.toFixed(3)} (rotacja=${absoluteRotation}°)`)
    }

    DEBUG_LOGS && console.log(`✅ ElementManager.updateSectionWidth: Zaktualizowano element ${elementId} na ${newWidthMultiplier}x (szerokość: ${newWidth}m)`)
  }

  // 🎯 Usuń element z 3D sceny
  removeElement(elementId: string, scene: THREE.Scene): boolean {
    DEBUG_LOGS && console.log(`🗑️ ElementManager.removeElement: Usuwam element ${elementId}`)
    
    const element = this.getElement(elementId)
    if (!element) {
      console.error(`❌ ElementManager.removeElement: Element ${elementId} nie znaleziony`)
      return false
    }

    // Usuń element ze sceny
    scene.remove(element)
    
    // Usuń z mapy elementów
    this.elements.delete(elementId)
    
    // Usuń z pending elements
    this.pendingElements.delete(elementId)
    
    DEBUG_LOGS && console.log(`✅ ElementManager.removeElement: Element ${elementId} usunięty z 3D sceny`)
    return true
  }

  // 🎯 Usuń ostatni element (najwyższy ID)
  removeLastElement(scene: THREE.Scene): boolean {
    DEBUG_LOGS && console.log(`🗑️ ElementManager.removeLastElement: Usuwam ostatni element`)
    
    if (this.elements.size === 0) {
      DEBUG_LOGS && console.log(`⚠️ ElementManager.removeLastElement: Brak elementów do usunięcia`)
      return false
    }

    // Znajdź element z najwyższym ID
    const elementIds = Array.from(this.elements.keys())
    const sortedIds = elementIds.sort((a, b) => {
      const numA = parseInt(a.split('-')[1]) || 0
      const numB = parseInt(b.split('-')[1]) || 0
      return numB - numA // Malejąco (najwyższy pierwszy)
    })
    
    const lastElementId = sortedIds[0]
    DEBUG_LOGS && console.log(`🎯 ElementManager.removeLastElement: Ostatni element to ${lastElementId}`)
    
    return this.removeElement(lastElementId, scene)
  }

}

